import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

import { apiClient } from '../utils/api';

interface Language {
    id: string;
    code: string;
    name: string;
    native_name: string;
    is_active: boolean;
    is_default: boolean;
    sort_order: number;
    created_at?: string;
    updated_at?: string;
}



interface LanguageContextType {
    currentLanguage: string;
    setLanguage: (code: string) => void;
    languages: Language[];
    isLoading: boolean;
    t: (key: string, ...args: any[]) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

interface LanguageProviderProps {
    children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        // Try to get language from localStorage, fallback to 'fr' (French)
        const savedLanguage = localStorage.getItem('restaurant-language');
        // If saved language is 'hi' (old Hindi) or 'ln' (old Lingala), migrate to 'fr'
        if (savedLanguage === 'hi' || savedLanguage === 'ln') {
            localStorage.setItem('restaurant-language', 'fr');
            return 'fr';
        }
        return savedLanguage || 'fr';
    });
    const [languages, setLanguages] = useState<Language[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Simple translations for testing
    const translations: Record<string, Record<string, string>> = {
        en: {
            'nav.home': 'Home',
            'nav.menu': 'Our Menu',
            'nav.about': 'About Us',
            'nav.gallery': 'Gallery',
            'nav.contact': 'Contact',
            'nav.reservations': 'Reservations',
            'hero.slide1.tag': 'Welcome',
            'hero.slide1.title': 'Ultimate Dining & Entertainment',
            'hero.slide1.subtitle': 'Experience exceptional cuisine, premium bar, and exciting gaming - all under one roof',
            'hero.slide1.cta': 'Book Now',
            'hero.slide2.tag': 'Menu',
            'hero.slide2.title': '24/7 Service Excellence',
            'hero.slide2.subtitle': 'Open around the clock to serve you day and night',
            'hero.slide2.cta': 'View Menu',
            'hero.slide3.tag': 'Experience',
            'hero.slide3.title': 'All-You-Can-Eat & Happy Hour',
            'hero.slide3.subtitle': 'Unlimited dining experiences and special happy hour deals',
            'hero.slide3.cta': 'Explore Gallery',
            'hero.bookNow': 'Book Now',
            'booking.title': 'Book Your Table',
            'booking.titlePerfect': 'Book Your Perfect Table',
            'booking.subtitle': 'Reserve your spot for an unforgettable dining experience',
            'booking.quickReservation': 'Quick Reservation',
            'booking.reservationForm': 'Reservation Form',
            'booking.description': 'Fill out the form below and we\'ll confirm your reservation within 2 hours. Can\'t wait to welcome you!',
            'booking.success': 'Reservation submitted successfully!',
            'booking.error': 'Failed to submit reservation',
            'booking.form.name': 'Full Name',
            'booking.form.namePlaceholder': 'Enter your full name',
            'booking.form.nameRequired': 'Name is required',
            'booking.form.email': 'Email',
            'booking.form.emailPlaceholder': 'Enter your email',
            'booking.form.emailRequired': 'Email is required',
            'booking.form.emailInvalid': 'Invalid email address',
            'booking.form.phone': 'Phone Number',
            'booking.form.phonePlaceholder': 'Enter your phone number',
            'booking.form.phoneRequired': 'Phone number is required',
            'booking.form.date': 'Date',
            'booking.form.dateRequired': 'Date is required',
            'booking.form.time': 'Time',
            'booking.form.timeSelect': 'Select time',
            'booking.form.timeRequired': 'Time is required',
            'booking.form.guests': 'Number of Guests',
            'booking.form.guest': 'Guest',
            'booking.form.guestsSelect': 'Select number of guests',
            'booking.form.guestsRequired': 'Number of guests is required',
            'booking.form.guestsMin': 'Minimum 1 guest',
            'booking.form.guestsMax': 'Maximum 20 guests',
            'booking.form.specialRequests': 'Special Requests',
            'booking.form.specialRequestsPlaceholder': 'Any special requests or dietary requirements...',
            'booking.form.submit': 'Book Table',
            'booking.form.submitting': 'Submitting...',
            'booking.form.orCall': 'Or call us directly:',
            'booking.form.confirmationTime': 'We\'ll confirm your reservation within 2 hours',
            'highlights.title': 'Our',
            'highlights.subtitle': 'Highlights',
            'highlights.description': 'Discover what makes restaurant the premier destination for authentic Italian dining and unforgettable culinary experiences in Goma.',
            'highlights.tandoori.title': 'Kids Menu',
            'highlights.tandoori.description': 'Enjoy our special kids menu featuring child-friendly Italian dishes and fresh preparations perfect for families.',
            'highlights.curries.title': 'Happy Hour Restaurant Discounts',
            'highlights.curries.description': 'Take advantage of our special offers and attractive discounts on our signature dishes during peak hours. A perfect opportunity to discover our cuisine at reduced prices.',
            'highlights.ambiance.title': 'Happy Hour Specials',
            'highlights.ambiance.description': 'More than just dining - enjoy our premium bar, happy hour deals, and vibrant atmosphere perfect for any occasion.',
            'testimonials.title': 'What Our',
            'testimonials.subtitle': 'Customers Say',
            'testimonials.description': 'Don\'t just take our word for it. Here\'s what our valued customers have to say about their dining experience.',
            'testimonials.featured': 'Featured',
            'testimonials.overall.title': 'Overall Rating',
            'testimonials.overall.description': 'Based on {count}+ customer reviews',
            'footer.description': 'Experience authentic Italian cuisine at restaurant in the heart of Goma. We offer exceptional Italian dishes, premium bar service, and happy hour specials.',
            'footer.quickLinks': 'Quick Links',
            'footer.contactInfo': 'Contact Info',
            'footer.openingHours': 'Opening Hours',
            'footer.days.monday': 'Monday',
            'footer.days.tuesday': 'Tuesday',
            'footer.days.wednesday': 'Wednesday',
            'footer.days.thursday': 'Thursday',
            'footer.days.friday': 'Friday',
            'footer.days.saturday': 'Saturday',
            'footer.days.sunday': 'Sunday',
            'footer.closed': 'Closed',
            'footer.hours': '8:00 AM - 1:00 AM',
            'footer.hours.saturday': '10:30 AM - 1:00 AM',
            'footer.copyright': 'All rights reserved.',
            'home.welcome': 'Welcome to',
            'home.description': 'Experience authentic Italian cuisine at restaurant in the heart of Goma. We offer exceptional Italian dishes, premium bar service, happy hour specials, and kids menu.',
            'home.rating.title': '4.3 Star Rating',
            'home.rating.description': 'Consistently rated among the best in Goma',
            'stats.starRating': 'Star Rating',
            'stats.happyCustomers': 'Happy Customers',
            'stats.signatureDishes': 'Signature Dishes',
            'stats.yearsExcellence': 'Years of Excellence',
            'about.title': 'About Us',
            'about.subtitle': 'Discover the story behind restaurant and our passion for exceptional dining and entertainment experiences.',
            'about.story.title': 'Our Story',
            'about.values.title': 'Our Values',
            'about.established': 'Established',
            'about.values.subtitle': 'The principles that guide everything we do at restaurant.',
            'about.values.authenticity.title': 'Excellence',
            'about.values.authenticity.description': 'We strive for excellence in every aspect - from our authentic Italian cuisine and bar service to hospitality.',
            'about.values.quality.title': '24/7 Service',
            'about.values.quality.description': 'We\'re always here for you, providing consistent quality and service around the clock.',
            'about.values.hospitality.title': 'Hospitality',
            'about.values.hospitality.description': 'We create unforgettable experiences combining authentic Italian food, premium drinks, and warm hospitality.',
            'about.team.title': 'Meet Our Team',
            'about.team.subtitle': 'Our passionate team dedicated to bringing you the best Italian dining experience.',
            'about.team.chef.name': 'Chef Marie Uwimana',
            'about.team.chef.role': 'Executive Chef',
            'about.team.chef.description': 'With over 12 years of Italian cuisine experience, Chef Marie creates exceptional authentic Italian dishes for our menu.',
            'about.team.sousChef.name': 'James Kabera',
            'about.team.sousChef.role': 'Bar Manager',
            'about.team.sousChef.description': 'James oversees our premium bar operations, crafting signature cocktails and managing our extensive spirits collection.',
            'about.team.manager.name': 'Sarah Mugisha',
            'about.team.manager.role': 'General Manager',
            'about.team.manager.description': 'Sarah ensures seamless restaurant operations and that every guest receives exceptional service for their dining experience.',
            'about.story.content': 'restaurant is an authentic Italian restaurant located in the heart of Goma, offering exceptional dining experiences. Known for its traditional Italian cuisine, premium bar service, and warm hospitality, restaurant is the perfect place for any occasion.<br/><br/>We provide continuous service to meet all your dining needs throughout the day. Our mission is simple: to create an exceptional destination where authentic Italian food, premium drinks, and genuine hospitality come together.<br/><br/>We specialize in traditional Italian dishes, happy hour specials, and kids menu, ensuring that every visit offers great value and satisfaction. From family dinners to business meetings, from romantic dates to celebrations, restaurant adapts to your needs.<br/><br/>We take pride in our commitment to excellence, authentic Italian cuisine, and creating unforgettable dining experiences. Our dedicated team works to ensure that every guest enjoys exceptional service and memorable moments.',
            'contact.title': 'Contact Us',
            'contact.subtitle': 'Get in touch with us for reservations, inquiries, or just to say hello.',
            'contact.getInTouch': 'Get in Touch',
            'contact.address': 'Address',
            'contact.phone': 'Phone',
            'contact.email': 'Email',
            'contact.hours': 'Opening Hours',
            'contact.hours.description': 'Sunday-Friday: 8:00 AM - 1:00 AM, Saturday: 10:30 AM - 1:00 AM',
            'contact.form.title': 'Send us a Message',
            'contact.form.name': 'Full Name',
            'contact.form.email': 'Email Address',
            'contact.form.subject': 'Subject',
            'contact.form.message': 'Message',
            'contact.form.submit': 'Send Message',
            'contact.form.submitting': 'Sending...',
            'reservation.title': 'Book Your Table',
            'reservation.subtitle': 'Reserve your spot for an unforgettable dining experience',
            'reservation.confirmed.title': 'Reservation Sent!',
            'reservation.confirmed.subtitle': 'Thank you for choosing restaurant. Your reservation has been successfully submitted. A confirmation email will be sent to your email address.',
            'reservation.confirmed.details': 'Reservation Details',
            'reservation.confirmed.message': 'We will contact you shortly to confirm your reservation',
            'reservation.confirmed.another': 'Make Another Reservation',
            'reservation.form.name': 'Full Name',
            'reservation.form.namePlaceholder': 'Enter your full name',
            'reservation.form.email': 'Email Address',
            'reservation.form.emailPlaceholder': 'Enter your email address',
            'reservation.form.phone': 'Phone Number',
            'reservation.form.phonePlaceholder': 'Enter your phone number',
            'reservation.form.date': 'Date',
            'reservation.form.time': 'Time',
            'reservation.form.guests': 'Number of Guests',
            'reservation.form.message': 'Special Requests',
            'reservation.form.messagePlaceholder': 'Any special requests or dietary requirements...',
            'reservation.form.submit': 'Book Table',
            'reservation.form.submitting': 'Submitting...',
            'reservation.info.title': 'Reservation Info',
            'reservation.info.openingHours': 'Opening Hours',
            'reservation.info.monday': 'Monday',
            'reservation.info.closed': 'Closed',
            'reservation.info.tuesdaySunday': 'Sunday - Friday',
            'reservation.info.hours': '8:00 AM - 1:00 AM',
            'reservation.info.contact': 'Contact',
            'reservation.info.location': 'Location',
            'reservation.info.importantNotes': 'Important Notes',
            'reservation.info.notes.held': 'Reservations are held for 15 minutes',
            'reservation.info.notes.groups': 'Large groups (8+ guests) require 24h notice',
            'reservation.info.notes.dietary': 'Special dietary requirements can be accommodated',
            'reservation.info.notes.confirmation': 'Confirmation will be sent via email/SMS',
            'home.hours.title': 'Extended Hours',
            'home.hours.description': 'Sunday-Friday: 8:00 AM - 1:00 AM, Saturday: 10:30 AM - 1:00 AM',
            'home.location.title': 'Prime Location',
            'home.location.description': 'Goma, Congo-Kinshasa',
            'home.cta.title': 'Ready to Experience Ultimate Dining & Entertainment?',
            'home.cta.description': 'Book your table today and enjoy our extended hours, all-you-can-eat buffet, premium bar, and exciting gaming experiences.',
            'home.cta.readyToDine': 'Ready to dine?',
            'home.cta.callNow': 'Call Now',
            'home.features.extendedHours.title': 'Extended Hours',
            'home.features.extendedHours.description': 'Open late every day for your convenience',
            'home.features.primeLocation.title': 'Prime Location',
            'home.features.primeLocation.description': 'Easy parking & accessibility',
            'home.features.allYouCanEat.title': 'Kids Menu',
            'home.features.allYouCanEat.description': 'Special children\'s dishes for families',
            'highlights.stats.awardWinner': 'Award Winner',
            'highlights.stats.customers': '500+ Customers',
            'highlights.stats.experience': '5+ Years',
            'testimonials.stats.averageRating': '4.3★ Average',
            'testimonials.stats.totalReviews': '500+ Reviews',
            'testimonials.stats.satisfiedCustomers': '95% Satisfied',
            'testimonials.stats.totalReviewsLabel': 'Total Reviews',
            'testimonials.stats.fiveStarReviews': '5-Star Reviews',
            'testimonials.stats.recommendUs': 'Recommend Us',
            'gallery.discoverMore': 'Discover more stunning photos from our restaurant',
            'home.badges.entertainmentHub': 'Entertainment Hub',
            'home.badges.healthCertified': 'Health Certified',
            'home.badges.rating': '4.8★ Rating',
            'highlights.signatureExperience': 'Our Signature Experience',
            'home.premiumExperience': 'Premium Experience',
            'home.premiumBarExperience': 'Premium Bar Experience',
            'testimonials.customerStories': 'Customer Love Stories',
            'testimonials.overallRating': 'Overall Rating',
            'footer.privacyPolicy': 'Privacy Policy',
            'footer.termsOfService': 'Terms of Service',
            'footer.cookiePolicy': 'Cookie Policy',
            'gallery.viewFullGallery': 'View Full Gallery',
            // Hero section specific translations
            'hero.cta.reservation': 'Book a Table',
            'hero.cta.menu': 'View Menu',
            'loading': 'Loading...',
            'error.title': 'Oops!',
            'error.hero': 'Unable to load content',
            'status.live': 'Live API',
            'status.demo': 'Demo Fallback',
            'menu.title': 'Our Menu',
            'menu.subtitle': 'Discover our carefully curated selection of international dishes, prepared with the finest ingredients and modern cooking techniques.',
            'menu.allDishes': 'All Dishes',
            'menu.loading': 'Loading menu...',
            'menu.loadingCategories': 'Loading categories...',
            'menu.loadingItems': 'Loading dishes...',
            'menu.noItems': 'No dishes found in this category.',
            'menu.viewDetails': 'View Details',
            'menu.addToOrder': 'Add to Order',
            'menu.close': 'Close',
            'menu.vegetarian': 'Vegetarian',
            'menu.spicy': 'Spicy',
            'menu.searchPlaceholder': 'Search for a dish...',
            'menu.filters': 'Filters',
            'menu.dietary': 'Dietary',
            'menu.priceRange': 'Price Range',
            'menu.allPrices': 'All Prices',
            'menu.under15': 'Under $15',
            'menu.15to20': '$15 - $20',
            'menu.over20': 'Over $20',
            'menu.reset': 'Reset',
            'menu.exploreCategories': 'Explore our categories',
            'menu.dishes': 'Dishes',
            'menu.chefSelection': 'Chef\'s Selection',
            'menu.internationalCuisine': 'International Cuisine',
            'menu.culinaryDelights': 'Culinary Delights',
            'menu.curriesDescription': 'From traditional pasta to freshly baked pizza, each dish is crafted with authentic Italian ingredients and traditional techniques passed down through generations.',
            'menu.noItemsDescription': 'Try adjusting your filters or search terms to find what you\'re looking for.',
            'menu.resetFilters': 'Reset Filters',
            'gallery.title': 'Our Gallery',
            'gallery.culinaryArtistry': 'Culinary Artistry',
            'gallery.visualGallery': 'Visual Gallery',
            'restaurantBar.title': 'restaurant Bar',
            'restaurantBar.subtitle': 'Premium Beverages & Cocktails',
            'restaurantBar.description': 'Discover our sophisticated bar experience featuring premium spirits, handcrafted cocktails, and an extensive wine selection.',
            'restaurantBar.signature.title': 'Signature Cocktails',
            'restaurantBar.signature.description': 'Expertly crafted cocktails with premium spirits and fresh ingredients, inspired by international flavors and classic recipes.',
            'restaurantBar.wines.title': 'Wine Collection',
            'restaurantBar.wines.description': 'Curated selection of fine wines from around the world, perfectly paired with our diverse international cuisine.',
            'restaurantBar.spirits.title': 'Premium Spirits',
            'restaurantBar.spirits.description': 'An extensive collection of premium whiskeys, gins, vodkas, and other fine spirits for the discerning palate.',
            'restaurantBar.features.expertBartenders': 'Expert Bartenders',
            'restaurantBar.features.premiumSpirits': 'Premium Spirits',
            'restaurantBar.features.customCocktails': 'Custom Cocktails',
            'restaurantBar.features.wineCollection': 'Wine Collection',
            'restaurantBar.features.happyHour': 'Happy Hour Daily',
            'restaurantBar.features.liveMusic': 'Live Music Nights',
            'restaurantBar.cta': 'Visit Our Bar',
            'restaurantBar.hours': 'Bar Hours: Sunday-Friday 8:00 AM - 1:00 AM, Saturday 10:30 AM - 1:00 AM',
            // Hardcoded texts identified
            'booking.form.numberOfGuests': 'Number of Guests',
            'booking.form.selectPartySize': 'Select party size',
            'reservation.confirmed.thankYou': 'Thank you for choosing us! Your reservation has been sent successfully. A confirmation email will be sent to your email address. We\'re excited to welcome you for an authentic Italian dining experience.',
            'contact.fromCityCenter': 'From City Center',
            'contact.parkingAvailable': 'Parking Available',
            'contact.free': 'Free',
            'contact.minutes': 'min',
            'contact.contactOurTeam': 'Contact Our Team',
            'contact.readyToExperience': 'Ready to experience authentic Italian dining? We\'re here to help you plan your perfect visit to restaurant.',
            'contact.contactDetails': 'Contact Details',
            'contact.howToReachUs': 'How to Reach Us',
            'contact.helpWithReservations': 'We\'re here to help you with reservations, inquiries, or any special requests for your dining experience.',
            'contact.visitRestaurant': 'Visit Our Restaurant',
            'contact.callUsDirectly': 'Call Us Directly',
            'contact.sendUsEmail': 'Send Us an Email',
            'contact.openingHours': 'Opening Hours',
            'contact.getDirections': 'Get Directions',
            'contact.callNow': 'Call Now',
            'contact.sendEmail': 'Send Email',
            'contact.bookTable': 'Book Table',
            'contact.availableDuringBusiness': 'Available during business hours',
            'contact.respondWithin24': 'We\'ll respond within 24 hours',
            'contact.monday': 'Monday',
            'contact.tuesdayToSunday': 'Tuesday - Sunday',
            'contact.closed': 'Closed',
            'contact.findUs': 'Find Us',
            'contact.sendMessage': 'Send Message',
            'contact.getInTouchWithUs': 'Get in Touch with Us',
            'contact.haveQuestion': 'Have a question, special request, or want to make a reservation? We\'d love to hear from you and help make your dining experience perfect.',
            'contact.personalInformation': 'Personal Information',
            'contact.contactInformation': 'Contact Information',
            'contact.messageDetails': 'Message Details',
            'contact.firstName': 'First Name',
            'contact.lastName': 'Last Name',
            'contact.emailAddress': 'Email Address',
            'contact.phoneNumber': 'Phone Number',
            'contact.subject': 'Subject',
            'contact.yourMessage': 'Your Message',
            'contact.enterFirstName': 'Enter your first name',
            'contact.enterLastName': 'Enter your last name',
            'contact.emailPlaceholder': 'your.email@example.com',
            'contact.phonePlaceholder': '+243 xxx xxx xxx',
            'contact.selectPurpose': 'Select the purpose of your message',
            'contact.messagePlaceholder': 'Tell us about your inquiry, special requirements, preferred dates, number of guests, or any other details that would help us assist you better...',
            'contact.respondWithin24Hours': 'We\'ll respond within 24 hours',
            'contact.readyToDine': 'Ready to Dine?',
            'contact.experienceAuthentic': 'Experience Authentic Italian Cuisine',
            'contact.readyToEmbark': 'Ready to embark on a culinary journey through the rich flavors of Italy? Book your table today and let us create an unforgettable dining experience for you.',
            'contact.callNumber': 'Call +243 971 466 525',
            'contact.bookOnline': 'Book Online',
            'contact.quickResponse': 'Quick Response',
            'contact.confirmWithin2Hours': 'We\'ll confirm your reservation within 2 hours',
            'contact.premiumExperience': 'Premium Experience',
            'contact.diningEntertainment': '24/7 dining experience',
            'contact.perfectLocation': 'Perfect Location',
            'contact.easyToFind': 'Easy to find with free parking available',
            'contact.reservationInquiry': '🍽️ Reservation Inquiry',
            'contact.generalInquiry': '💬 General Inquiry',
            'contact.feedbackReviews': '⭐ Feedback & Reviews',
            'contact.privateEventBooking': '🎉 Private Event Booking',
            'contact.cateringServices': '🚚 Catering Services',
            'contact.dietaryRequirements': '🥗 Dietary Requirements',
            'contact.other': '📝 Other',
            'reservation.bookYourTable': 'Book Your Table',
            'reservation.reserveDiningExperience': 'Reserve Your Dining Experience',
            'reservation.secureYourTable': 'Secure your table at our award-winning restaurant and embark on an unforgettable culinary journey through authentic Indian flavors.',
            'reservation.instantConfirmation': 'Instant Confirmation',
            'reservation.awardWinning': 'Award Winning',
            'reservation.madeWithLove': 'Made with Love',
            'reservation.personalInformation': 'Personal Information',
            'reservation.reservationDetails': 'Reservation Details',
            'reservation.specialRequests': 'Special Requests',
            'reservation.fullName': 'Full Name',
            'reservation.emailAddress': 'Email Address',
            'reservation.phoneNumber': 'Phone Number',
            'reservation.preferredDate': 'Preferred Date',
            'reservation.preferredTime': 'Preferred Time',
            'reservation.numberOfGuests': 'Number of Guests',
            'reservation.additionalInformation': 'Additional Information (Optional)',
            'reservation.specialOccasionPlaceholder': 'Tell us about any special occasion, dietary requirements, seating preferences, or other requests that would help us make your experience perfect...',
            'reservation.examples': '💡 Examples: Anniversary celebration, wheelchair accessibility, vegetarian menu, window seat, etc.',
            'reservation.confirmWithin2Hours': 'We\'ll confirm your reservation within 2 hours',
            'reservation.submitting': 'Submitting...',
            'reservation.reserveMyTable': 'Reserve My Table',
            'reservation.restaurantInfo': 'Restaurant Info',
            'reservation.everythingYouNeed': 'Everything you need to know',
            'reservation.openingHours': 'Opening Hours',
            'reservation.monday': 'Monday',
            'reservation.tuesdayToSunday': 'Tuesday - Sunday',
            'reservation.closed': 'Closed',
            'reservation.contactDetails': 'Contact Details',
            'reservation.callUsDirectly': 'Call us directly',
            'reservation.sendUsEmail': 'Send us an email',
            'reservation.ourLocation': 'Our Location',
            'reservation.freeParkingAvailable': 'Free parking available',
            'reservation.importantNotes': 'Important Notes',
            'reservation.reservationsHeld': 'Reservations held for 15 minutes past booking time',
            'reservation.groupsAdvanceNotice': 'Groups of 8+ require advance notice',
            'reservation.dietaryRequirements': 'Dietary requirements can be accommodated',
            'reservation.confirmationWithin2Hours': 'Confirmation within 2 hours via email/phone',
            'reservation.specialOffers': 'Special Offers',
            'reservation.limitedTimePromotions': 'Limited time promotions',
            'reservation.anniversarySpecial': '🎉 Anniversary Special',
            'reservation.complimentaryDessert': 'Complimentary dessert for celebrations',
            'reservation.winePairing': '🍷 Wine Pairing',
            'reservation.expertSommelier': 'Expert sommelier recommendations',
            'reservation.confirmed.confirmationId': 'Confirmation ID',
            'reservation.confirmed.responseTime': 'Response Time',
            'reservation.confirmed.status': 'Status',
            'reservation.confirmed.within2Hours': 'Within 2 hours',
            'reservation.confirmed.confirmed': 'Confirmed',
            'reservation.confirmed.emailSent': 'A confirmation email will be sent to your email address. Our team will contact you within 2 hours to confirm the details.',
            'reservation.confirmed.makeAnother': 'Make Another Reservation',
            'reservation.confirmed.returnHome': 'Return Home',
            'reservation.lunch': 'Lunch',
            'reservation.dinner': 'Dinner',
            'reservation.selectTimeSlot': 'Select time slot',
            'reservation.selectPartySize': 'Select party size',
            'reservation.guest': 'Guest',
            'reservation.guests': 'Guests',
            'about.ourStory': 'Our Story',
            'about.discoverOurJourney': 'Discover Our Journey',
            'about.awardWinner': 'Award Winner',
            'about.customers': 'Customers',
            'about.yearsExcellence': 'Years Excellence',
            'about.ourBeginning': 'Our Beginning',
            'about.culinaryJourney': 'A Culinary Journey Born from Passion',
            'about.uniqueEstablishment': 'restaurant is an authentic Italian restaurant located in the heart of Goma, offering exceptional dining experiences.',
            'about.storyBegins': 'Our story begins with a passionate chef who traveled across Italy, learning traditional cooking techniques from master chefs in Rome, Florence, and Naples.',
            'about.todayWePride': 'Today, we pride ourselves on serving not just food, but an experience that transports you to the vibrant streets and aromatic kitchens of Italy.',
            'about.authenticRecipes': 'Authentic Italian recipes passed down through generations',
            'about.freshSpices': 'Fresh ingredients imported directly from Italy',
            'about.serviceAvailability': '24/7 service availability',
            'about.ourAchievements': 'Our Achievements',
            'about.numbersTellStory': 'Numbers That Tell Our Story',
            'about.milestonesRepresent': 'These milestones represent our commitment to excellence and the trust our customers place in us.',
            'about.ourValues': 'Our Values',
            'about.whatDrivesUs': 'What Drives Us Forward',
            'about.coreValuesShape': 'Our core values shape every aspect of our culinary journey, from sourcing ingredients to serving our guests.',
            'about.authenticity': 'Authenticity',
            'about.authenticityDescription': 'We honor traditional Indian cooking methods and authentic recipes passed down through generations.',
            'about.quality': 'Quality',
            'about.qualityDescription': 'Every ingredient is carefully selected and every dish is prepared with meticulous attention to detail.',
            'about.hospitality': 'Hospitality',
            'about.hospitalityDescription': 'We believe in treating every guest like family, creating warm and memorable dining experiences.',
            'about.meetOurTeam': 'Meet Our Team',
            'about.passionatePeople': 'The Passionate People Behind Our Success',
            'about.meetTalentedIndividuals': 'Meet the talented individuals who bring our culinary vision to life with expertise, passion, and dedication.',
            'about.chefRajeshKumar': 'Chef Rajesh Kumar',
            'about.executiveChef': 'Executive Chef',
            'about.chefRajeshDescription': 'With 15+ years of experience, Chef Rajesh brings authentic Indian flavors to every dish.',
            'about.internationalCuisine': 'International Cuisine & Bar',
            'about.chefPriyaSharma': 'Chef Priya Sharma',
            'about.sousChef': 'Sous Chef',
            'about.chefPriyaDescription': 'Specializing in South Indian cuisine, Chef Priya ensures every curry is perfectly balanced.',
            'about.southIndianCurries': 'South Indian Curries',
            'about.amitPatel': 'Amit Patel',
            'about.restaurantManager': 'Restaurant Manager',
            'about.amitDescription': 'Amit ensures every guest feels welcome and enjoys an exceptional dining experience.',
            'about.guestExperience': 'Guest Experience',
            'about.loadingOurStory': 'Loading our story...',
            'join.stayConnected': 'Stay Connected',
            'join.culinaryJourney': 'Join Our Culinary Journey',
            'join.getExclusiveOffers': 'Get exclusive offers, recipes, and be the first to know about our special events',
            'join.enterYourEmail': 'Enter your email',
            'join.subscribe': 'Subscribe',
            'join.rating': '4.3★ Rating',
            'join.awardWinner': 'Award Winner',
            'join.happyCustomers': '500+ Happy Customers',
            'join.diningEntertainmentHub': 'Dining & Entertainment Hub',
            'join.followUs': 'Follow us:',
            'gallery.loadingCategories': 'Loading gallery...',
            'gallery.subtitle': 'Immerse yourself in a visual journey through our culinary artistry, ambiance, and unforgettable moments.',
            'gallery.photos': '50+ Photos',
            'gallery.highQuality': 'High Quality',
            'gallery.curatedCollection': 'Curated Collection',
            'gallery.browseCollection': 'Browse Our Collection',
            'gallery.photo': 'photo',
            'gallery.photosAvailable': 'photos available',
            'gallery.allPhotos': 'All Photos',
            'gallery.loadingItems': 'Loading beautiful photos...',
            'gallery.viewFullSize': 'View Full Size',
            'gallery.gallery': 'Gallery',
            'gallery.noPhotosFound': 'No Photos Found',
            'gallery.noPhotos': 'No photos are available in this category at the moment.',
            'gallery.tryDifferentCategory': 'Try selecting a different category or check back later for new content.',
            'gallery.viewAllPhotos': 'View All Photos',
            'gallery.fullResolution': 'Full Resolution',
            'booking.personalInformation': 'Personal Information',
            'booking.fullName': 'Full Name',
            'booking.fullNameRequired': 'Full name is required',
            'booking.enterFullName': 'Enter your full name',
            'booking.emailAddress': 'Email Address',
            'booking.emailRequired': 'Email address is required',
            'booking.invalidEmail': 'Please enter a valid email address',
            'booking.emailPlaceholder': 'your.email@example.com',
            'booking.phoneNumber': 'Phone Number',
            'booking.phoneRequired': 'Phone number is required',
            'booking.phonePlaceholder': '+243 xxx xxx xxx',
            'booking.reservationDetails': 'Reservation Details',
            'booking.preferredDate': 'Preferred Date',
            'booking.dateRequired': 'Date is required',
            'booking.preferredTime': 'Preferred Time',
            'booking.timeRequired': 'Time is required',
            'booking.selectTimeSlot': 'Select time slot',
            'booking.lunch': 'Lunch',
            'booking.dinner': 'Dinner',
            'booking.additionalInformation': 'Additional Information (Optional)',
            'booking.specialOccasionPlaceholder': 'Tell us about any special occasion, dietary requirements, seating preferences, or other requests that would help us make your experience perfect...',
            'booking.examples': '💡 Examples: Anniversary celebration, wheelchair accessibility, vegetarian menu, window seat, etc.',
            'booking.confirmWithin2Hours': 'We\'ll confirm your reservation within 2 hours',
            'booking.submitting': 'Submitting...',
            'booking.reserveTable': 'Reserve Table',
            // Accessibility labels
            'aria.toggleTheme': 'Toggle theme',
            'aria.toggleMenu': 'Toggle menu',
            'aria.selectLanguage': 'Select language',
            'aria.previousSlide': 'Previous slide',
            'aria.nextSlide': 'Next slide',
            'aria.goToSlide': 'Go to slide {number}',
            'aria.resumeVideo': 'Resume video',
            'aria.pauseVideo': 'Pause video',
            'aria.facebook': 'Facebook',
            'aria.instagram': 'Instagram',
            'aria.twitter': 'Twitter',
            // Alt text
            'alt.logo': 'restaurant Logo',
            'alt.restaurantInterior': 'Restaurant Interior',
        },
        fr: {
            'nav.home': 'Accueil',
            'nav.menu': 'Menu',
            'nav.about': 'Apropo',
            'nav.gallery': 'Galerie',
            'nav.contact': 'Contact',
            'nav.reservations': 'Réservations',
            'hero.slide1.tag': 'Bienvenue',
            'hero.slide1.title': 'Restauration et Divertissement Ultime',
            'hero.slide1.subtitle': 'Découvrez une cuisine exceptionnelle, un bar premium et des jeux passionnants - tout sous un même toit',
            'hero.slide1.cta': 'Réserver maintenant',
            'hero.slide2.tag': 'Menu',
            'hero.slide2.title': 'Expérience gastronomique élégante',
            'hero.slide2.subtitle': 'Ambiance sophistiquée pour des moments mémorables',
            'hero.slide2.cta': 'Voir le menu',
            'hero.slide3.tag': 'Expérience',
            'hero.slide3.title': 'Épices traditionnelles, techniques modernes',
            'hero.slide3.subtitle': 'Où la tradition rencontre l\'innovation dans chaque plat',
            'hero.slide3.cta': 'Explorer la galerie',
            'hero.bookNow': 'Réserver maintenant',
            'booking.title': 'Réserver votre table',
            'booking.titlePerfect': 'Réservez Votre Table Parfaite',
            'booking.subtitle': 'Réservez votre place pour une expérience culinaire inoubliable',
            'booking.quickReservation': 'Réservation Rapide',
            'booking.reservationForm': 'Formulaire de Réservation',
            'booking.description': 'Remplissez le formulaire ci-dessous et nous confirmerons votre réservation dans les 2 heures. Nous avons hâte de vous accueillir !',
            'booking.success': 'Réservation soumise avec succès !',
            'booking.error': 'Erreur lors de la soumission de la réservation',
            'booking.form.name': 'Nom complet',
            'booking.form.namePlaceholder': 'Entrez votre nom complet',
            'booking.form.nameRequired': 'Le nom est requis',
            'booking.form.email': 'Email',
            'booking.form.emailPlaceholder': 'Entrez votre email',
            'booking.form.emailRequired': 'L\'email est requis',
            'booking.form.emailInvalid': 'Adresse email invalide',
            'booking.form.phone': 'Numéro de téléphone',
            'booking.form.phonePlaceholder': 'Entrez votre numéro de téléphone',
            'booking.form.phoneRequired': 'Le numéro de téléphone est requis',
            'booking.form.date': 'Date',
            'booking.form.dateRequired': 'La date est requise',
            'booking.form.time': 'Heure',
            'booking.form.timeSelect': 'Sélectionner l\'heure',
            'booking.form.timeRequired': 'L\'heure est requise',
            'booking.form.guests': 'Nombre d\'invités',
            'booking.form.guest': 'Invité',
            'booking.form.guestsSelect': 'Sélectionner le nombre d\'invités',
            'booking.form.guestsRequired': 'Le nombre d\'invités est requis',
            'booking.form.guestsMin': 'Minimum 1 invité',
            'booking.form.guestsMax': 'Maximum 20 invités',
            'booking.form.specialRequests': 'Demandes spéciales',
            'booking.form.specialRequestsPlaceholder': 'Toute demande spéciale ou exigence alimentaire...',
            'booking.form.submit': 'Réserver une table',
            'booking.form.submitting': 'Soumission...',
            'booking.form.orCall': 'Ou appelez-nous directement :',
            'booking.form.confirmationTime': 'Nous confirmerons votre réservation dans les 2 heures',
            'highlights.title': 'Nos',
            'highlights.subtitle': 'Points forts',
            'highlights.description': 'Découvrez ce qui fait d\'restaurant la destination de choix pour la restauration, le divertissement et les expériences inoubliables à Goma.',
            'highlights.tandoori.title': 'Buffet à Volonté',
            'highlights.tandoori.description': 'Profitez d\'un buffet illimité avec une cuisine diverse et des préparations fraîches tout au long de la journée.',
            'highlights.curries.title': 'Remises "happy hour" sur la restauration',
            'highlights.curries.description': 'Profitez de nos offres spéciales et remises attractives sur nos plats signature pendant nos heures de pointe. Une occasion parfaite de découvrir notre cuisine à prix réduit.',
            'highlights.ambiance.title': 'L\'Ambiance',
            'highlights.ambiance.description': 'Immergez-vous dans notre atmosphère sophistiquée mais chaleureuse, parfaite pour les dîners intimes et les célébrations spéciales.',
            'testimonials.title': 'Ce que disent',
            'testimonials.subtitle': 'nos clients',
            'testimonials.description': 'Ne vous fiez pas seulement à nos dires. Voici ce que nos clients appréciés ont à dire sur leur expérience culinaire.',
            'testimonials.featured': 'En vedette',
            'testimonials.overall.title': 'Note globale',
            'testimonials.overall.description': 'Basé sur {count}+ avis clients',
            'footer.description': 'Découvrez la cuisine italienne authentique chez restaurant au cœur de Goma. Nous offrons des plats italiens exceptionnels, un service de bar premium et des offres happy hour.',
            'footer.quickLinks': 'Liens rapides',
            'footer.contactInfo': 'Informations de contact',
            'footer.openingHours': 'Heures d\'ouverture',
            'footer.days.monday': 'Lundi',
            'footer.days.tuesday': 'Mardi',
            'footer.days.wednesday': 'Mercredi',
            'footer.days.thursday': 'Jeudi',
            'footer.days.friday': 'Vendredi',
            'footer.days.saturday': 'Samedi',
            'footer.days.sunday': 'Dimanche',
            'footer.closed': 'Fermé',
            'footer.hours': '8h30 - 01h00',
            'footer.hours.saturday': '10h30 - 01h00',
            'footer.copyright': 'Tous droits réservés.',
            'home.welcome': 'Bienvenue chez',
            'home.description': 'Découvrez la cuisine italienne authentique chez restaurant au cœur de Goma. Nous offrons des plats italiens exceptionnels, un service de bar premium, des offres happy hour et un menu enfant.',
            'home.rating.title': 'Note de 4,3 étoiles',
            'home.rating.description': 'Considéré parmi les meilleurs de Kinshasa',
            'stats.starRating': 'Note Étoiles',
            'stats.happyCustomers': 'Clients Satisfaits',
            'stats.signatureDishes': 'Plats Signature',
            'stats.yearsExcellence': 'Années d\'Excellence',
            'about.title': 'À propos de nous',
            'about.subtitle': 'Découvrez l\'histoire derrière restaurant et notre passion pour les expériences exceptionnelles de restauration et de divertissement.',
            'about.story.title': 'Notre Histoire',
            'about.values.title': 'Nos Valeurs',
            'about.established': 'Établi',
            'about.values.subtitle': 'Les principes qui guident tout ce que nous faisons chez restaurant.',
            'about.values.authenticity.title': 'Authenticité',
            'about.values.authenticity.description': 'Nous restons fidèles aux méthodes et recettes traditionnelles indiennes, garantissant que chaque plat est authentique.',
            'about.values.quality.title': 'Qualité',
            'about.values.quality.description': 'Nous utilisons uniquement les meilleurs ingrédients et maintenons les plus hauts standards dans la préparation des aliments.',
            'about.values.hospitality.title': 'Hospitalité',
            'about.values.hospitality.description': 'Nous croyons en la création d\'expériences chaleureuses et accueillantes qui font que chaque invité se sent spécial.',
            'about.team.title': 'Rencontrez notre équipe',
            'about.team.subtitle': 'Notre équipe passionnée d\'experts culinaires dédiés à vous apporter la meilleure expérience de restauration indienne.',
            'about.team.chef.name': 'Chef Rajesh Kumar',
            'about.team.chef.role': 'Chef Principal',
            'about.team.chef.description': 'Avec plus de 12 ans d\'expérience en cuisine internationale, le Chef Marie crée des plats exceptionnels pour notre menu diversifié.',
            'about.team.sousChef.name': 'Priya Sharma',
            'about.team.sousChef.role': 'Chef Adjoint',
            'about.team.sousChef.description': 'James supervise nos opérations de bar premium, créant des cocktails signature et gérant notre vaste collection de spiritueux.',
            'about.team.manager.name': 'Amit Patel',
            'about.team.manager.role': 'Directeur de Restaurant',
            'about.team.manager.description': 'Amit s\'assure que chaque invité reçoit un service exceptionnel et une expérience de restauration mémorable.',
            'about.story.content': 'restaurant est un établissement unique situé au cœur de Goma, offrant une expérience complète de restauration et de divertissement. Connu pour ses services diversifiés de bar, restaurant et jeux, restaurant est un lieu de rassemblement dynamique pour toute occasion.<br/><br/>Avec des horaires d\'ouverture 24h/24, nous offrons un service continu pour répondre à tous vos besoins, de jour comme de nuit. Notre mission est simple : créer une destination exceptionnelle où la grande cuisine, les boissons premium et le divertissement passionnant se rencontrent sous un même toit.<br/><br/>Nous nous spécialisons dans les expériences buffet à volonté et les offres happy hour, garantissant que chaque visite offre une grande valeur et satisfaction. Du petit-déjeuner matinal au dîner tardif, des réunions d\'affaires aux célébrations, restaurant s\'adapte à votre style de vie.<br/><br/>Nous sommes fiers de notre engagement envers l\'excellence, la disponibilité 24h/7 et la création d\'expériences inoubliables. Notre équipe dévouée travaille 24h/24 pour s\'assurer que chaque client profite d\'un service exceptionnel et de moments mémorables.',
            'contact.title': 'Contactez-nous',
            'contact.subtitle': 'Contactez-nous pour des réservations, des demandes de renseignements ou simplement pour dire bonjour.',
            'contact.getInTouch': 'Contactez-nous',
            'contact.address': 'Adresse',
            'contact.phone': 'Téléphone',
            'contact.email': 'Email',
            'contact.hours': 'Heures d\'ouverture',
            'contact.hours.description': 'Dimanche-Vendredi: 8h30 - 01h00, Samedi: 10h30 - 01h00',
            'contact.form.title': 'Envoyez-nous un message',
            'contact.form.name': 'Nom complet',
            'contact.form.email': 'Adresse email',
            'contact.form.subject': 'Sujet',
            'contact.form.message': 'Message',
            'contact.form.submit': 'Envoyer le message',
            'contact.form.submitting': 'Envoi...',
            'reservation.title': 'Réservez votre table',
            'reservation.subtitle': 'Réservez votre place pour une expérience culinaire inoubliable',
            'reservation.confirmed.title': 'Réservation envoyée !',
            'reservation.confirmed.subtitle': 'Merci d\'avoir choisi restaurant. Votre réservation a été soumise avec succès. Un email de confirmation sera envoyé à votre adresse email.',
            'reservation.confirmed.details': 'Détails de la réservation',
            'reservation.confirmed.message': 'Nous vous contacterons bientôt pour confirmer votre réservation',
            'reservation.confirmed.another': 'Faire une autre réservation',
            'reservation.form.name': 'Nom complet',
            'reservation.form.namePlaceholder': 'Entrez votre nom complet',
            'reservation.form.email': 'Adresse email',
            'reservation.form.emailPlaceholder': 'Entrez votre adresse email',
            'reservation.form.phone': 'Numéro de téléphone',
            'reservation.form.phonePlaceholder': 'Entrez votre numéro de téléphone',
            'reservation.form.date': 'Date',
            'reservation.form.time': 'Heure',
            'reservation.form.guests': 'Nombre d\'invités',
            'reservation.form.message': 'Demandes spéciales',
            'reservation.form.messagePlaceholder': 'Toute demande spéciale ou exigence alimentaire...',
            'reservation.form.submit': 'Réserver une table',
            'reservation.form.submitting': 'Soumission...',
            'reservation.info.title': 'Informations de réservation',
            'reservation.info.openingHours': 'Heures d\'ouverture',
            'reservation.info.monday': 'Lundi',
            'reservation.info.closed': 'Fermé',
            'reservation.info.tuesdaySunday': 'Dimanche - Vendredi',
            'reservation.info.hours': '8h30 - 01h00',
            'reservation.info.contact': 'Contact',
            'reservation.info.location': 'Emplacement',
            'reservation.info.importantNotes': 'Notes importantes',
            'reservation.info.notes.held': 'Les réservations sont maintenues pendant 15 minutes',
            'reservation.info.notes.groups': 'Les grands groupes (8+ invités) nécessitent un préavis de 24h',
            'reservation.info.notes.dietary': 'Les exigences alimentaires spéciales peuvent être accommodées',
            'reservation.info.notes.confirmation': 'La confirmation sera envoyée par email/SMS',
            'home.hours.title': 'Horaires Étendus',
            'home.hours.description': 'Dimanche-Vendredi: 8h30 - 01h00, Samedi: 10h30 - 01h00',
            'home.location.title': 'Emplacement privilégié',
            'home.location.description': 'Modern Paradise 8225, Av. Kabasele Tshamala',
            'home.cta.title': 'Prêt à découvrir la cuisine italienne authentique ?',
            'home.cta.description': 'Réservez votre table aujourd\'hui et profitez de notre cuisine italienne exceptionnelle, bar premium et offres happy hour.',
            'home.cta.readyToDine': 'Prêt à dîner ?',
            'home.cta.callNow': 'Appeler maintenant',
            'home.features.extendedHours.title': 'Horaires Étendus',
            'home.features.extendedHours.description': 'Ouvert tard chaque jour pour votre commodité',
            'home.features.primeLocation.title': 'Emplacement Privilégié',
            'home.features.primeLocation.description': 'Stationnement facile et accessibilité',
            'home.features.allYouCanEat.title': 'Menu Enfant',
            'home.features.allYouCanEat.description': 'Plats spéciaux pour enfants pour les familles',
            'highlights.stats.awardWinner': 'Gagnant de Prix',
            'highlights.stats.customers': '500+ Clients',
            'highlights.stats.experience': '5+ Années',
            'testimonials.stats.averageRating': '4.3★ Moyenne',
            'testimonials.stats.totalReviews': '500+ Avis',
            'testimonials.stats.satisfiedCustomers': '95% Satisfaits',
            'testimonials.stats.totalReviewsLabel': 'Total des Avis',
            'testimonials.stats.fiveStarReviews': 'Avis 5 Étoiles',
            'testimonials.stats.recommendUs': 'Nous Recommandent',
            'gallery.discoverMore': 'Découvrez plus de photos magnifiques de notre restaurant',
            'home.badges.entertainmentHub': 'Hub de Divertissement',
            'home.badges.healthCertified': 'Certifié Santé',
            'home.badges.rating': '4.8★ Note',
            'highlights.signatureExperience': 'Notre Expérience Signature',
            'home.premiumExperience': 'Expérience Premium',
            'home.premiumBarExperience': 'Expérience Bar Premium',
            'testimonials.customerStories': 'Histoires d\'Amour des Clients',
            'testimonials.overallRating': 'Note Globale',
            'footer.privacyPolicy': 'Politique de Confidentialité',
            'footer.termsOfService': 'Conditions d\'Utilisation',
            'footer.cookiePolicy': 'Politique des Cookies',
            'gallery.viewFullGallery': 'Voir la Galerie Complète',
            // Hero section specific translations
            'hero.cta.reservation': 'Réserver une table',
            'hero.cta.menu': 'Voir le menu',
            'loading': 'Chargement...',
            'error.title': 'Oops!',
            'error.hero': 'Impossible de charger le contenu',
            'status.live': 'En direct API',
            'status.demo': 'Démo Fallback',
            'menu.title': 'Notre Menu',
            'menu.subtitle': 'Découvrez notre sélection soigneusement élaborée de plats internationaux, préparés avec les meilleurs ingrédients et des techniques culinaires modernes.',
            'menu.allDishes': 'Tous les plats',
            'menu.loading': 'Chargement du menu...',
            'menu.loadingCategories': 'Chargement des catégories...',
            'menu.loadingItems': 'Chargement des plats...',
            'menu.noItems': 'Aucun plat trouvé dans cette catégorie.',
            'menu.viewDetails': 'Voir les détails',
            'menu.addToOrder': 'Ajouter à la commande',
            'menu.close': 'Fermer',
            'menu.vegetarian': 'Végétarien',
            'menu.spicy': 'Épicé',
            'menu.searchPlaceholder': 'Rechercher un plat...',
            'menu.filters': 'Filtres',
            'menu.dietary': 'Régime alimentaire',
            'menu.priceRange': 'Gamme de prix',
            'menu.allPrices': 'Tous les prix',
            'menu.under15': 'Moins de $15',
            'menu.15to20': '$15 - $20',
            'menu.over20': 'Plus de $20',
            'menu.reset': 'Réinitialiser',
            'menu.exploreCategories': 'Explorez nos catégories',
            'menu.dishes': 'Plats',
            'menu.chefSelection': 'Sélection du Chef',
            'menu.internationalCuisine': 'Cuisine Internationale',
            'menu.culinaryDelights': 'Délices Culinaires',
            'menu.curriesDescription': 'Des currys aromatiques aux naans fraîchement cuits, chaque plat est préparé avec des épices authentiques et des techniques traditionnelles transmises de génération en génération.',
            'menu.noItemsDescription': 'Essayez d\'ajuster vos filtres ou termes de recherche pour trouver ce que vous cherchez.',
            'menu.resetFilters': 'Réinitialiser les filtres',
            'gallery.title': 'Notre Galerie',
            'gallery.culinaryArtistry': 'Art Culinaire',
            'gallery.visualGallery': 'Galerie Visuelle',
            'restaurantBar.title': 'restaurant Bar',
            'restaurantBar.subtitle': 'Boissons Premium & Cocktails',
            'restaurantBar.description': 'Découvrez notre expérience de bar sophistiquée avec des licors premium, des cócteles artisanaux et une vaste sélection de vins.',
            'restaurantBar.signature.title': 'Cocktails Signature',
            'restaurantBar.signature.description': 'Cocktails expertement élaborés avec des licors premium e ingredientes frais, inspirés en sabores indios et classiques internationaux.',
            'restaurantBar.wines.title': 'Collection de Vins',
            'restaurantBar.wines.description': 'Sélection soigneusement choisie de vins fins du monde entier, parfaitement maridados avec notre cuisine internationale diversifiée.',
            'restaurantBar.spirits.title': 'Licores Premium',
            'restaurantBar.spirits.description': 'Une vaste collection de whiskies, ginebras, vodkas premium et autres licors fins pour le palais averti.',
            'restaurantBar.features.expertBartenders': 'Barmans Experts',
            'restaurantBar.features.premiumSpirits': 'Licores Premium',
            'restaurantBar.features.customCocktails': 'Cócteles Personalizados',
            'restaurantBar.features.wineCollection': 'Collection de Vins',
            'restaurantBar.features.happyHour': 'Happy Hour Quotidien',
            'restaurantBar.features.liveMusic': 'Soirées Musique Live',
            'restaurantBar.cta': 'Visitez Notre Bar',
            'restaurantBar.hours': 'Heures du Bar: Dimanche-Vendredi 8h30 - 01h00, Samedi 10h30 - 01h00',
            // Textes hardcodés identifiés
            'booking.form.numberOfGuests': 'Nombre d\'invités',
            'booking.form.selectPartySize': 'Sélectionner la taille du groupe',
            'home.games.puzzleGames': 'Jeux de Puzzle',
            'home.games.puzzleDescription': 'Puzzles stimulants et jeux de réflexion',
            'home.games.arcadeClassics': 'Classiques d\'Arcade',
            'home.games.arcadeDescription': 'Jeux d\'arcade rétro et classiques',
            'home.games.strategyGames': 'Jeux de Stratégie',
            'home.games.strategyDescription': 'Jeux de réflexion stratégique et de planification',
            'home.games.quickGames': 'Jeux Rapides',
            'home.games.quickDescription': 'Mini-jeux rapides et défis',
            'home.games.cardGames': 'Jeux de Cartes',
            'home.games.cardDescription': 'Jeux de cartes numériques et classiques de casino',
            'home.games.difficulty': 'Difficulté',
            'home.games.players': 'Joueurs',
            'home.games.duration': 'Durée',
            'home.games.easy': 'Facile',
            'home.games.medium': 'Moyen',
            'home.games.hard': 'Difficile',
            'reservation.confirmed.thankYou': 'Merci de nous avoir choisis ! Votre réservation a été envoyée avec succès. Un email de confirmation sera envoyé à votre adresse email. Nous sommes ravis de vous accueillir pour une expérience culinaire indienne authentique.',
            'contact.fromCityCenter': 'Du centre-ville',
            'contact.parkingAvailable': 'Parking disponible',
            'contact.free': 'Gratuit',
            'contact.minutes': 'minutes',
            'contact.contactOurTeam': 'Contactez Notre Équipe',
            'contact.readyToExperience': 'Prêt à vivre une expérience culinaire et de divertissement ultime ? Nous sommes là pour vous aider à planifier votre visite parfaite chez restaurant.',
            'contact.contactDetails': 'Détails de Contact',
            'contact.howToReachUs': 'Comment Nous Contacter',
            'contact.helpWithReservations': 'Nous sommes là pour vous aider avec les réservations, les demandes de renseignements ou toute demande spéciale pour votre expérience culinaire.',
            'contact.visitRestaurant': 'Visitez Notre Restaurant',
            'contact.callUsDirectly': 'Appelez-nous Directement',
            'contact.sendUsEmail': 'Envoyez-nous un Email',
            'contact.openingHours': 'Heures d\'Ouverture',
            'contact.getDirections': 'Obtenir l\'Itinéraire',
            'contact.callNow': 'Appeler Maintenant',
            'contact.sendEmail': 'Envoyer un Email',
            'contact.bookTable': 'Réserver une Table',
            'contact.availableDuringBusiness': 'Disponible pendant les heures d\'ouverture',
            'contact.respondWithin24': 'Nous répondrons dans les 24 heures',
            'contact.monday': 'Lundi',
            'contact.tuesdayToSunday': 'Mardi - Dimanche',
            'contact.closed': 'Fermé',
            'contact.findUs': 'Nous Trouver',
            'contact.sendMessage': 'Envoyer un Message',
            'contact.getInTouchWithUs': 'Entrer en Contact avec Nous',
            'contact.haveQuestion': 'Vous avez une question, une demande spéciale ou souhaitez faire une réservation ? Nous aimerions avoir de vos nouvelles et vous aider à rendre votre expérience culinaire parfaite.',
            'contact.personalInformation': 'Informations Personnelles',
            'contact.contactInformation': 'Informations de Contact',
            'contact.messageDetails': 'Détails du Message',
            'contact.firstName': 'Prénom',
            'contact.lastName': 'Nom de Famille',
            'contact.emailAddress': 'Adresse Email',
            'contact.phoneNumber': 'Numéro de Téléphone',
            'contact.subject': 'Sujet',
            'contact.yourMessage': 'Votre Message',
            'contact.enterFirstName': 'Entrez votre prénom',
            'contact.enterLastName': 'Entrez votre nom de famille',
            'contact.emailPlaceholder': 'votre.email@exemple.com',
            'contact.phonePlaceholder': '+243 xxx xxx xxx',
            'contact.selectPurpose': 'Sélectionnez le but de votre message',
            'contact.messagePlaceholder': 'Parlez-nous de votre demande, exigences spéciales, dates préférées, nombre d\'invités ou tout autre détail qui nous aiderait à mieux vous assister...',
            'contact.respondWithin24Hours': 'Nous répondrons dans les 24 heures',
            'contact.readyToDine': 'Prêt à Dîner ?',
            'contact.experienceAuthentic': 'Découvrez la Cuisine Italienne Authentique',
            'contact.readyToEmbark': 'Prêt à vous lancer dans un voyage culinaire à travers les saveurs riches de l\'Italie ? Réservez votre table aujourd\'hui et laissez-nous créer une expérience culinaire inoubliable pour vous.',
            'contact.callNumber': 'Appeler +243 971 466 525',
            'contact.bookOnline': 'Réserver en Ligne',
            'contact.quickResponse': 'Réponse Rapide',
            'contact.confirmWithin2Hours': 'Nous confirmerons votre réservation dans les 2 heures',
            'contact.premiumExperience': 'Expérience Premium',
            'contact.diningEntertainment': 'Expérience de restauration et de divertissement 24/7',
            'contact.perfectLocation': 'Emplacement Parfait',
            'contact.easyToFind': 'Facile à trouver avec parking gratuit disponible',
            'contact.reservationInquiry': '🍽️ Demande de Réservation',
            'contact.generalInquiry': '💬 Demande Générale',
            'contact.feedbackReviews': '⭐ Commentaires et Avis',
            'contact.privateEventBooking': '🎉 Réservation d\'Événement Privé',
            'contact.cateringServices': '🚚 Services de Traiteur',
            'contact.dietaryRequirements': '🥗 Exigences Alimentaires',
            'contact.other': '📝 Autre',
            'reservation.bookYourTable': 'Réservez Votre Table',
            'reservation.reserveDiningExperience': 'Réservez Votre Expérience Culinaire',
            'reservation.secureYourTable': 'Réservez votre table dans notre restaurant primé et embarquez pour un voyage culinaire inoubliable à travers les saveurs authentiques de l\'Inde.',
            'reservation.instantConfirmation': 'Confirmation Instantanée',
            'reservation.awardWinning': 'Primé',
            'reservation.madeWithLove': 'Fait avec Amour',
            'reservation.personalInformation': 'Informations Personnelles',
            'reservation.reservationDetails': 'Détails de la Réservation',
            'reservation.specialRequests': 'Demandes Spéciales',
            'reservation.fullName': 'Nom Complet',
            'reservation.emailAddress': 'Adresse Email',
            'reservation.phoneNumber': 'Numéro de Téléphone',
            'reservation.preferredDate': 'Date Préférée',
            'reservation.preferredTime': 'Heure Préférée',
            'reservation.numberOfGuests': 'Nombre d\'Invités',
            'reservation.additionalInformation': 'Informations Supplémentaires (Optionnel)',
            'reservation.specialOccasionPlaceholder': 'Parlez-nous de toute occasion spéciale, exigences alimentaires, préférences de sièges ou autres demandes qui nous aideraient à rendre votre expérience parfaite...',
            'reservation.examples': '💡 Exemples : Célébration d\'anniversaire, accessibilité fauteuil roulant, menu végétarien, siège près de la fenêtre, etc.',
            'reservation.confirmWithin2Hours': 'Nous confirmerons votre réservation dans les 2 heures',
            'reservation.submitting': 'Envoi en cours...',
            'reservation.reserveMyTable': 'Réserver Ma Table',
            'reservation.restaurantInfo': 'Info Restaurant',
            'reservation.everythingYouNeed': 'Tout ce que vous devez savoir',
            'reservation.openingHours': 'Heures d\'Ouverture',
            'reservation.monday': 'Lundi',
            'reservation.tuesdayToSunday': 'Mardi - Dimanche',
            'reservation.closed': 'Fermé',
            'reservation.contactDetails': 'Détails de Contact',
            'reservation.callUsDirectly': 'Appelez-nous directement',
            'reservation.sendUsEmail': 'Envoyez-nous un email',
            'reservation.ourLocation': 'Notre Emplacement',
            'reservation.freeParkingAvailable': 'Parking gratuit disponible',
            'reservation.importantNotes': 'Notes Importantes',
            'reservation.reservationsHeld': 'Les réservations sont maintenues 15 minutes après l\'heure de réservation',
            'reservation.groupsAdvanceNotice': 'Les groupes de 8+ nécessitent un préavis',
            'reservation.dietaryRequirements': 'Les exigences alimentaires peuvent être accommodées',
            'reservation.confirmationWithin2Hours': 'Confirmation dans les 2 heures par email/téléphone',
            'reservation.specialOffers': 'Offres Spéciales',
            'reservation.limitedTimePromotions': 'Promotions à durée limitée',
            'reservation.anniversarySpecial': '🎉 Spécial Anniversaire',
            'reservation.complimentaryDessert': 'Dessert gratuit pour les célébrations',
            'reservation.winePairing': '🍷 Accord Mets et Vins',
            'reservation.expertSommelier': 'Recommandations d\'expert sommelier',
            'reservation.confirmed.confirmationId': 'ID de Confirmation',
            'reservation.confirmed.responseTime': 'Temps de Réponse',
            'reservation.confirmed.status': 'Statut',
            'reservation.confirmed.within2Hours': 'Dans les 2 heures',
            'reservation.confirmed.confirmed': 'Confirmé',
            'reservation.confirmed.emailSent': 'Un email de confirmation sera envoyé à votre adresse email. Notre équipe vous contactera dans les 2 heures pour confirmer les détails.',
            'reservation.confirmed.makeAnother': 'Faire une Autre Réservation',
            'reservation.confirmed.returnHome': 'Retour à l\'Accueil',
            'reservation.lunch': 'Déjeuner',
            'reservation.dinner': 'Dîner',
            'reservation.selectTimeSlot': 'Sélectionner un créneau horaire',
            'reservation.selectPartySize': 'Sélectionner la taille du groupe',
            'reservation.guest': 'Invité',
            'reservation.guests': 'Invités',
            'about.ourStory': 'Notre Histoire',
            'about.discoverOurJourney': 'Découvrez Notre Parcours',
            'about.awardWinner': 'Gagnant de Prix',
            'about.customers': 'Clients',
            'about.yearsExcellence': 'Années d\'Excellence',
            'about.ourBeginning': 'Notre Début',
            'about.culinaryJourney': 'Un Voyage Culinaire Né de la Passion',
            'about.uniqueEstablishment': 'restaurant est un établissement unique situé au cœur de Goma, offrant une expérience complète de restauration et de divertissement.',
            'about.storyBegins': 'Notre histoire commence avec un chef passionné qui a voyagé à travers l\'Inde, apprenant les techniques de cuisine traditionnelles auprès de chefs maîtres à Mumbai, Delhi et Kerala.',
            'about.todayWePride': 'Aujourd\'hui, nous sommes fiers de servir non seulement de la nourriture, mais une expérience qui vous transporte dans les rues animées et les cuisines aromatiques de l\'Inde.',
            'about.authenticRecipes': 'Recettes indiennes authentiques transmises de génération en génération',
            'about.freshSpices': 'Épices fraîches importées directement d\'Inde',
            'about.serviceAvailability': 'Service disponible 24h/24',
            'about.ourAchievements': 'Nos Réalisations',
            'about.numbersTellStory': 'Les Chiffres qui Raconte Notre Histoire',
            'about.milestonesRepresent': 'Ces jalons représentent notre engagement envers l\'excellence et la confiance que nos clients nous accordent.',
            'about.ourValues': 'Nos Valeurs',
            'about.whatDrivesUs': 'Ce qui Nous Pousse Vers l\'Avant',
            'about.coreValuesShape': 'Nos valeurs fondamentales façonnent chaque aspect de notre voyage culinaire, de l\'approvisionnement en ingrédients au service de nos invités.',
            'about.authenticity': 'Authenticité',
            'about.authenticityDescription': 'Nous honorons les méthodes de cuisine indienne traditionnelles et les recettes authentiques transmises de génération en génération.',
            'about.quality': 'Qualité',
            'about.qualityDescription': 'Chaque ingrédient est soigneusement sélectionné et chaque plat est préparé avec une attention méticuleuse aux détails.',
            'about.hospitality': 'Hospitalité',
            'about.hospitalityDescription': 'Nous croyons en traiter chaque invité comme de la famille, créant des expériences culinaires chaleureuses et mémorables.',
            'about.meetOurTeam': 'Rencontrez Notre Équipe',
            'about.passionatePeople': 'Les Personnes Passionnées Derrière Notre Succès',
            'about.meetTalentedIndividuals': 'Rencontrez les individus talentueux qui donnent vie à notre vision culinaire avec expertise, passion et dévouement.',
            'about.chefRajeshKumar': 'Chef Rajesh Kumar',
            'about.executiveChef': 'Chef Exécutif',
            'about.chefRajeshDescription': 'Avec plus de 15 ans d\'expérience, le Chef Rajesh apporte des saveurs indiennes authentiques à chaque plat.',
            'about.internationalCuisine': 'Cuisine Internationale et Bar',
            'about.chefPriyaSharma': 'Chef Priya Sharma',
            'about.sousChef': 'Sous-Chef',
            'about.chefPriyaDescription': 'Spécialisée dans la cuisine du Sud de l\'Inde, le Chef Priya s\'assure que chaque curry est parfaitement équilibré.',
            'about.southIndianCurries': 'Currys du Sud de l\'Inde',
            'about.amitPatel': 'Amit Patel',
            'about.restaurantManager': 'Gérant du Restaurant',
            'about.amitDescription': 'Amit s\'assure que chaque invité se sente bienvenu et profite d\'une expérience culinaire exceptionnelle.',
            'about.guestExperience': 'Expérience Client',
            'about.loadingOurStory': 'Chargement de notre histoire...',
            'join.stayConnected': 'Restez Connecté',
            'join.culinaryJourney': 'Rejoignez Notre Voyage Culinaire',
            'join.getExclusiveOffers': 'Obtenez des offres exclusives, des recettes et soyez les premiers à connaître nos événements spéciaux',
            'join.enterYourEmail': 'Entrez votre email',
            'join.subscribe': 'S\'abonner',
            'join.rating': '4.3★ Note',
            'join.awardWinner': 'Gagnant de Prix',
            'join.happyCustomers': '500+ Clients Heureux',
            'join.diningEntertainmentHub': 'Hub de Restauration et Divertissement',
            'join.followUs': 'Suivez-nous :',
            'gallery.loadingCategories': 'Chargement de la galerie...',
            'gallery.subtitle': 'Plongez-vous dans un voyage visuel à travers notre art culinaire, notre ambiance et nos moments inoubliables.',
            'gallery.photos': '50+ Photos',
            'gallery.highQuality': 'Haute Qualité',
            'gallery.curatedCollection': 'Collection Curée',
            'gallery.browseCollection': 'Parcourez Notre Collection',
            'gallery.photo': 'photo',
            'gallery.photosAvailable': 'photos disponibles',
            'gallery.allPhotos': 'Toutes les Photos',
            'gallery.loadingItems': 'Chargement de belles photos...',
            'gallery.viewFullSize': 'Voir en Pleine Taille',
            'gallery.gallery': 'Galerie',
            'gallery.noPhotosFound': 'Aucune Photo Trouvée',
            'gallery.noPhotos': 'Aucune photo n\'est disponible dans cette catégorie pour le moment.',
            'gallery.tryDifferentCategory': 'Essayez de sélectionner une catégorie différente ou revenez plus tard pour du nouveau contenu.',
            'gallery.viewAllPhotos': 'Voir Toutes les Photos',
            'gallery.fullResolution': 'Résolution Complète',
            'booking.personalInformation': 'Informations Personnelles',
            'booking.fullName': 'Nom Complet',
            'booking.fullNameRequired': 'Le nom complet est requis',
            'booking.enterFullName': 'Entrez votre nom complet',
            'booking.emailAddress': 'Adresse Email',
            'booking.emailRequired': 'L\'adresse email est requise',
            'booking.invalidEmail': 'Veuillez entrer une adresse email valide',
            'booking.emailPlaceholder': 'votre.email@exemple.com',
            'booking.phoneNumber': 'Numéro de Téléphone',
            'booking.phoneRequired': 'Le numéro de téléphone est requis',
            'booking.phonePlaceholder': '+243 xxx xxx xxx',
            'booking.reservationDetails': 'Détails de la Réservation',
            'booking.preferredDate': 'Date Préférée',
            'booking.dateRequired': 'La date est requise',
            'booking.preferredTime': 'Heure Préférée',
            'booking.timeRequired': 'L\'heure est requise',
            'booking.selectTimeSlot': 'Sélectionner un créneau horaire',
            'booking.lunch': 'Déjeuner',
            'booking.dinner': 'Dîner',
            'booking.additionalInformation': 'Informations Supplémentaires (Optionnel)',
            'booking.specialOccasionPlaceholder': 'Parlez-nous de toute occasion spéciale, exigences alimentaires, préférences de siège, ou autres demandes qui nous aideraient à rendre votre expérience parfaite...',
            'booking.examples': '💡 Exemples : Célébration d\'anniversaire, accessibilité fauteuil roulant, menu végétarien, siège près de la fenêtre, etc.',
            'booking.confirmWithin2Hours': 'Nous confirmerons votre réservation dans les 2 heures',
            'booking.submitting': 'Envoi en cours...',
            'booking.reserveTable': 'Réserver une Table',
            // Accessibility labels
            'aria.toggleTheme': 'Basculer le thème',
            'aria.toggleMenu': 'Basculer le menu',
            'aria.selectLanguage': 'Sélectionner la langue',
            'aria.previousSlide': 'Slide précédent',
            'aria.nextSlide': 'Slide suivant',
            'aria.goToSlide': 'Aller au slide {number}',
            'aria.resumeVideo': 'Reprendre la vidéo',
            'aria.pauseVideo': 'Mettre en pause',
            'aria.facebook': 'Facebook',
            'aria.instagram': 'Instagram',
            'aria.twitter': 'Twitter',
            // Alt text
            'alt.logo': 'Logo restaurant',
            'alt.restaurantInterior': 'Intérieur du restaurant',
        },
        es: {
            'nav.home': 'Inicio',
            'nav.menu': 'Nuestro Menú',
            'nav.about': 'Sobre Nosotros',
            'nav.gallery': 'Galería',
            'nav.contact': 'Contacto',
            'nav.reservations': 'Reservas',
            'hero.slide1.tag': 'Bienvenido',
            'hero.slide1.title': 'Gastronomía y Entretenimiento Último',
            'hero.slide1.subtitle': 'Experimenta cocina excepcional, bar premium y juegos emocionantes - todo bajo un mismo techo',
            'hero.slide1.cta': 'Reservar ahora',
            'hero.slide2.tag': 'Menú',
            'hero.slide2.title': 'Experiencia gastronómica elegante',
            'hero.slide2.subtitle': 'Ambiente sofisticado para momentos memorables',
            'hero.slide2.cta': 'Ver menú',
            'hero.slide3.tag': 'Experiencia',
            'hero.slide3.title': 'Especias tradicionales, técnicas modernas',
            'hero.slide3.subtitle': 'Donde la tradición se encuentra con la innovación en cada plato',
            'hero.slide3.cta': 'Explorar galería',
            'hero.bookNow': 'Reservar ahora',
            'booking.title': 'Reservar tu mesa',
            'booking.titlePerfect': 'Reserva Tu Mesa Perfecta',
            'booking.subtitle': 'Reserva tu lugar para una experiencia gastronómica inolvidable',
            'booking.quickReservation': 'Reserva Rápida',
            'booking.reservationForm': 'Formulario de Reserva',
            'booking.description': 'Completa el formulario a continuación y confirmaremos tu reserva en 2 horas. ¡No podemos esperar a darte la bienvenida!',
            'booking.success': '¡Reserva enviada con éxito!',
            'booking.error': 'Error al enviar la reserva',
            'booking.form.name': 'Nombre completo',
            'booking.form.namePlaceholder': 'Ingresa tu nombre completo',
            'booking.form.nameRequired': 'El nombre es requerido',
            'booking.form.email': 'Correo electrónico',
            'booking.form.emailPlaceholder': 'Ingresa tu correo electrónico',
            'booking.form.emailRequired': 'El correo electrónico es requerido',
            'booking.form.emailInvalid': 'Dirección de correo electrónico inválida',
            'booking.form.phone': 'Número de teléfono',
            'booking.form.phonePlaceholder': 'Ingresa tu número de teléfono',
            'booking.form.phoneRequired': 'El número de teléfono es requerido',
            'booking.form.date': 'Fecha',
            'booking.form.dateRequired': 'La fecha es requerida',
            'booking.form.time': 'Hora',
            'booking.form.timeSelect': 'Seleccionar hora',
            'booking.form.timeRequired': 'La hora es requerida',
            'booking.form.guests': 'Número de invitados',
            'booking.form.guest': 'Invitado',
            'booking.form.guestsSelect': 'Seleccionar número de invitados',
            'booking.form.guestsRequired': 'El número de invitados es requerido',
            'booking.form.guestsMin': 'Mínimo 1 invitado',
            'booking.form.guestsMax': 'Máximo 20 invitados',
            'booking.form.specialRequests': 'Peticiones especiales',
            'booking.form.specialRequestsPlaceholder': 'Cualquier petición especial o requisito dietético...',
            'booking.form.submit': 'Reservar mesa',
            'booking.form.submitting': 'Enviando...',
            'booking.form.orCall': 'O llámanos directamente:',
            'booking.form.confirmationTime': 'Confirmaremos tu reserva en 2 horas',
            'highlights.title': 'Nuestros',
            'highlights.subtitle': 'Destacados',
            'highlights.description': 'Descubre qué hace de restaurant el destino principal para gastronomía, entretenimiento y experiencias inolvidables en Goma.',
            'highlights.tandoori.title': 'Todo lo que Puedas Comer',
            'highlights.tandoori.description': 'Disfruta de comida ilimitada con nuestro excepcional buffet con cocina diversa y preparaciones frescas durante todo el día.',
            'highlights.curries.title': 'Descuentos "happy hour" en restaurante',
            'highlights.curries.description': 'Aprovecha nuestras ofertas especiales y descuentos atractivos en nuestros platos signature durante las horas pico. Una oportunidad perfecta para descubrir nuestra cocina a precios reducidos.',
            'highlights.ambiance.title': 'El Ambiente',
            'highlights.ambiance.description': 'Sumérgete en nuestra atmósfera sofisticada pero cálida, perfecta para cenas íntimas y celebraciones especiales.',
            'testimonials.title': 'Lo que dicen',
            'testimonials.subtitle': 'nuestros clientes',
            'testimonials.description': 'No solo tomes nuestra palabra. Aquí está lo que nuestros valiosos clientes tienen que decir sobre su experiencia gastronómica.',
            'testimonials.featured': 'Destacado',
            'testimonials.overall.title': 'Calificación general',
            'testimonials.overall.description': 'Basado en {count}+ reseñas de clientes',
            'footer.description': 'Experimenta la máxima gastronomía y entretenimiento en restaurant en el corazón de Goma. Con horarios extendidos, ofrecemos cocina excepcional, servicio de bar premium y experiencias de juego emocionantes bajo un mismo techo.',
            'footer.quickLinks': 'Enlaces rápidos',
            'footer.contactInfo': 'Información de contacto',
            'footer.openingHours': 'Horario de apertura',
            'footer.days.monday': 'Lunes',
            'footer.days.tuesday': 'Martes',
            'footer.days.wednesday': 'Miércoles',
            'footer.days.thursday': 'Jueves',
            'footer.days.friday': 'Viernes',
            'footer.days.saturday': 'Sábado',
            'footer.days.sunday': 'Domingo',
            'footer.closed': 'Cerrado',
            'footer.hours': '8:00 AM - 1:00 AM',
            'footer.hours.saturday': '10:30 AM - 1:00 AM',
            'footer.copyright': 'Todos los derechos reservados.',
            'home.welcome': 'Bienvenido a',
            'home.description': 'Experimenta la máxima gastronomía y entretenimiento en restaurant en el corazón de Goma. Con horarios extendidos, ofrecemos cocina excepcional, servicio de bar premium, juegos emocionantes y experiencias de buffet ilimitado.',
            'home.rating.title': 'Calificación de 4.3 estrellas',
            'home.rating.description': 'Consistentemente calificado entre los mejores en Kinshasa',
            'stats.starRating': 'Calificación Estrellas',
            'stats.happyCustomers': 'Clientes Felices',
            'stats.signatureDishes': 'Platos Signature',
            'stats.yearsExcellence': 'Años de Excelencia',
            'about.title': 'Sobre Nosotros',
            'about.subtitle': 'Descubre la historia detrás de restaurant y nuestra pasión por experiencias excepcionales de gastronomía y entretenimiento.',
            'about.story.title': 'Nuestra Historia',
            'about.values.title': 'Nuestros Valores',
            'about.established': 'Establecido',
            'about.values.subtitle': 'Los principios que guían todo lo que hacemos en restaurant.',
            'about.values.authenticity.title': 'Autenticidad',
            'about.values.authenticity.description': 'Nos esforzamos por la excelencia en todos los aspectos: desde nuestra cocina y servicio de bar hasta el juego y la hospitalidad.',
            'about.values.quality.title': 'Calidad',
            'about.values.quality.description': 'Usamos solo los mejores ingredientes y mantenemos los más altos estándares en la preparación de alimentos.',
            'about.values.hospitality.title': 'Hospitalidad',
            'about.values.hospitality.description': 'Creemos en crear experiencias cálidas y acogedoras que hagan que cada invitado se sienta especial.',
            'about.team.title': 'Conoce nuestro equipo',
            'about.team.subtitle': 'Nuestro equipo apasionado dedicado a brindarte la mejor experiencia gastronómica y de entretenimiento.',
            'about.team.chef.name': 'Chef Rajesh Kumar',
            'about.team.chef.role': 'Chef Principal',
            'about.team.chef.description': 'Con más de 12 años de experiencia en cocina internacional, la Chef Marie crea platos excepcionales para nuestro menú diverso.',
            'about.team.sousChef.name': 'Priya Sharma',
            'about.team.sousChef.role': 'Chef Adjunto',
            'about.team.sousChef.description': 'James supervisa nuestras operaciones de bar premium, creando cócteles signature y gestionando nuestra extensa colección de licores.',
            'about.team.manager.name': 'Amit Patel',
            'about.team.manager.role': 'Gerente del Restaurante',
            'about.team.manager.description': 'Amit asegura que cada invitado reciba un servicio excepcional y una experiencia gastronómica memorable.',
            'about.story.content': 'restaurant es un establecimiento único ubicado en el corazón de Goma, ofreciendo una experiencia completa de gastronomía y entretenimiento. Conocido por sus diversos servicios de bar, restaurante y juegos, restaurant es un lugar de encuentro dinámico para cualquier ocasión.<br/><br/>Con horarios de operación 24/7, proporcionamos servicio continuo para satisfacer todas tus necesidades, día y noche. Nuestra misión es simple: crear un destino excepcional donde la gran comida, bebidas premium y entretenimiento emocionante se unen bajo un mismo techo.<br/><br/>Nos especializamos en experiencias de todo lo que puedas comer y ofertas de happy hour, asegurando que cada visita ofrezca gran valor y satisfacción. Desde desayuno temprano hasta cena tardía, desde reuniones de negocios hasta celebraciones, restaurant se adapta a tu estilo de vida.<br/><br/>Nos enorgullece nuestro compromiso con la excelencia, disponibilidad 24/7 y crear experiencias inolvidables. Nuestro equipo dedicado trabaja las 24 horas para asegurar que cada huésped disfrute de un servicio excepcional y momentos memorables.',
            'contact.title': 'Contáctanos',
            'contact.subtitle': 'Ponte en contacto con nosotros para reservas, consultas o simplemente para saludar.',
            'contact.getInTouch': 'Ponte en Contacto',
            'contact.address': 'Dirección',
            'contact.phone': 'Teléfono',
            'contact.email': 'Correo electrónico',
            'contact.hours': 'Horario de apertura',
            'contact.hours.description': 'Domingo-Viernes: 8:30 AM - 1:00 AM, Sábado: 10:30 AM - 1:00 AM',
            'contact.form.title': 'Envíanos un mensaje',
            'contact.form.name': 'Nombre completo',
            'contact.form.email': 'Dirección de correo electrónico',
            'contact.form.subject': 'Asunto',
            'contact.form.message': 'Mensaje',
            'contact.form.submit': 'Enviar mensaje',
            'contact.form.submitting': 'Enviando...',
            'reservation.title': 'Reserva tu mesa',
            'reservation.subtitle': 'Reserva tu lugar para una experiencia gastronómica inolvidable',
            'reservation.confirmed.title': '¡Reserva enviada!',
            'reservation.confirmed.subtitle': 'Gracias por elegir restaurant. Tu reserva ha sido enviada exitosamente. Se enviará un email de confirmación a tu dirección de correo electrónico.',
            'reservation.confirmed.details': 'Detalles de la reserva',
            'reservation.confirmed.message': 'Te contactaremos pronto para confirmar tu reserva',
            'reservation.confirmed.another': 'Hacer otra reserva',
            'reservation.form.name': 'Nombre completo',
            'reservation.form.namePlaceholder': 'Ingresa tu nombre completo',
            'reservation.form.email': 'Dirección de correo electrónico',
            'reservation.form.emailPlaceholder': 'Ingresa tu dirección de correo electrónico',
            'reservation.form.phone': 'Número de teléfono',
            'reservation.form.phonePlaceholder': 'Ingresa tu número de teléfono',
            'reservation.form.date': 'Fecha',
            'reservation.form.time': 'Hora',
            'reservation.form.guests': 'Número de invitados',
            'reservation.form.message': 'Peticiones especiales',
            'reservation.form.messagePlaceholder': 'Cualquier petición especial o requisito dietético...',
            'reservation.form.submit': 'Reservar mesa',
            'reservation.form.submitting': 'Enviando...',
            'reservation.info.title': 'Información de reserva',
            'reservation.info.openingHours': 'Horario de apertura',
            'reservation.info.monday': 'Lunes',
            'reservation.info.closed': 'Cerrado',
            'reservation.info.tuesdaySunday': 'Domingo - Viernes',
            'reservation.info.hours': '8:00 AM - 1:00 AM',
            'reservation.info.contact': 'Contacto',
            'reservation.info.location': 'Ubicación',
            'reservation.info.importantNotes': 'Notas importantes',
            'reservation.info.notes.held': 'Las reservas se mantienen por 15 minutos',
            'reservation.info.notes.groups': 'Los grupos grandes (8+ invitados) requieren 24h de aviso',
            'reservation.info.notes.dietary': 'Se pueden acomodar requisitos dietéticos especiales',
            'reservation.info.notes.confirmation': 'La confirmación se enviará por email/SMS',
            'home.hours.title': 'Horarios Extendidos',
            'home.hours.description': 'Domingo-Viernes: 8:30 AM - 1:00 AM, Sábado: 10:30 AM - 1:00 AM',
            'home.location.title': 'Ubicación privilegiada',
            'home.location.description': 'Modern Paradise 8225, Av. Kabasele Tshamala',
            'home.cta.title': '¿Listo para experimentar la auténtica cocina italiana?',
            'home.cta.description': 'Reserva tu mesa hoy y disfruta de nuestra excepcional cocina italiana, bar premium y ofertas happy hour.',
            'home.cta.readyToDine': '¿Listo para cenar?',
            'home.cta.callNow': 'Llamar ahora',
            'home.features.extendedHours.title': 'Horarios Extendidos',
            'home.features.extendedHours.description': 'Abierto hasta tarde todos los días para su conveniencia',
            'home.features.primeLocation.title': 'Ubicación Privilegiada',
            'home.features.primeLocation.description': 'Estacionamiento fácil y accesibilidad',
            'home.features.allYouCanEat.title': 'Menú Infantil',
            'home.features.allYouCanEat.description': 'Platos especiales para niños para las familias',
            'highlights.stats.awardWinner': 'Ganador de Premios',
            'highlights.stats.customers': '500+ Clientes',
            'highlights.stats.experience': '5+ Años',
            'testimonials.stats.averageRating': '4.3★ Promedio',
            'testimonials.stats.totalReviews': '500+ Reseñas',
            'testimonials.stats.satisfiedCustomers': '95% Satisfechos',
            'testimonials.stats.totalReviewsLabel': 'Total de Reseñas',
            'testimonials.stats.fiveStarReviews': 'Reseñas 5 Estrellas',
            'testimonials.stats.recommendUs': 'Nos Recomiendan',
            'gallery.discoverMore': 'Descubre más fotos impresionantes de nuestro restaurante',
            'home.badges.entertainmentHub': 'Hub de Entretenimiento',
            'home.badges.healthCertified': 'Certificado de Salud',
            'home.badges.rating': '4.8★ Calificación',
            'highlights.signatureExperience': 'Nuestra Experiencia Signature',
            'home.premiumExperience': 'Experiencia Premium',
            'home.premiumBarExperience': 'Experiencia de Bar Premium',
            'testimonials.customerStories': 'Historias de Amor de Clientes',
            'testimonials.overallRating': 'Calificación General',
            'footer.privacyPolicy': 'Política de Privacidad',
            'footer.termsOfService': 'Términos de Servicio',
            'footer.cookiePolicy': 'Política de Cookies',
            'gallery.viewFullGallery': 'Ver Galería Completa',
            // Hero section specific translations
            'hero.cta.reservation': 'Reservar una mesa',
            'hero.cta.menu': 'Ver menú',
            'loading': 'Cargando...',
            'error.title': '¡Oops!',
            'error.hero': 'No se puede cargar el contenido',
            'status.live': 'API en vivo',
            'status.demo': 'Demo de respaldo',
            'menu.title': 'Nuestro Menú',
            'menu.subtitle': 'Descubre nuestra selección cuidadosamente elaborada de platos internacionales, preparados con los mejores ingredientes y técnicas culinarias modernas.',
            'menu.allDishes': 'Todos los platos',
            'menu.loading': 'Cargando menú...',
            'menu.loadingCategories': 'Cargando categorías...',
            'menu.loadingItems': 'Cargando platos...',
            'menu.noItems': 'No se encontraron platos en esta categoría.',
            'menu.viewDetails': 'Ver detalles',
            'menu.addToOrder': 'Agregar al pedido',
            'menu.close': 'Cerrar',
            'menu.vegetarian': 'Vegetariano',
            'menu.spicy': 'Picante',
            'menu.searchPlaceholder': 'Buscar un plato...',
            'menu.filters': 'Filtros',
            'menu.dietary': 'Dieta',
            'menu.priceRange': 'Rango de precios',
            'menu.allPrices': 'Todos los precios',
            'menu.under15': 'Menos de $15',
            'menu.15to20': '$15 - $20',
            'menu.over20': 'Más de $20',
            'menu.reset': 'Restablecer',
            'menu.exploreCategories': 'Explora nuestras categorías',
            'menu.dishes': 'Platos',
            'menu.chefSelection': 'Selección del Chef',
            'menu.internationalCuisine': 'Cocina Internacional',
            'menu.culinaryDelights': 'Delicias Culinarias',
            'menu.curriesDescription': 'Desde currys aromáticos hasta naans recién horneados, cada plato está elaborado con especias auténticas y técnicas tradicionales transmitidas de generación en generación.',
            'menu.noItemsDescription': 'Intenta ajustar tus filtros o términos de búsqueda para encontrar lo que buscas.',
            'menu.resetFilters': 'Restablecer filtros',
            'gallery.title': 'Nuestra Galería',
            'gallery.culinaryArtistry': 'Arte Culinario',
            'gallery.visualGallery': 'Galería Visual',
            'restaurantBar.title': 'restaurant Bar',
            'restaurantBar.subtitle': 'Bebidas Premium & Cócteles',
            'restaurantBar.description': 'Descubre nuestra sofisticada experiencia de bar con licores premium, cócteles artesanales y una extensa selección de vinos.',
            'restaurantBar.signature.title': 'Cócteles Signature',
            'restaurantBar.signature.description': 'Cócteles expertamente elaborados con licores premium e ingredientes frescos, inspirados en sabores indios y clásicos internacionales.',
            'restaurantBar.wines.title': 'Colección de Vinos',
            'restaurantBar.wines.description': 'Selección cuidadosa de vinos finos de todo el mundo, perfectamente maridados con nuestra cocina internacional diversa.',
            'restaurantBar.spirits.title': 'Licores Premium',
            'restaurantBar.spirits.description': 'Una extensa colección de whiskies, ginebras, vodkas premium y otros licores finos para el paladar exigente.',
            'restaurantBar.features.expertBartenders': 'Cantineros Expertos',
            'restaurantBar.features.premiumSpirits': 'Licores Premium',
            'restaurantBar.features.customCocktails': 'Cócteles Personalizados',
            'restaurantBar.features.wineCollection': 'Colección de Vinos',
            'restaurantBar.features.happyHour': 'Happy Hour Diario',
            'restaurantBar.features.liveMusic': 'Noches de Música en Vivo',
            'restaurantBar.cta': 'Visita Nuestro Bar',
            'restaurantBar.hours': 'Horario del Bar: Domingo-Viernes 8:30 AM - 1:00 AM, Sábado 10:30 AM - 1:00 AM',
            // Textes hardcodés identifiés
            'booking.form.numberOfGuests': 'Número de invitados',
            'booking.form.selectPartySize': 'Seleccionar tamaño del grupo',
            'home.games.puzzleGames': 'Juegos de Puzzle',
            'home.games.puzzleDescription': 'Puzzles desafiantes y juegos mentales',
            'home.games.arcadeClassics': 'Clásicos de Arcade',
            'home.games.arcadeDescription': 'Juegos de arcade retro y clásicos',
            'home.games.strategyGames': 'Juegos de Estrategia',
            'home.games.strategyDescription': 'Juegos de pensamiento estratégico y planificación',
            'home.games.quickGames': 'Juegos Rápidos',
            'home.games.quickDescription': 'Mini-juegos rápidos y desafíos',
            'home.games.cardGames': 'Juegos de Cartas',
            'home.games.cardDescription': 'Juegos de cartas digitales y clásicos de casino',
            'home.games.difficulty': 'Dificultad',
            'home.games.players': 'Jugadores',
            'home.games.duration': 'Duración',
            'home.games.easy': 'Fácil',
            'home.games.medium': 'Medio',
            'home.games.hard': 'Difícil',
            'reservation.confirmed.thankYou': '¡Gracias por elegirnos! Tu reserva ha sido enviada exitosamente. Se enviará un email de confirmación a tu dirección de correo electrónico. Estamos emocionados de darte la bienvenida para una experiencia culinaria india auténtica.',
            'contact.fromCityCenter': 'Del centro de la ciudad',
            'contact.parkingAvailable': 'Estacionamiento disponible',
            'contact.free': 'Gratis',
            'contact.minutes': 'minutos',
            'contact.contactOurTeam': 'Contacta Nuestro Equipo',
            'contact.readyToExperience': '¿Listo para experimentar la cena y entretenimiento definitivos? Estamos aquí para ayudarte a planificar tu visita perfecta a restaurant.',
            'contact.contactDetails': 'Detalles de Contacto',
            'contact.howToReachUs': 'Cómo Contactarnos',
            'contact.helpWithReservations': 'Estamos aquí para ayudarte con reservas, consultas o cualquier solicitud especial para tu experiencia culinaria.',
            'contact.visitRestaurant': 'Visita Nuestro Restaurante',
            'contact.callUsDirectly': 'Llámanos Directamente',
            'contact.sendUsEmail': 'Envíanos un Email',
            'contact.openingHours': 'Horarios de Apertura',
            'contact.getDirections': 'Obtener Direcciones',
            'contact.callNow': 'Llamar Ahora',
            'contact.sendEmail': 'Enviar Email',
            'contact.bookTable': 'Reservar Mesa',
            'contact.availableDuringBusiness': 'Disponible durante horario comercial',
            'contact.respondWithin24': 'Responderemos dentro de 24 horas',
            'contact.monday': 'Lunes',
            'contact.tuesdayToSunday': 'Martes - Domingo',
            'contact.closed': 'Cerrado',
            'contact.findUs': 'Encuéntranos',
            'contact.sendMessage': 'Enviar Mensaje',
            'contact.getInTouchWithUs': 'Ponte en Contacto con Nosotros',
            'contact.haveQuestion': '¿Tienes una pregunta, solicitud especial o quieres hacer una reserva? Nos encantaría saber de ti y ayudarte a hacer perfecta tu experiencia culinaria.',
            'contact.personalInformation': 'Información Personal',
            'contact.contactInformation': 'Información de Contacto',
            'contact.messageDetails': 'Detalles del Mensaje',
            'contact.firstName': 'Nombre',
            'contact.lastName': 'Apellido',
            'contact.emailAddress': 'Dirección de Email',
            'contact.phoneNumber': 'Número de Teléfono',
            'contact.subject': 'Asunto',
            'contact.yourMessage': 'Tu Mensaje',
            'contact.enterFirstName': 'Ingresa tu nombre',
            'contact.enterLastName': 'Ingresa tu apellido',
            'contact.emailPlaceholder': 'tu.email@ejemplo.com',
            'contact.phonePlaceholder': '+243 xxx xxx xxx',
            'contact.selectPurpose': 'Selecciona el propósito de tu mensaje',
            'contact.messagePlaceholder': 'Cuéntanos sobre tu consulta, requisitos especiales, fechas preferidas, número de invitados o cualquier otro detalle que nos ayude a asistirte mejor...',
            'contact.respondWithin24Hours': 'Responderemos dentro de 24 horas',
            'contact.readyToDine': '¿Listo para Cenar?',
            'contact.experienceAuthentic': 'Experimenta la Cocina Italiana Auténtica',
            'contact.readyToEmbark': '¿Listo para embarcarte en un viaje culinario a través de los ricos sabores de Italia? Reserva tu mesa hoy y déjanos crear una experiencia culinaria inolvidable para ti.',
            'contact.callNumber': 'Llamar +243 971 466 525',
            'contact.bookOnline': 'Reservar en Línea',
            'contact.quickResponse': 'Respuesta Rápida',
            'contact.confirmWithin2Hours': 'Confirmaremos tu reserva dentro de 2 horas',
            'contact.premiumExperience': 'Experiencia Premium',
            'contact.diningEntertainment': 'Experiencia de cena y entretenimiento 24/7',
            'contact.perfectLocation': 'Ubicación Perfecta',
            'contact.easyToFind': 'Fácil de encontrar con estacionamiento gratuito disponible',
            'contact.reservationInquiry': '🍽️ Consulta de Reserva',
            'contact.generalInquiry': '💬 Consulta General',
            'contact.feedbackReviews': '⭐ Comentarios y Reseñas',
            'contact.privateEventBooking': '🎉 Reserva de Evento Privado',
            'contact.cateringServices': '🚚 Servicios de Catering',
            'contact.dietaryRequirements': '🥗 Requisitos Dietéticos',
            'contact.other': '📝 Otro',
            'reservation.bookYourTable': 'Reserva Tu Mesa',
            'reservation.reserveDiningExperience': 'Reserva Tu Experiencia Culinaria',
            'reservation.secureYourTable': 'Asegura tu mesa en nuestro restaurante galardonado y embárcate en un viaje culinario inolvidable a través de sabores auténticos de la India.',
            'reservation.instantConfirmation': 'Confirmación Instantánea',
            'reservation.awardWinning': 'Galardonado',
            'reservation.madeWithLove': 'Hecho con Amor',
            'reservation.personalInformation': 'Información Personal',
            'reservation.reservationDetails': 'Detalles de la Reserva',
            'reservation.specialRequests': 'Solicitudes Especiales',
            'reservation.fullName': 'Nombre Completo',
            'reservation.emailAddress': 'Dirección de Email',
            'reservation.phoneNumber': 'Número de Teléfono',
            'reservation.preferredDate': 'Fecha Preferida',
            'reservation.preferredTime': 'Hora Preferida',
            'reservation.numberOfGuests': 'Número de Invitados',
            'reservation.additionalInformation': 'Información Adicional (Opcional)',
            'reservation.specialOccasionPlaceholder': 'Cuéntanos sobre cualquier ocasión especial, requisitos dietéticos, preferencias de asientos u otras solicitudes que nos ayuden a hacer tu experiencia perfecta...',
            'reservation.examples': '💡 Ejemplos: Celebración de aniversario, accesibilidad en silla de ruedas, menú vegetariano, asiento junto a la ventana, etc.',
            'reservation.confirmWithin2Hours': 'Confirmaremos tu reserva dentro de 2 horas',
            'reservation.submitting': 'Enviando...',
            'reservation.reserveMyTable': 'Reservar Mi Mesa',
            'reservation.restaurantInfo': 'Info del Restaurante',
            'reservation.everythingYouNeed': 'Todo lo que necesitas saber',
            'reservation.openingHours': 'Horarios de Apertura',
            'reservation.monday': 'Lunes',
            'reservation.tuesdayToSunday': 'Martes - Domingo',
            'reservation.closed': 'Cerrado',
            'reservation.contactDetails': 'Detalles de Contacto',
            'reservation.callUsDirectly': 'Llámanos directamente',
            'reservation.sendUsEmail': 'Envíanos un email',
            'reservation.ourLocation': 'Nuestra Ubicación',
            'reservation.freeParkingAvailable': 'Estacionamiento gratuito disponible',
            'reservation.importantNotes': 'Notas Importantes',
            'reservation.reservationsHeld': 'Las reservas se mantienen 15 minutos después de la hora de reserva',
            'reservation.groupsAdvanceNotice': 'Los grupos de 8+ requieren aviso previo',
            'reservation.dietaryRequirements': 'Los requisitos dietéticos pueden ser acomodados',
            'reservation.confirmationWithin2Hours': 'Confirmación dentro de 2 horas por email/teléfono',
            'reservation.specialOffers': 'Ofertas Especiales',
            'reservation.limitedTimePromotions': 'Promociones por tiempo limitado',
            'reservation.anniversarySpecial': '🎉 Especial de Aniversario',
            'reservation.complimentaryDessert': 'Postre gratuito para celebraciones',
            'reservation.winePairing': '🍷 Maridaje de Vinos',
            'reservation.expertSommelier': 'Recomendaciones de sommelier experto',
            'reservation.confirmed.confirmationId': 'ID de Confirmación',
            'reservation.confirmed.responseTime': 'Tiempo de Respuesta',
            'reservation.confirmed.status': 'Estado',
            'reservation.confirmed.within2Hours': 'Dentro de 2 horas',
            'reservation.confirmed.confirmed': 'Confirmado',
            'reservation.confirmed.emailSent': 'Se enviará un email de confirmación a tu dirección de email. Nuestro equipo te contactará dentro de 2 horas para confirmar los detalles.',
            'reservation.confirmed.makeAnother': 'Hacer Otra Reserva',
            'reservation.confirmed.returnHome': 'Volver al Inicio',
            'reservation.lunch': 'Almuerzo',
            'reservation.dinner': 'Cena',
            'reservation.selectTimeSlot': 'Seleccionar horario',
            'reservation.selectPartySize': 'Seleccionar tamaño del grupo',
            'reservation.guest': 'Invitado',
            'reservation.guests': 'Invitados',
            'about.ourStory': 'Nuestra Historia',
            'about.discoverOurJourney': 'Descubre Nuestro Viaje',
            'about.awardWinner': 'Ganador de Premios',
            'about.customers': 'Clientes',
            'about.yearsExcellence': 'Años de Excelencia',
            'about.ourBeginning': 'Nuestro Comienzo',
            'about.culinaryJourney': 'Un Viaje Culinario Nacido de la Pasión',
            'about.uniqueEstablishment': 'restaurant es un establecimiento único ubicado en el corazón de Goma, ofreciendo una experiencia completa de comida y entretenimiento.',
            'about.storyBegins': 'Nuestra historia comienza con un chef apasionado que viajó por toda la India, aprendiendo técnicas de cocina tradicionales de chefs maestros en Mumbai, Delhi y Kerala.',
            'about.todayWePride': 'Hoy, nos enorgullecemos de servir no solo comida, sino una experiencia que te transporta a las calles vibrantes y cocinas aromáticas de la India.',
            'about.authenticRecipes': 'Recetas indias auténticas transmitidas de generación en generación',
            'about.freshSpices': 'Especias frescas importadas directamente de la India',
            'about.serviceAvailability': 'Servicio disponible 24/7',
            'about.ourAchievements': 'Nuestros Logros',
            'about.numbersTellStory': 'Números que Cuentan Nuestra Historia',
            'about.milestonesRepresent': 'Estos hitos representan nuestro compromiso con la excelencia y la confianza que nuestros clientes depositan en nosotros.',
            'about.ourValues': 'Nuestros Valores',
            'about.whatDrivesUs': 'Lo que Nos Impulsa Hacia Adelante',
            'about.coreValuesShape': 'Nuestros valores fundamentales dan forma a cada aspecto de nuestro viaje culinario, desde el abastecimiento de ingredientes hasta servir a nuestros huéspedes.',
            'about.authenticity': 'Autenticidad',
            'about.authenticityDescription': 'Honramos los métodos de cocina india tradicionales y recetas auténticas transmitidas de generación en generación.',
            'about.quality': 'Calidad',
            'about.qualityDescription': 'Cada ingrediente es cuidadosamente seleccionado y cada plato se prepara con atención meticulosa al detalle.',
            'about.hospitality': 'Hospitalidad',
            'about.hospitalityDescription': 'Creemos en tratar a cada huésped como familia, creando experiencias culinarias cálidas y memorables.',
            'about.meetOurTeam': 'Conoce Nuestro Equipo',
            'about.passionatePeople': 'Las Personas Apasionadas Detrás de Nuestro Éxito',
            'about.meetTalentedIndividuals': 'Conoce a los individuos talentosos que dan vida a nuestra visión culinaria con experiencia, pasión y dedicación.',
            'about.chefRajeshKumar': 'Chef Rajesh Kumar',
            'about.executiveChef': 'Chef Ejecutivo',
            'about.chefRajeshDescription': 'Con más de 15 años de experiencia, el Chef Rajesh aporta sabores indios auténticos a cada plato.',
            'about.internationalCuisine': 'Cocina Internacional y Bar',
            'about.chefPriyaSharma': 'Chef Priya Sharma',
            'about.sousChef': 'Sous Chef',
            'about.chefPriyaDescription': 'Especializada en cocina del sur de la India, la Chef Priya asegura que cada curry esté perfectamente balanceado.',
            'about.southIndianCurries': 'Currys del Sur de la India',
            'about.amitPatel': 'Amit Patel',
            'about.restaurantManager': 'Gerente del Restaurante',
            'about.amitDescription': 'Amit asegura que cada huésped se sienta bienvenido y disfrute de una experiencia culinaria excepcional.',
            'about.guestExperience': 'Experiencia del Huésped',
            'about.loadingOurStory': 'Cargando nuestra historia...',
            'join.stayConnected': 'Mantente Conectado',
            'join.culinaryJourney': 'Únete a Nuestro Viaje Culinario',
            'join.getExclusiveOffers': 'Obtén ofertas exclusivas, recetas y sé el primero en conocer nuestros eventos especiales',
            'join.enterYourEmail': 'Ingresa tu email',
            'join.subscribe': 'Suscribirse',
            'join.rating': '4.3★ Calificación',
            'join.awardWinner': 'Ganador de Premios',
            'join.happyCustomers': '500+ Clientes Felices',
            'join.diningEntertainmentHub': 'Hub de Comida y Entretenimiento',
            'join.followUs': 'Síguenos:',
            'gallery.loadingCategories': 'Cargando galería...',
            'gallery.subtitle': 'Sumérgete en un viaje visual a través de nuestro arte culinario, ambiente y momentos inolvidables.',
            'gallery.photos': '50+ Fotos',
            'gallery.highQuality': 'Alta Calidad',
            'gallery.curatedCollection': 'Colección Curada',
            'gallery.browseCollection': 'Explora Nuestra Colección',
            'gallery.photo': 'foto',
            'gallery.photosAvailable': 'fotos disponibles',
            'gallery.allPhotos': 'Todas las Fotos',
            'gallery.loadingItems': 'Cargando hermosas fotos...',
            'gallery.viewFullSize': 'Ver Tamaño Completo',
            'gallery.gallery': 'Galería',
            'gallery.noPhotosFound': 'No se Encontraron Fotos',
            'gallery.noPhotos': 'No hay fotos disponibles en esta categoría en este momento.',
            'gallery.tryDifferentCategory': 'Intenta seleccionar una categoría diferente o vuelve más tarde para nuevo contenido.',
            'gallery.viewAllPhotos': 'Ver Todas las Fotos',
            'gallery.fullResolution': 'Resolución Completa',
            'booking.personalInformation': 'Información Personal',
            'booking.fullName': 'Nombre Completo',
            'booking.fullNameRequired': 'El nombre completo es requerido',
            'booking.enterFullName': 'Ingresa tu nombre completo',
            'booking.emailAddress': 'Dirección de Email',
            'booking.emailRequired': 'La dirección de email es requerida',
            'booking.invalidEmail': 'Por favor ingresa una dirección de email válida',
            'booking.emailPlaceholder': 'tu.email@ejemplo.com',
            'booking.phoneNumber': 'Número de Teléfono',
            'booking.phoneRequired': 'El número de teléfono es requerido',
            'booking.phonePlaceholder': '+243 xxx xxx xxx',
            'booking.reservationDetails': 'Detalles de la Reserva',
            'booking.preferredDate': 'Fecha Preferida',
            'booking.dateRequired': 'La fecha es requerida',
            'booking.preferredTime': 'Hora Preferida',
            'booking.timeRequired': 'La hora es requerida',
            'booking.selectTimeSlot': 'Seleccionar horario',
            'booking.lunch': 'Almuerzo',
            'booking.dinner': 'Cena',
            'booking.additionalInformation': 'Información Adicional (Opcional)',
            'booking.specialOccasionPlaceholder': 'Cuéntanos sobre cualquier ocasión especial, requisitos dietéticos, preferencias de asiento, u otras solicitudes que nos ayuden a hacer tu experiencia perfecta...',
            'booking.examples': '💡 Ejemplos: Celebración de aniversario, accesibilidad para silla de ruedas, menú vegetariano, asiento junto a la ventana, etc.',
            'booking.confirmWithin2Hours': 'Confirmaremos tu reserva dentro de 2 horas',
            'booking.submitting': 'Enviando...',
            'booking.reserveTable': 'Reservar Mesa',
            // Accessibility labels
            'aria.toggleTheme': 'Cambiar tema',
            'aria.toggleMenu': 'Alternar menú',
            'aria.selectLanguage': 'Seleccionar idioma',
            'aria.previousSlide': 'Diapositiva anterior',
            'aria.nextSlide': 'Siguiente diapositiva',
            'aria.goToSlide': 'Ir a la diapositiva {number}',
            'aria.resumeVideo': 'Reanudar video',
            'aria.pauseVideo': 'Pausar video',
            'aria.facebook': 'Facebook',
            'aria.instagram': 'Instagram',
            'aria.twitter': 'Twitter',
            // Alt text
            'alt.logo': 'Logo de restaurant',
            'alt.restaurantInterior': 'Interior del restaurante',
        },
        ar: {
            'nav.home': 'الرئيسية',
            'nav.menu': 'قائمتنا',
            'nav.about': 'من نحن',
            'nav.gallery': 'المعرض',
            'nav.contact': 'اتصل بنا',
            'nav.reservations': 'الحجوزات',
            'hero.slide1.tag': 'مرحباً',
            'hero.slide1.title': 'الطعام والترفيه المثالي',
            'hero.slide1.subtitle': 'استمتع بمطبخ استثنائي، بار مميز وألعاب مثيرة - كل شيء تحت سقف واحد',
            'hero.slide1.cta': 'احجز الآن',
            'hero.slide2.tag': 'القائمة',
            'hero.slide2.title': 'خدمة 24/7 متميزة',
            'hero.slide2.subtitle': 'مفتوح على مدار الساعة لخدمتك ليلاً ونهاراً',
            'hero.slide2.cta': 'عرض القائمة',
            'hero.slide3.tag': 'التجربة',
            'hero.slide3.title': 'أكل بلا حدود وساعة سعيدة',
            'hero.slide3.subtitle': 'تجارب طعام غير محدودة وعروض ساعة سعيدة خاصة',
            'hero.slide3.cta': 'استكشف المعرض',
            'hero.bookNow': 'احجز الآن',
            'booking.title': 'احجز طاولتك',
            'booking.titlePerfect': 'احجز طاولتك المثالية',
            'booking.subtitle': 'احجز مكانك لتجربة طعام لا تُنسى',
            'booking.quickReservation': 'حجز سريع',
            'booking.reservationForm': 'نموذج الحجز',
            'booking.description': 'املأ النموذج أدناه وسنؤكد حجزك خلال ساعتين. لا يمكننا الانتظار لاستقبالك!',
            'booking.success': 'تم إرسال الحجز بنجاح!',
            'booking.error': 'فشل في إرسال الحجز',
            'booking.form.name': 'الاسم الكامل',
            'booking.form.namePlaceholder': 'أدخل اسمك الكامل',
            'booking.form.nameRequired': 'الاسم مطلوب',
            'booking.form.email': 'البريد الإلكتروني',
            'booking.form.emailPlaceholder': 'أدخل بريدك الإلكتروني',
            'booking.form.emailRequired': 'البريد الإلكتروني مطلوب',
            'booking.form.emailInvalid': 'عنوان بريد إلكتروني غير صحيح',
            'booking.form.phone': 'رقم الهاتف',
            'booking.form.phonePlaceholder': 'أدخل رقم هاتفك',
            'booking.form.phoneRequired': 'رقم الهاتف مطلوب',
            'booking.form.date': 'التاريخ',
            'booking.form.dateRequired': 'التاريخ مطلوب',
            'booking.form.time': 'الوقت',
            'booking.form.timeSelect': 'اختر الوقت',
            'booking.form.timeRequired': 'الوقت مطلوب',
            'booking.form.guests': 'عدد الضيوف',
            'booking.form.guest': 'ضيف',
            'booking.form.guestsSelect': 'اختر عدد الضيوف',
            'booking.form.guestsRequired': 'عدد الضيوف مطلوب',
            'booking.form.guestsMin': 'الحد الأدنى ضيف واحد',
            'booking.form.guestsMax': 'الحد الأقصى 20 ضيف',
            'booking.form.specialRequests': 'طلبات خاصة',
            'booking.form.specialRequestsPlaceholder': 'أي طلبات خاصة أو متطلبات غذائية...',
            'booking.form.submit': 'احجز طاولة',
            'booking.form.submitting': 'جاري الإرسال...',
            'booking.form.orCall': 'أو اتصل بنا مباشرة:',
            'booking.form.confirmationTime': 'سنؤكد حجزك خلال ساعتين',
            'highlights.title': 'نقاط',
            'highlights.subtitle': 'التميز',
            'highlights.description': 'اكتشف ما يجعل روستي الوجهة الأولى للطعام والترفيه والتجارب التي لا تُنسى في غوما.',
            'highlights.tandoori.title': 'أكل بلا حدود',
            'highlights.tandoori.description': 'استمتع بطعام غير محدود مع بوفيهنا الاستثنائي الذي يضم مطابخ متنوعة وتحضيرات طازجة طوال اليوم.',
            'highlights.curries.title': 'خصومات "الساعة السعيدة" في المطعم',
            'highlights.curries.description': 'استفد من عروضنا الخاصة وخصوماتنا الجذابة على أطباقنا المميزة خلال ساعات الذروة. فرصة مثالية لاكتشاف مطبخنا بأسعار مخفضة.',
            'highlights.ambiance.title': 'مركز الترفيه',
            'highlights.ambiance.description': 'أكثر من مجرد طعام - استمتع ببارنا المميز، ألعاب مثيرة، وبيئة نابضة بالحياة مثالية لأي مناسبة.',
            'testimonials.title': 'ما يقوله',
            'testimonials.subtitle': 'عملاؤنا',
            'testimonials.description': 'لا تأخذ كلامنا فقط. إليك ما يقوله عملاؤنا المقدرون عن تجربة الطعام لديهم.',
            'testimonials.featured': 'مميز',
            'testimonials.overall.title': 'التقييم العام',
            'testimonials.overall.description': 'بناءً على {count}+ تقييمات العملاء',
            'footer.description': 'استمتع بالطعام والترفيه المثالي في روستي في قلب غوما. مع ساعات عمل ممتدة، نقدم مطبخ استثنائي، خدمة بار مميزة، وتجارب ألعاب مثيرة تحت سقف واحد.',
            'footer.quickLinks': 'روابط سريعة',
            'footer.contactInfo': 'معلومات الاتصال',
            'footer.openingHours': 'ساعات العمل',
            'footer.days.monday': 'الاثنين',
            'footer.days.tuesday': 'الثلاثاء',
            'footer.days.wednesday': 'الأربعاء',
            'footer.days.thursday': 'الخميس',
            'footer.days.friday': 'الجمعة',
            'footer.days.saturday': 'السبت',
            'footer.days.sunday': 'الأحد',
            'footer.closed': 'مغلق',
            'footer.hours': '8:30 ص - 1:00 ص',
            'footer.hours.saturday': '10:30 ص - 1:00 ص',
            'footer.copyright': 'جميع الحقوق محفوظة.',
            'home.welcome': 'مرحباً بك في',
            'home.description': 'استمتع بالطعام والترفيه المثالي في روستي في قلب غوما. مع ساعات عمل ممتدة، نقدم مطبخ استثنائي، خدمة بار مميزة، ألعاب مثيرة، وتجارب أكل بلا حدود.',
            'home.rating.title': 'تقييم 4.8 نجوم',
            'home.rating.description': 'مقيم باستمرار من بين الأفضل في غوما',
            'stats.starRating': 'تقييم النجوم',
            'stats.happyCustomers': 'عملاء سعداء',
            'stats.signatureDishes': 'أطباق مميزة',
            'stats.yearsExcellence': 'سنوات من التميز',
            'about.title': 'من نحن',
            'about.subtitle': 'اكتشف القصة وراء روستي وشغفنا بتجارب الطعام والترفيه الاستثنائية.',
            'about.story.title': 'قصتنا',
            'about.values.title': 'قيمنا',
            'about.established': 'تأسس',
            'about.values.subtitle': 'المبادئ التي توجه كل ما نقوم به في روستي.',
            'about.values.authenticity.title': 'التميز',
            'about.values.authenticity.description': 'نسعى للتميز في كل جانب - من مطبخنا وخدمة البار إلى الألعاب والضيافة.',
            'about.values.quality.title': 'خدمة 24/7',
            'about.values.quality.description': 'نحن هنا دائماً من أجلك، نقدم جودة وخدمة متسقة على مدار الساعة.',
            'about.values.hospitality.title': 'الترفيه',
            'about.values.hospitality.description': 'نخلق تجارب لا تُنسى تجمع بين الطعام الرائع، المشروبات المميزة، والترفيه المثير.',
            'about.team.title': 'تعرف على فريقنا',
            'about.team.subtitle': 'فريقنا الشغوف المكرس لتقديم أفضل تجربة طعام وترفيه.',
            'about.team.chef.name': 'الشيف ماري أوييمانا',
            'about.team.chef.role': 'الشيف التنفيذي',
            'about.team.chef.description': 'مع أكثر من 12 عاماً من الخبرة في المطبخ الدولي، تخلق الشيف ماري أطباق استثنائية لقائمتنا المتنوعة.',
            'about.team.sousChef.name': 'جيمس كابيرا',
            'about.team.sousChef.role': 'مدير البار',
            'about.team.sousChef.description': 'جيمس يشرف على عمليات بارنا المميز، ويصنع كوكتيلات مميزة ويدير مجموعتنا الواسعة من المشروبات الروحية.',
            'about.team.manager.name': 'سارة موغيشا',
            'about.team.manager.role': 'المدير العام',
            'about.team.manager.description': 'سارة تضمن عمليات سلسة 24/7 وأن كل ضيف يحصل على خدمة استثنائية عبر جميع خدماتنا.',
            'about.story.content': 'روستي هو مؤسسة فريدة تقع في قلب غوما، تقدم تجربة طعام وترفيه كاملة. معروف بباره المتنوع، مطعمه، وخدمات الألعاب، روستي هو مكان تجمع ديناميكي لأي مناسبة.<br/><br/>مع ساعات عمل 24/7، نقدم خدمة مستمرة لتلبية جميع احتياجاتك، ليلاً ونهاراً. مهمتنا بسيطة: إنشاء وجهة استثنائية حيث الطعام الرائع، المشروبات المميزة، والترفيه المثير يجتمعون تحت سقف واحد.<br/><br/>نتخصص في تجارب الأكل بلا حدود وعروض الساعة السعيدة، مما يضمن أن كل زيارة تقدم قيمة كبيرة ورضا. من إفطار الصباح الباكر إلى العشاء المتأخر، من اجتماعات العمل إلى الاحتفالات، روستي يتكيف مع نمط حياتك.<br/><br/>نفتخر بالتزامنا بالتميز، التوفر 24/7، وخلق تجارب لا تُنسى. فريقنا المتفاني يعمل على مدار الساعة لضمان أن كل ضيف يستمتع بخدمة استثنائية ولحظات لا تُنسى.',
            'contact.title': 'اتصل بنا',
            'contact.subtitle': 'تواصل معنا للحجوزات، الاستفسارات، أو فقط لتحية.',
            'contact.getInTouch': 'تواصل معنا',
            'contact.address': 'العنوان',
            'contact.phone': 'الهاتف',
            'contact.email': 'البريد الإلكتروني',
            'contact.hours': 'ساعات العمل',
            'contact.hours.description': 'الأحد-الجمعة: 8:30 ص - 1:00 ص، السبت: 10:30 ص - 1:00 ص',
            'contact.form.title': 'أرسل لنا رسالة',
            'contact.form.name': 'الاسم الكامل',
            'contact.form.email': 'عنوان البريد الإلكتروني',
            'contact.form.subject': 'الموضوع',
            'contact.form.message': 'الرسالة',
            'contact.form.submit': 'إرسال الرسالة',
            'contact.form.submitting': 'جاري الإرسال...',
            'reservation.title': 'احجز طاولتك',
            'reservation.subtitle': 'احجز مكانك لتجربة طعام لا تُنسى',
            'reservation.confirmed.title': 'تم إرسال الحجز!',
            'reservation.confirmed.subtitle': 'شكراً لاختيارك روستي. تم إرسال حجزك بنجاح. سيتم إرسال بريد إلكتروني للتأكيد إلى عنوان بريدك الإلكتروني.',
            'reservation.confirmed.details': 'تفاصيل الحجز',
            'reservation.confirmed.message': 'سنتواصل معك قريباً لتأكيد حجزك',
            'reservation.confirmed.another': 'احجز مرة أخرى',
            'reservation.form.name': 'الاسم الكامل',
            'reservation.form.namePlaceholder': 'أدخل اسمك الكامل',
            'reservation.form.email': 'عنوان البريد الإلكتروني',
            'reservation.form.emailPlaceholder': 'أدخل عنوان بريدك الإلكتروني',
            'reservation.form.phone': 'رقم الهاتف',
            'reservation.form.phonePlaceholder': 'أدخل رقم هاتفك',
            'reservation.form.date': 'التاريخ',
            'reservation.form.time': 'الوقت',
            'reservation.form.guests': 'عدد الضيوف',
            'reservation.form.message': 'طلبات خاصة',
            'reservation.form.messagePlaceholder': 'أي طلبات خاصة أو متطلبات غذائية...',
            'reservation.form.submit': 'احجز طاولة',
            'reservation.form.submitting': 'جاري الإرسال...',
            'reservation.info.title': 'معلومات الحجز',
            'reservation.info.openingHours': 'ساعات العمل',
            'reservation.info.monday': 'الاثنين',
            'reservation.info.closed': 'مغلق',
            'reservation.info.tuesdaySunday': 'الأحد - الجمعة',
            'reservation.info.hours': '8:30 ص - 1:00 ص',
            'reservation.info.contact': 'اتصل',
            'reservation.info.location': 'الموقع',
            'reservation.info.importantNotes': 'ملاحظات مهمة',
            'reservation.info.notes.held': 'الحجوزات محفوظة لمدة 15 دقيقة',
            'reservation.info.notes.groups': 'المجموعات الكبيرة (8+ ضيوف) تتطلب إشعار 24 ساعة',
            'reservation.info.notes.dietary': 'يمكن استيعاب المتطلبات الغذائية الخاصة',
            'reservation.info.notes.confirmation': 'سيتم إرسال التأكيد عبر البريد الإلكتروني/الرسائل النصية',
            'home.hours.title': 'ساعات ممتدة',
            'home.hours.description': 'الأحد-الجمعة: 8:30 ص - 1:00 ص، السبت: 10:30 ص - 1:00 ص',
            'home.location.title': 'موقع مميز',
            'home.location.description': '119 شارع كانياموهانغا، غوما',
            'home.cta.title': 'مستعد لتجربة المطبخ الإيطالي الأصيل؟',
            'home.cta.description': 'احجز طاولتك اليوم واستمتع بمطبخنا الإيطالي الاستثنائي، البار المميز، وعروض الساعة السعيدة.',
            'home.cta.readyToDine': 'مستعد لتناول الطعام؟',
            'home.cta.callNow': 'اتصل الآن',
            'home.features.extendedHours.title': 'ساعات ممتدة',
            'home.features.extendedHours.description': 'مفتوح حتى وقت متأخر كل يوم لراحتكم',
            'home.features.primeLocation.title': 'موقع مميز',
            'home.features.primeLocation.description': 'مواقف سهلة وإمكانية الوصول',
            'home.features.allYouCanEat.title': 'قائمة الأطفال',
            'home.features.allYouCanEat.description': 'أطباق خاصة للأطفال للعائلات',
            'highlights.stats.awardWinner': 'فائز بجائزة',
            'highlights.stats.customers': '500+ عميل',
            'highlights.stats.experience': '5+ سنوات',
            'testimonials.stats.averageRating': '4.3★ متوسط',
            'testimonials.stats.totalReviews': '500+ مراجعة',
            'testimonials.stats.satisfiedCustomers': '95% راضون',
            'testimonials.stats.totalReviewsLabel': 'إجمالي المراجعات',
            'testimonials.stats.fiveStarReviews': 'مراجعات 5 نجوم',
            'testimonials.stats.recommendUs': 'يوصون بنا',
            'gallery.discoverMore': 'اكتشف المزيد من الصور الرائعة من مطعمنا',
            'home.badges.entertainmentHub': 'مركز الترفيه',
            'home.badges.healthCertified': 'معتمد صحي',
            'home.badges.rating': '4.8★ تقييم',
            'highlights.signatureExperience': 'تجربتنا المميزة',
            'home.premiumExperience': 'تجربة مميزة',
            'home.premiumBarExperience': 'تجربة بار مميزة',
            'testimonials.customerStories': 'قصص حب العملاء',
            'testimonials.overallRating': 'التقييم العام',
            'footer.privacyPolicy': 'سياسة الخصوصية',
            'footer.termsOfService': 'شروط الخدمة',
            'footer.cookiePolicy': 'سياسة ملفات تعريف الارتباط',
            'gallery.viewFullGallery': 'عرض المعرض الكامل',
            'hero.cta.reservation': 'احجز طاولة',
            'hero.cta.menu': 'عرض القائمة',
            'loading': 'جاري التحميل...',
            'error.title': 'عذراً!',
            'error.hero': 'لا يمكن تحميل المحتوى',
            'status.live': 'API مباشر',
            'status.demo': 'نسخة تجريبية احتياطية',
            'menu.title': 'قائمتنا',
            'menu.subtitle': 'اكتشف اختيارنا المدروس بعناية من الأطباق الدولية، المحضرة بأفضل المكونات وتقنيات الطبخ الحديثة.',
            'menu.allDishes': 'جميع الأطباق',
            'menu.loading': 'جاري تحميل القائمة...',
            'menu.loadingCategories': 'جاري تحميل الفئات...',
            'menu.loadingItems': 'جاري تحميل الأطباق...',
            'menu.noItems': 'لم يتم العثور على أطباق في هذه الفئة.',
            'menu.viewDetails': 'عرض التفاصيل',
            'menu.addToOrder': 'أضف للطلب',
            'menu.close': 'إغلاق',
            'menu.vegetarian': 'نباتي',
            'menu.spicy': 'حار',
            'menu.searchPlaceholder': 'ابحث عن طبق...',
            'menu.filters': 'المرشحات',
            'menu.dietary': 'النظام الغذائي',
            'menu.priceRange': 'نطاق السعر',
            'menu.allPrices': 'جميع الأسعار',
            'menu.under15': 'أقل من $15',
            'menu.15to20': '$15 - $20',
            'menu.over20': 'أكثر من $20',
            'menu.reset': 'إعادة تعيين',
            'menu.exploreCategories': 'استكشف فئاتنا',
            'menu.dishes': 'أطباق',
            'menu.chefSelection': 'اختيار الشيف',
            'menu.internationalCuisine': 'المطبخ الدولي',
            'menu.culinaryDelights': 'متع الطهي',
            'menu.curriesDescription': 'من الكاري العطري إلى النان المخبوز حديثاً، كل طبق محضر بالتوابل الأصيلة والتقنيات التقليدية المنقولة عبر الأجيال.',
            'menu.noItemsDescription': 'حاول تعديل مرشحاتك أو مصطلحات البحث للعثور على ما تبحث عنه.',
            'menu.resetFilters': 'إعادة تعيين المرشحات',
            'gallery.title': 'معرضنا',
            'gallery.culinaryArtistry': 'الفن الطهوي',
            'gallery.visualGallery': 'المعرض البصري',
            'restaurantBar.title': 'بار روستي',
            'restaurantBar.subtitle': 'مشروبات مميزة وكوكتيلات',
            'restaurantBar.description': 'اكتشف تجربة بارنا المتطورة مع المشروبات الروحية المميزة، الكوكتيلات المصنوعة يدوياً، ومجموعة واسعة من النبيذ.',
            'restaurantBar.signature.title': 'كوكتيلات مميزة',
            'restaurantBar.signature.description': 'كوكتيلات مصنوعة بخبرة مع المشروبات الروحية المميزة والمكونات الطازجة، مستوحاة من النكهات الهندية والوصفات الكلاسيكية الدولية.',
            'restaurantBar.wines.title': 'مجموعة النبيذ',
            'restaurantBar.wines.description': 'اختيار مدروس بعناية من النبيذ الفاخر من جميع أنحاء العالم، متطابق تماماً مع مطبخنا الدولي المتنوع.',
            'restaurantBar.spirits.title': 'مشروبات روحية مميزة',
            'restaurantBar.spirits.description': 'مجموعة واسعة من الويسكي، الجن، الفودكا المميزة، ومشروبات روحية فاخرة أخرى للذوق المميز.',
            'restaurantBar.features.expertBartenders': 'ساقون خبراء',
            'restaurantBar.features.premiumSpirits': 'مشروبات روحية مميزة',
            'restaurantBar.features.customCocktails': 'كوكتيلات مخصصة',
            'restaurantBar.features.wineCollection': 'مجموعة النبيذ',
            'restaurantBar.features.happyHour': 'ساعة سعيدة يومية',
            'restaurantBar.features.liveMusic': 'ليالي موسيقى حية',
            'restaurantBar.cta': 'زر بارنا',
            'restaurantBar.hours': 'ساعات البار: الأحد-الجمعة 8:30 ص - 1:00 ص، السبت 10:30 ص - 1:00 ص',
            // Textes hardcodés identifiés
            'booking.form.numberOfGuests': 'عدد الضيوف',
            'booking.form.selectPartySize': 'اختر حجم المجموعة',
            'home.games.puzzleGames': 'ألعاب الألغاز',
            'home.games.puzzleDescription': 'ألغاز محيرة وألعاب ذهنية',
            'home.games.arcadeClassics': 'كلاسيكيات الأركيد',
            'home.games.arcadeDescription': 'ألعاب أركيد رجعية وكلاسيكيات',
            'home.games.strategyGames': 'ألعاب استراتيجية',
            'home.games.strategyDescription': 'ألعاب التفكير الاستراتيجي والتخطيط',
            'home.games.quickGames': 'ألعاب سريعة',
            'home.games.quickDescription': 'ألعاب مصغرة سريعة وتحديات',
            'home.games.cardGames': 'ألعاب الورق',
            'home.games.cardDescription': 'ألعاب ورق رقمية وكلاسيكيات الكازينو',
            'home.games.difficulty': 'الصعوبة',
            'home.games.players': 'اللاعبون',
            'home.games.duration': 'المدة',
            'home.games.easy': 'سهل',
            'home.games.medium': 'متوسط',
            'home.games.hard': 'صعب',
            'reservation.confirmed.thankYou': 'شكراً لاختيارنا! تم إرسال حجزك بنجاح. سيتم إرسال بريد إلكتروني للتأكيد إلى عنوان بريدك الإلكتروني. نحن متحمسون لاستقبالك لتجربة طعام هندي أصيلة.',
            'contact.fromCityCenter': 'من وسط المدينة',
            'contact.parkingAvailable': 'موقف سيارات متاح',
            'contact.free': 'مجاني',
            'contact.minutes': 'دقيقة',
            'contact.contactOurTeam': 'تواصل مع فريقنا',
            'contact.readyToExperience': 'هل أنت مستعد لتجربة الطعام والترفيه المثالي؟ نحن هنا لمساعدتك في التخطيط لزيارتك المثالية إلى روستي.',
            'contact.contactDetails': 'تفاصيل الاتصال',
            'contact.howToReachUs': 'كيفية الوصول إلينا',
            'contact.helpWithReservations': 'نحن هنا لمساعدتك في الحجوزات والاستفسارات أو أي طلبات خاصة لتجربة الطعام الخاصة بك.',
            'contact.visitRestaurant': 'زر مطعمنا',
            'contact.callUsDirectly': 'اتصل بنا مباشرة',
            'contact.sendUsEmail': 'أرسل لنا بريداً إلكترونياً',
            'contact.openingHours': 'ساعات العمل',
            'contact.getDirections': 'احصل على الاتجاهات',
            'contact.callNow': 'اتصل الآن',
            'contact.sendEmail': 'إرسال بريد إلكتروني',
            'contact.bookTable': 'احجز طاولة',
            'contact.availableDuringBusiness': 'متاح خلال ساعات العمل',
            'contact.respondWithin24': 'سنجيب خلال 24 ساعة',
            'contact.monday': 'الاثنين',
            'contact.tuesdayToSunday': 'الثلاثاء - الأحد',
            'contact.closed': 'مغلق',
            'contact.findUs': 'اعثر علينا',
            'contact.sendMessage': 'إرسال رسالة',
            'contact.getInTouchWithUs': 'تواصل معنا',
            'contact.haveQuestion': 'هل لديك سؤال أو طلب خاص أو تريد إجراء حجز؟ نحب أن نسمع منك ونساعدك في جعل تجربة الطعام الخاصة بك مثالية.',
            'contact.personalInformation': 'المعلومات الشخصية',
            'contact.contactInformation': 'معلومات الاتصال',
            'contact.messageDetails': 'تفاصيل الرسالة',
            'contact.firstName': 'الاسم الأول',
            'contact.lastName': 'اسم العائلة',
            'contact.emailAddress': 'عنوان البريد الإلكتروني',
            'contact.phoneNumber': 'رقم الهاتف',
            'contact.subject': 'الموضوع',
            'contact.yourMessage': 'رسالتك',
            'contact.enterFirstName': 'أدخل اسمك الأول',
            'contact.enterLastName': 'أدخل اسم عائلتك',
            'contact.emailPlaceholder': 'بريدك.الإلكتروني@مثال.com',
            'contact.phonePlaceholder': '+243 xxx xxx xxx',
            'contact.selectPurpose': 'اختر الغرض من رسالتك',
            'contact.messagePlaceholder': 'أخبرنا عن استفسارك، المتطلبات الخاصة، التواريخ المفضلة، عدد الضيوف أو أي تفاصيل أخرى تساعدنا على مساعدتك بشكل أفضل...',
            'contact.respondWithin24Hours': 'سنجيب خلال 24 ساعة',
            'contact.readyToDine': 'مستعد لتناول الطعام؟',
            'contact.experienceAuthentic': 'اختبر المطبخ الهندي الأصيل',
            'contact.readyToEmbark': 'هل أنت مستعد للانطلاق في رحلة طهوية عبر النكهات الغنية للهند؟ احجز طاولتك اليوم ودعنا نخلق لك تجربة طعام لا تُنسى.',
            'contact.callNumber': 'اتصل +243 971 466 525',
            'contact.bookOnline': 'احجز عبر الإنترنت',
            'contact.quickResponse': 'استجابة سريعة',
            'contact.confirmWithin2Hours': 'سنؤكد حجزك خلال ساعتين',
            'contact.premiumExperience': 'تجربة مميزة',
            'contact.diningEntertainment': 'تجربة طعام وترفيه على مدار الساعة',
            'contact.perfectLocation': 'موقع مثالي',
            'contact.easyToFind': 'سهل العثور عليه مع موقف سيارات مجاني متاح',
            'contact.reservationInquiry': '🍽️ استفسار عن الحجز',
            'contact.generalInquiry': '💬 استفسار عام',
            'contact.feedbackReviews': '⭐ التعليقات والمراجعات',
            'contact.privateEventBooking': '🎉 حجز حدث خاص',
            'contact.cateringServices': '🚚 خدمات التموين',
            'contact.dietaryRequirements': '🥗 المتطلبات الغذائية',
            'contact.other': '📝 أخرى',
            'reservation.bookYourTable': 'احجز طاولتك',
            'reservation.reserveDiningExperience': 'احجز تجربة الطعام الخاصة بك',
            'reservation.secureYourTable': 'احجز طاولتك في مطعمنا الحائز على جوائز وابدأ رحلة طهوية لا تُنسى عبر النكهات الهندية الأصيلة.',
            'reservation.instantConfirmation': 'تأكيد فوري',
            'reservation.awardWinning': 'حائز على جوائز',
            'reservation.madeWithLove': 'مصنوع بحب',
            'reservation.personalInformation': 'المعلومات الشخصية',
            'reservation.reservationDetails': 'تفاصيل الحجز',
            'reservation.specialRequests': 'طلبات خاصة',
            'reservation.fullName': 'الاسم الكامل',
            'reservation.emailAddress': 'عنوان البريد الإلكتروني',
            'reservation.phoneNumber': 'رقم الهاتف',
            'reservation.preferredDate': 'التاريخ المفضل',
            'reservation.preferredTime': 'الوقت المفضل',
            'reservation.numberOfGuests': 'عدد الضيوف',
            'reservation.additionalInformation': 'معلومات إضافية (اختياري)',
            'reservation.specialOccasionPlaceholder': 'أخبرنا عن أي مناسبة خاصة، متطلبات غذائية، تفضيلات المقاعد أو أي طلبات أخرى تساعدنا في جعل تجربتك مثالية...',
            'reservation.examples': '💡 أمثلة: احتفال بالذكرى السنوية، إمكانية الوصول للكراسي المتحركة، قائمة نباتية، مقعد بجانب النافذة، إلخ.',
            'reservation.confirmWithin2Hours': 'سنؤكد حجزك خلال ساعتين',
            'reservation.submitting': 'جاري الإرسال...',
            'reservation.reserveMyTable': 'احجز طاولتي',
            'reservation.restaurantInfo': 'معلومات المطعم',
            'reservation.everythingYouNeed': 'كل ما تحتاج لمعرفته',
            'reservation.openingHours': 'ساعات العمل',
            'reservation.monday': 'الاثنين',
            'reservation.tuesdayToSunday': 'الثلاثاء - الأحد',
            'reservation.closed': 'مغلق',
            'reservation.contactDetails': 'تفاصيل الاتصال',
            'reservation.callUsDirectly': 'اتصل بنا مباشرة',
            'reservation.sendUsEmail': 'أرسل لنا بريداً إلكترونياً',
            'reservation.ourLocation': 'موقعنا',
            'reservation.freeParkingAvailable': 'موقف سيارات مجاني متاح',
            'reservation.importantNotes': 'ملاحظات مهمة',
            'reservation.reservationsHeld': 'الحجوزات محفوظة لمدة 15 دقيقة بعد وقت الحجز',
            'reservation.groupsAdvanceNotice': 'المجموعات من 8+ تتطلب إشعار مسبق',
            'reservation.dietaryRequirements': 'يمكن استيعاب المتطلبات الغذائية',
            'reservation.confirmationWithin2Hours': 'تأكيد خلال ساعتين عبر البريد الإلكتروني/الهاتف',
            'reservation.specialOffers': 'عروض خاصة',
            'reservation.limitedTimePromotions': 'عروض ترويجية لفترة محدودة',
            'reservation.anniversarySpecial': '🎉 خاص بالذكرى السنوية',
            'reservation.complimentaryDessert': 'حلوى مجانية للاحتفالات',
            'reservation.winePairing': '🍷 تذوق النبيذ',
            'reservation.expertSommelier': 'توصيات خبير النبيذ',
            'reservation.confirmed.confirmationId': 'رقم التأكيد',
            'reservation.confirmed.responseTime': 'وقت الاستجابة',
            'reservation.confirmed.status': 'الحالة',
            'reservation.confirmed.within2Hours': 'خلال ساعتين',
            'reservation.confirmed.confirmed': 'مؤكد',
            'reservation.confirmed.emailSent': 'سيتم إرسال بريد إلكتروني للتأكيد إلى عنوان بريدك الإلكتروني. سيتصل بنا فريقنا خلال ساعتين لتأكيد التفاصيل.',
            'reservation.confirmed.makeAnother': 'إجراء حجز آخر',
            'reservation.confirmed.returnHome': 'العودة للصفحة الرئيسية',
            'reservation.lunch': 'غداء',
            'reservation.dinner': 'عشاء',
            'reservation.selectTimeSlot': 'اختر الوقت',
            'reservation.selectPartySize': 'اختر حجم المجموعة',
            'reservation.guest': 'ضيف',
            'reservation.guests': 'ضيوف',
            'about.ourStory': 'قصتنا',
            'about.discoverOurJourney': 'اكتشف رحلتنا',
            'about.awardWinner': 'فائز بجائزة',
            'about.customers': 'عملاء',
            'about.yearsExcellence': 'سنوات من التميز',
            'about.ourBeginning': 'بدايتنا',
            'about.culinaryJourney': 'رحلة طهوية ولدت من الشغف',
            'about.uniqueEstablishment': 'روستي هو مؤسسة فريدة تقع في قلب غوما، تقدم تجربة طعام وترفيه كاملة.',
            'about.storyBegins': 'تبدأ قصتنا مع طاهٍ شغوف سافر عبر الهند، وتعلم تقنيات الطبخ التقليدية من الطهاة الماهرين في مومباي ودلهي وكيرالا.',
            'about.todayWePride': 'اليوم، نفخر بخدمة ليس فقط الطعام، بل تجربة تنقلك إلى الشوارع النابضة بالحياة والمطابخ العطرة في الهند.',
            'about.authenticRecipes': 'وصفات هندية أصيلة موروثة عبر الأجيال',
            'about.freshSpices': 'توابل طازجة مستوردة مباشرة من الهند',
            'about.serviceAvailability': 'خدمة متاحة على مدار الساعة',
            'about.ourAchievements': 'إنجازاتنا',
            'about.numbersTellStory': 'أرقام تحكي قصتنا',
            'about.milestonesRepresent': 'هذه المعالم تمثل التزامنا بالتميز والثقة التي يضعها عملاؤنا فينا.',
            'about.ourValues': 'قيمنا',
            'about.whatDrivesUs': 'ما يدفعنا إلى الأمام',
            'about.coreValuesShape': 'قيمنا الأساسية تشكل كل جانب من جوانب رحلتنا الطهوية، من توريد المكونات إلى خدمة ضيوفنا.',
            'about.authenticity': 'الأصالة',
            'about.authenticityDescription': 'نكرم طرق الطبخ الهندي التقليدية والوصفات الأصيلة الموروثة عبر الأجيال.',
            'about.quality': 'الجودة',
            'about.qualityDescription': 'كل مكون يتم اختياره بعناية وكل طبق يتم تحضيره بعناية فائقة بالتفاصيل.',
            'about.hospitality': 'الضيافة',
            'about.hospitalityDescription': 'نؤمن بمعاملة كل ضيف كعائلة، وخلق تجارب طعام دافئة ومليئة بالذكريات.',
            'about.meetOurTeam': 'تعرف على فريقنا',
            'about.passionatePeople': 'الأشخاص الشغوفون وراء نجاحنا',
            'about.meetTalentedIndividuals': 'تعرف على الأفراد الموهوبين الذين يجعلون رؤيتنا الطهوية حقيقة بالخبرة والشغف والتفاني.',
            'about.chefRajeshKumar': 'الشيف راجيش كومار',
            'about.executiveChef': 'الشيف التنفيذي',
            'about.chefRajeshDescription': 'مع أكثر من 15 عاماً من الخبرة، يجلب الشيف راجيش النكهات الهندية الأصيلة إلى كل طبق.',
            'about.internationalCuisine': 'المطبخ الدولي والبار',
            'about.chefPriyaSharma': 'الشيف بريا شارما',
            'about.sousChef': 'الشيف المساعد',
            'about.chefPriyaDescription': 'متخصصة في مطبخ جنوب الهند، تتأكد الشيف بريا من أن كل كاري متوازن بشكل مثالي.',
            'about.southIndianCurries': 'كاري جنوب الهند',
            'about.amitPatel': 'أميت باتيل',
            'about.restaurantManager': 'مدير المطعم',
            'about.amitDescription': 'أميت يتأكد من أن كل ضيف يشعر بالترحيب ويستمتع بتجربة طعام استثنائية.',
            'about.guestExperience': 'تجربة الضيف',
            'about.loadingOurStory': 'جاري تحميل قصتنا...',
            'join.stayConnected': 'ابق على تواصل',
            'join.culinaryJourney': 'انضم إلى رحلتنا الطهوية',
            'join.getExclusiveOffers': 'احصل على عروض حصرية ووصفات وكن أول من يعرف عن أحداثنا الخاصة',
            'join.enterYourEmail': 'أدخل بريدك الإلكتروني',
            'join.subscribe': 'اشترك',
            'join.rating': '4.3★ تقييم',
            'join.awardWinner': 'فائز بجائزة',
            'join.happyCustomers': '500+ عميل سعيد',
            'join.diningEntertainmentHub': 'مركز الطعام والترفيه',
            'join.followUs': 'تابعنا:',
            'gallery.loadingCategories': 'جاري تحميل المعرض...',
            'gallery.subtitle': 'اغمر نفسك في رحلة بصرية عبر فننا الطهوي وأجوائنا ولحظاتنا التي لا تُنسى.',
            'gallery.photos': '50+ صورة',
            'gallery.highQuality': 'جودة عالية',
            'gallery.curatedCollection': 'مجموعة منتقاة',
            'gallery.browseCollection': 'تصفح مجموعتنا',
            'gallery.photo': 'صورة',
            'gallery.photosAvailable': 'صورة متاحة',
            'gallery.allPhotos': 'جميع الصور',
            'gallery.loadingItems': 'جاري تحميل الصور الجميلة...',
            'gallery.viewFullSize': 'عرض بالحجم الكامل',
            'gallery.gallery': 'معرض',
            'gallery.noPhotosFound': 'لم يتم العثور على صور',
            'gallery.noPhotos': 'لا توجد صور متاحة في هذه الفئة في الوقت الحالي.',
            'gallery.tryDifferentCategory': 'حاول اختيار فئة مختلفة أو تحقق لاحقاً للحصول على محتوى جديد.',
            'gallery.viewAllPhotos': 'عرض جميع الصور',
            'gallery.fullResolution': 'دقة كاملة',
            'booking.personalInformation': 'المعلومات الشخصية',
            'booking.fullName': 'الاسم الكامل',
            'booking.fullNameRequired': 'الاسم الكامل مطلوب',
            'booking.enterFullName': 'أدخل اسمك الكامل',
            'booking.emailAddress': 'عنوان البريد الإلكتروني',
            'booking.emailRequired': 'عنوان البريد الإلكتروني مطلوب',
            'booking.invalidEmail': 'يرجى إدخال عنوان بريد إلكتروني صحيح',
            'booking.emailPlaceholder': 'بريدك.الإلكتروني@مثال.com',
            'booking.phoneNumber': 'رقم الهاتف',
            'booking.phoneRequired': 'رقم الهاتف مطلوب',
            'booking.phonePlaceholder': '+243 xxx xxx xxx',
            'booking.reservationDetails': 'تفاصيل الحجز',
            'booking.preferredDate': 'التاريخ المفضل',
            'booking.dateRequired': 'التاريخ مطلوب',
            'booking.preferredTime': 'الوقت المفضل',
            'booking.timeRequired': 'الوقت مطلوب',
            'booking.selectTimeSlot': 'اختر الوقت',
            'booking.lunch': 'غداء',
            'booking.dinner': 'عشاء',
            'booking.additionalInformation': 'معلومات إضافية (اختياري)',
            'booking.specialOccasionPlaceholder': 'أخبرنا عن أي مناسبة خاصة، متطلبات غذائية، تفضيلات الجلوس، أو طلبات أخرى تساعدنا في جعل تجربتك مثالية...',
            'booking.examples': '💡 أمثلة: احتفال بالذكرى السنوية، إمكانية الوصول للكرسي المتحرك، قائمة نباتية، مقعد بجانب النافذة، إلخ.',
            'booking.confirmWithin2Hours': 'سنؤكد حجزك خلال ساعتين',
            'booking.submitting': 'جاري الإرسال...',
            'booking.reserveTable': 'احجز طاولة',
            // Accessibility labels
            'aria.toggleTheme': 'تبديل المظهر',
            'aria.toggleMenu': 'تبديل القائمة',
            'aria.selectLanguage': 'اختيار اللغة',
            'aria.previousSlide': 'الشريحة السابقة',
            'aria.nextSlide': 'الشريحة التالية',
            'aria.goToSlide': 'الذهاب إلى الشريحة {number}',
            'aria.resumeVideo': 'استئناف الفيديو',
            'aria.pauseVideo': 'إيقاف الفيديو مؤقتاً',
            'aria.facebook': 'فيسبوك',
            'aria.instagram': 'إنستغرام',
            'aria.twitter': 'تويتر',
            // Alt text
            'alt.logo': 'شعار روستي',
            'alt.restaurantInterior': 'داخل المطعم',
        },
        la: {
            'nav.home': 'Domus',
            'nav.menu': 'Index',
            'nav.about': 'De Nobis',
            'nav.gallery': 'Pinacotheca',
            'nav.contact': 'Contactus',
            'nav.reservations': 'Reservationes',
            'hero.slide1.tag': 'Salve',
            'hero.slide1.title': 'Ultima Cena et Ludi',
            'hero.slide1.subtitle': 'Experire culinam excellentem, tabernam praemium, et ludos excitantes - omnia sub uno tecto',
            'hero.slide1.cta': 'Nunc Reserva',
            'hero.slide2.tag': 'Index',
            'hero.slide2.title': 'Excellentia Servitii 24/7',
            'hero.slide2.subtitle': 'Aperta per horologium ut te serviam die et nocte',
            'hero.slide2.cta': 'Vide Index',
            'hero.slide3.tag': 'Experientia',
            'hero.slide3.title': 'Omnia-Quae-Potes-Edere et Hora Felix',
            'hero.slide3.subtitle': 'Experientiae cenae infinitae et negotia horae felicis specialia',
            'hero.slide3.cta': 'Explora Pinacothecam',
            'hero.bookNow': 'Nunc Reserva',
            'booking.title': 'Reserva Mensam Tuam',
            'booking.titlePerfect': 'Reserva Mensam Tuam Perfectam',
            'booking.subtitle': 'Reserva locum tuum pro experientia cenae inobliviabili',
            'booking.quickReservation': 'Reservatio Celeris',
            'booking.reservationForm': 'Formula Reservationis',
            'booking.description': 'Comple formam infra et confirmabimus reservationem tuam intra 2 horas. Non possumus exspectare ut te accipiamus!',
            'booking.success': 'Reservatio feliciter submissa!',
            'booking.error': 'Reservatio submittere defecit',
            'booking.form.name': 'Nomen Plenum',
            'booking.form.namePlaceholder': 'Intra nomen tuum plenum',
            'booking.form.nameRequired': 'Nomen requiritur',
            'booking.form.email': 'Electronica Epistula',
            'booking.form.emailPlaceholder': 'Intra electronica epistula tua',
            'booking.form.emailRequired': 'Electronica epistula requiritur',
            'booking.form.emailInvalid': 'Electronica epistula invalida',
            'booking.form.phone': 'Numerus Telephoni',
            'booking.form.phonePlaceholder': 'Intra numerum telephoni tui',
            'booking.form.phoneRequired': 'Numerus telephoni requiritur',
            'booking.form.date': 'Dies',
            'booking.form.dateRequired': 'Dies requiritur',
            'booking.form.time': 'Tempus',
            'booking.form.timeSelect': 'Elige tempus',
            'booking.form.timeRequired': 'Tempus requiritur',
            'booking.form.guests': 'Numerus Hospitum',
            'booking.form.guest': 'Hospes',
            'booking.form.guestsSelect': 'Elige numerum hospitum',
            'booking.form.guestsRequired': 'Numerus hospitum requiritur',
            'booking.form.guestsMin': 'Minimum 1 hospes',
            'booking.form.guestsMax': 'Maximum 20 hospites',
            'booking.form.specialRequests': 'Peculiares Petitiones',
            'booking.form.specialRequestsPlaceholder': 'Quaecumque petitiones speciales vel requisita cibaria...',
            'booking.form.submit': 'Reserva Mensam',
            'booking.form.submitting': 'Submittens...',
            'booking.form.orCall': 'Vel voca nos directe:',
            'booking.form.confirmationTime': 'Confirmabimus reservationem tuam intra 2 horas',
            'highlights.title': 'Nostra',
            'highlights.subtitle': 'Emphases',
            'highlights.description': 'Inveni quid facit restaurant destinationem primam pro cena, ludo, et experientiis inobliviabilibus in Goma.',
            'highlights.tandoori.title': 'Omnia-Quae-Potes-Edere',
            'highlights.tandoori.description': 'Experire culinam authenticam cum omnibus quae potes edere experientiis et negotiis horae felicis.',
            'highlights.curries.title': 'Reductiones "Hora Felicis" in Taberna',
            'highlights.curries.description': 'Utere nostris offertis specialibus et reductionibus attractivis in nostris ferculis signature durante horis acutis. Opportunitas perfecta ad inveniendam culinam nostram pretiis reductis.',
            'highlights.ambiance.title': 'Centrum Ludi',
            'highlights.ambiance.description': 'Plus quam cena tantum - fruere taberna nostra praemium, ludis excitantibus, et ambitu vibrante perfecto pro quacumque occasione.',
            'testimonials.title': 'Quid Nostri',
            'testimonials.subtitle': 'Clientes Dicunt',
            'testimonials.description': 'Ne tantum verbum nostrum accipe. Ecce quid nostri clientes pretiosi dicunt de experientia cenae eorum.',
            'testimonials.featured': 'Emphases',
            'testimonials.overall.title': 'Aestimatio Generalis',
            'testimonials.overall.description': 'Basata in {count}+ recensionibus clientium',
            'footer.description': 'Experire ultimam cenam et ludum apud restaurant in corde Goma. Cum horis extensis, offerimus culinam excellentem, servitium tabernae praemium, et experientias ludorum excitantium omnes sub uno tecto.',
            'footer.quickLinks': 'Linkes Celeres',
            'footer.contactInfo': 'Info Contactus',
            'footer.openingHours': 'Horae Apertionis',
            'footer.days.monday': 'Dies Lunae',
            'footer.days.tuesday': 'Dies Martis',
            'footer.days.wednesday': 'Dies Mercurii',
            'footer.days.thursday': 'Dies Iovis',
            'footer.days.friday': 'Dies Veneris',
            'footer.days.saturday': 'Dies Saturni',
            'footer.days.sunday': 'Dies Solis',
            'footer.closed': 'Clausum',
            'footer.hours': '8:00 AM - 1:00 AM',
            'footer.hours.saturday': '10:30 AM - 1:00 AM',
            'footer.copyright': 'Omnia iura reservata.',
            'home.welcome': 'Salve ad',
            'home.description': 'Experire ultimam cenam et ludum apud restaurant in corde Goma. Cum horis extensis, offerimus culinam excellentem, servitium tabernae praemium, ludos excitantes, et experientias omnia-quae-potes-edere.',
            'home.rating.title': 'Aestimatio 4.8 Stellarum',
            'home.rating.description': 'Consistenter aestimata inter optimas in Goma',
            'stats.starRating': 'Aestimatio Stellarum',
            'stats.happyCustomers': 'Clientes Felices',
            'stats.signatureDishes': 'Fercula Signatura',
            'stats.yearsExcellence': 'Anni Excellentiae',
            'about.title': 'De Nobis',
            'about.subtitle': 'Inveni fabulam post restaurant et passionem nostram pro experientiis cenae et ludi excellentibus.',
            'about.story.title': 'Fabula Nostra',
            'about.values.title': 'Valores Nostri',
            'about.established': 'Stabilita',
            'about.values.subtitle': 'Principia quae omnia quae facimus apud restaurant ducunt.',
            'about.values.authenticity.title': 'Excellentia',
            'about.values.authenticity.description': 'Nitamur excellentiam in omni aspectu - a culina et servitio tabernae ad ludum et hospitalitatem.',
            'about.values.quality.title': 'Servitium 24/7',
            'about.values.quality.description': 'Semper hic sumus pro te, praebentes qualitatem et servitium consistentem per horologium.',
            'about.values.hospitality.title': 'Ludus',
            'about.values.hospitality.description': 'Creamus experientias inobliviabiles combinantes cibum magnum, potiones praemium, et ludum excitantem.',
            'about.team.title': 'Conveni Nostram Turmam',
            'about.team.subtitle': 'Nostra turma passionata dedicata ad tibi afferendum optimam experientiam cenae et ludi.',
            'about.team.chef.name': 'Magister Cocus Marie Uwimana',
            'about.team.chef.role': 'Magister Cocus Exsecutivus',
            'about.team.chef.description': 'Cum plus quam 12 annis experientiae culinariae internationalis, Magister Cocus Marie creat fercula excellentia pro indice nostro diverso.',
            'about.team.sousChef.name': 'James Kabera',
            'about.team.sousChef.role': 'Procurator Tabernae',
            'about.team.sousChef.description': 'James supervidet operationes tabernae nostrae praemium, creans cocktails signatura et gerens collectionem nostram spirituum extensam.',
            'about.team.manager.name': 'Sarah Mugisha',
            'about.team.manager.role': 'Procurator Generalis',
            'about.team.manager.description': 'Sarah sese curat operationes 24/7 seamless et quod omnis hospes recipit servitium excellentem per omnia servitia nostra.',
            'about.story.content': 'restaurant est stabilimentum unicum locatum in corde Goma, offerens experientiam completam cenae et ludi. Notum pro servitiis diversis tabernae, restaurantis, et ludorum, restaurant est locus conventus dynamicus pro quacumque occasione.<br/><br/>Cum horis operationis 24/7, praebemus servitium continuum ut omnes necessitates tuas occurramus, die et nocte. Missio nostra simplex est: creare destinationem excellentem ubi cibus magnus, potiones praemium, et ludus excitans conveniunt sub uno tecto.<br/><br/>Specializamur in experientiis omnia-quae-potes-edere et negotiis horae felicis, sese curantes quod omnis visitatio offert valorem magnum et satisfactionem. A ientaculo matutino ad cenam noctis tardae, a conventibus negotiorum ad celebrationes, restaurant adaptat ad vitam tuam.<br/><br/>Superbimus in obligatione nostra ad excellentiam, disponibilitatem 24/7, et creandas experientias inobliviabiles. Nostra turma dedicata laborat per horologium ut sese curet quod omnis hospes fruitur servitio excellenti.',
            'menu.title': 'Index Noster',
            'menu.subtitle': 'Inveni selectionem nostram curatam diligenter ferculorum internationalium, praeparatorum cum ingredientibus optimis et technicis coquendi modernis.',
            'menu.allDishes': 'Omnia Fercula',
            'menu.loading': 'Index onerans...',
            'menu.loadingCategories': 'Categoriae onerantes...',
            'menu.loadingItems': 'Fercula onerantes...',
            'menu.noItems': 'Nulla fercula inventa in hac categoria.',
            'menu.viewDetails': 'Vide Particularia',
            'menu.addToOrder': 'Adde ad Ordinem',
            'menu.close': 'Claude',
            'menu.vegetarian': 'Vegetarianus',
            'menu.spicy': 'Conditus',
            'menu.searchPlaceholder': 'Quaere ferculum...',
            'menu.filters': 'Filtra',
            'menu.dietary': 'Cibarius',
            'menu.priceRange': 'Ambitus Pretii',
            'menu.allPrices': 'Omnia Pretia',
            'menu.under15': 'Sub $15',
            'menu.15to20': '$15 - $20',
            'menu.over20': 'Super $20',
            'menu.reset': 'Resetta',
            'menu.exploreCategories': 'Explora categoriae nostras',
            'menu.dishes': 'Fercula',
            'menu.chefSelection': 'Selectio Magistri Coqui',
            'menu.internationalCuisine': 'Culina Internationalis',
            'menu.culinaryDelights': 'Deliciae Culinariae',
            'menu.curriesDescription': 'A curries aromaticis ad naan recens coctum, omne ferculum fabricatur cum condimentis authenticis et technicis traditionalibus per generationes traditis.',
            'menu.noItemsDescription': 'Tenta accommodare filtra tua vel terminos quaerendi ut invenias quod quaeris.',
            'menu.resetFilters': 'Resetta Filtra',
            'gallery.title': 'Pinacotheca',
            'gallery.culinaryArtistry': 'Ars Culinaria',
            'gallery.visualGallery': 'Pinacotheca Visalis',
            'gallery.loading': 'Pinacotheca onerans...',
            'gallery.noItems': 'Nulla imagines inventae.',
            'gallery.viewImage': 'Vide Imaginem',
            'gallery.close': 'Claude',
            'gallery.previous': 'Prior',
            'gallery.next': 'Sequens',
            'gallery.categories': 'Categoriae',
            'gallery.allCategories': 'Omnes Categoriae',
            'contact.title': 'Contactus',
            'contact.subtitle': 'Conveni nos pro quaestionibus, reservationibus, vel informatione.',
            'contact.form.name': 'Nomen',
            'contact.form.email': 'Electronica Epistula',
            'contact.form.subject': 'Subiectum',
            'contact.form.message': 'Nuntius',
            'contact.form.submit': 'Submitte',
            'contact.form.submitting': 'Submittens...',
            'contact.info.title': 'Info Contactus',
            'contact.info.address': 'Adressa',
            'contact.info.phone': 'Telephonum',
            'contact.info.email': 'Electronica Epistula',
            'contact.info.hours': 'Horae Apertionis',
            'contact.info.hours.weekdays': 'Dies Laboris: 8:30 AM - 1:00 AM',
            'contact.info.hours.weekends': 'Fines Hebdomadis: 10:30 AM - 1:00 AM',
            'contact.success': 'Nuntius tuus feliciter submissus est!',
            'contact.error': 'Defecit submittere nuntium.',
            'contact.required': 'Hoc campum requiritur',
            'contact.invalidEmail': 'Electronica epistula invalida',
            'contact.fromCityCenter': 'A Centro Urbis',
            'contact.parkingAvailable': 'Parking Disponibilis',
            'contact.free': 'Gratis',
            'contact.minutes': 'minuta',
            'contact.getInTouch': 'Contingere',
            'contact.contactOurTeam': 'Contacta Nostram Turmam',
            'contact.readyToExperience': 'Paratus experiri ultimam cenam et ludum? Hic sumus ut te adiuvemus ad perfectam visitationem tuam ad restaurant disponendam.',
            'contact.contactDetails': 'Particularia Contactus',
            'contact.howToReachUs': 'Quomodo Nos Attingere',
            'contact.helpWithReservations': 'Hic sumus ut te adiuvemus cum reservationibus, quaestionibus, vel petitionibus specialibus pro experientia cenae tuae.',
            'contact.visitRestaurant': 'Visita Nostrum Restaurant',
            'contact.callUsDirectly': 'Voca Nos Directe',
            'contact.sendUsEmail': 'Mitte Nobis Electronica Epistula',
            'contact.openingHours': 'Horae Apertionis',
            'contact.getDirections': 'Obtine Directiones',
            'contact.callNow': 'Voca Nunc',
            'contact.sendEmail': 'Mitte Electronica Epistula',
            'contact.bookTable': 'Reserva Mensam',
            'contact.availableDuringBusiness': 'Disponibilis durante horis negotii',
            'contact.respondWithin24': 'Respondebimus intra 24 horas',
            'contact.monday': 'Dies Lunae',
            'contact.tuesdayToSunday': 'Dies Martis - Dies Solis',
            'contact.closed': 'Clausum',
            'contact.findUs': 'Inveni Nos',
            'contact.sendMessage': 'Mitte Nuntium',
            'contact.getInTouchWithUs': 'Contingere cum Nobis',
            'contact.haveQuestion': 'Habes quaestionem, petitionem specialem, vel vis facere reservationem? Amamus audire a te et adiuvare te ad faciendam experientiam cenae tuae perfectam.',
            'contact.personalInformation': 'Informatio Personalis',
            'contact.contactInformation': 'Informatio Contactus',
            'contact.messageDetails': 'Particularia Nuntii',
            'contact.firstName': 'Nomen',
            'contact.lastName': 'Cognomen',
            'contact.emailAddress': 'Adressa Electronicae Epistulae',
            'contact.phoneNumber': 'Numerus Telephoni',
            'contact.subject': 'Subiectum',
            'contact.yourMessage': 'Nuntius Tuus',
            'contact.enterFirstName': 'Intra nomen tuum',
            'contact.enterLastName': 'Intra cognomen tuum',
            'contact.emailPlaceholder': 'nomen.tuum@exemplum.com',
            'contact.phonePlaceholder': '+243 xxx xxx xxx',
            'contact.selectPurpose': 'Elige propositum nuntii tui',
            'contact.messagePlaceholder': 'Narra nobis de quaestione tua, requisitis specialibus, diebus praeferatis, numero hospitum, vel aliis particularibus quae nos adiuvarent te melius adiuvare...',
            'contact.respondWithin24Hours': 'Respondebimus intra 24 horas',
            'contact.readyToDine': 'Paratus Cenare?',
            'contact.experienceAuthentic': 'Experire Culinam Indiam Authenticam',
            'contact.readyToEmbark': 'Paratus inire iter culinarium per sapores divites Indiae? Reserva mensam tuam hodie et sine nos creare experientiam cenae inobliviabilem pro te.',
            'contact.callNumber': 'Voca +243 971 466 525',
            'contact.bookOnline': 'Reserva Online',
            'contact.quickResponse': 'Responsio Celeris',
            'contact.confirmWithin2Hours': 'Confirmabimus reservationem tuam intra 2 horas',
            'contact.premiumExperience': 'Experientia Praemium',
            'contact.diningEntertainment': 'Experientia cenae et ludi 24/7',
            'contact.perfectLocation': 'Locus Perfectus',
            'contact.easyToFind': 'Facilis invenire cum parking gratis disponibili',
            'contact.reservationInquiry': '🍽️ Quaestio Reservationis',
            'contact.generalInquiry': '💬 Quaestio Generalis',
            'contact.feedbackReviews': '⭐ Feedback et Recensiones',
            'contact.privateEventBooking': '🎉 Reservatio Eventi Privati',
            'contact.cateringServices': '🚚 Servitia Catering',
            'contact.dietaryRequirements': '🥗 Requisita Cibaria',
            'contact.other': '📝 Alius',
            'reservation.bookYourTable': 'Reserva Mensam Tuam',
            'reservation.reserveDiningExperience': 'Reserva Experientiam Cenae Tuam',
            'reservation.secureYourTable': 'Secura mensam tuam apud restaurant nostrum praemium et inire iter culinarium inobliviabile per sapores Indicos authenticos.',
            'reservation.instantConfirmation': 'Confirmatio Instantanea',
            'reservation.awardWinning': 'Praemium Victor',
            'reservation.madeWithLove': 'Factum cum Amore',
            'reservation.personalInformation': 'Informatio Personalis',
            'reservation.reservationDetails': 'Particularia Reservationis',
            'reservation.specialRequests': 'Petitiones Speciales',
            'reservation.fullName': 'Nomen Plenum',
            'reservation.emailAddress': 'Adressa Electronicae Epistulae',
            'reservation.phoneNumber': 'Numerus Telephoni',
            'reservation.preferredDate': 'Dies Praeferata',
            'reservation.preferredTime': 'Tempus Praeferatum',
            'reservation.numberOfGuests': 'Numerus Hospitum',
            'reservation.additionalInformation': 'Informatio Addita (Optionale)',
            'reservation.specialOccasionPlaceholder': 'Narra nobis de occasione speciali, requisitis cibariis, praeferentibus sedendi, vel aliis petitionibus quae nos adiuvarent experientiam tuam perfectam facere...',
            'reservation.examples': '💡 Exempla: Celebratio anniversarii, accessibilitas rotarum, menu vegetarianum, sedes ad fenestram, etc.',
            'reservation.confirmWithin2Hours': 'Confirmabimus reservationem tuam intra 2 horas',
            'reservation.submitting': 'Submittens...',
            'reservation.reserveMyTable': 'Reserva Mensam Meam',
            'reservation.restaurantInfo': 'Info Restaurantis',
            'reservation.everythingYouNeed': 'Omnia quae scire debes',
            'reservation.openingHours': 'Horae Apertionis',
            'reservation.monday': 'Dies Lunae',
            'reservation.tuesdayToSunday': 'Dies Martis - Dies Solis',
            'reservation.closed': 'Clausum',
            'reservation.contactDetails': 'Particularia Contactus',
            'reservation.callUsDirectly': 'Voca nos directe',
            'reservation.sendUsEmail': 'Mitte nobis electronica epistula',
            'reservation.ourLocation': 'Locus Noster',
            'reservation.freeParkingAvailable': 'Parking gratis disponibilis',
            'reservation.importantNotes': 'Notae Importantes',
            'reservation.reservationsHeld': 'Reservationes tenentur 15 minuta post tempus reservationis',
            'reservation.groupsAdvanceNotice': 'Gruppae 8+ requirunt notitiam praeviam',
            'reservation.dietaryRequirements': 'Requisita cibaria possunt accommodari',
            'reservation.confirmationWithin2Hours': 'Confirmatio intra 2 horas per electronica epistula/telephonum',
            'reservation.specialOffers': 'Offertae Speciales',
            'reservation.limitedTimePromotions': 'Promotiones temporis limitati',
            'reservation.anniversarySpecial': '🎉 Speciale Anniversarii',
            'reservation.complimentaryDessert': 'Dulce gratuitum pro celebrationibus',
            'reservation.winePairing': '🍷 Accoppiamento Vini',
            'reservation.expertSommelier': 'Recommendationes experti sommelier',
            'reservation.confirmed.title': 'Reservatio Confirmata!',
            'reservation.confirmed.details': 'Particularia Reservationis',
            'reservation.confirmed.confirmationId': 'ID Confirmationis',
            'reservation.confirmed.responseTime': 'Tempus Responsionis',
            'reservation.confirmed.status': 'Status',
            'reservation.confirmed.within2Hours': 'Intra 2 horas',
            'reservation.confirmed.confirmed': 'Confirmata',
            'reservation.confirmed.emailSent': 'Electronica epistula confirmationis missa est ad adressam tuam. Turma nostra te contactabit intra 2 horas ad particularia confirmanda.',
            'reservation.confirmed.makeAnother': 'Facere Aliam Reservationem',
            'reservation.confirmed.returnHome': 'Redire Domum',
            'reservation.lunch': 'Prandium',
            'reservation.dinner': 'Cena',
            'reservation.selectTimeSlot': 'Elige tempus',
            'reservation.selectPartySize': 'Elige magnitudinem gruppae',
            'reservation.guest': 'Hospes',
            'reservation.guests': 'Hospites',
            'about.ourStory': 'Nostra Historia',
            'about.discoverOurJourney': 'Inveni Iter Nostrum',
            'about.awardWinner': 'Praemium Victor',
            'about.customers': 'Clientes',
            'about.yearsExcellence': 'Anni Excellentiae',
            'about.ourBeginning': 'Principium Nostrum',
            'about.culinaryJourney': 'Iter Culinarium Natum ex Passione',
            'about.uniqueEstablishment': 'restaurant est institutio unica sita in corde Goma, offerens experientiam cenae et ludi completam.',
            'about.storyBegins': 'Historia nostra incipit cum coquo passionato qui per Indiam iter fecit, discens technicas coquendi traditionales a magistris coquis in Mumbai, Delhi, et Kerala.',
            'about.todayWePride': 'Hodie nos gloriamur servire non solum cibum, sed experientiam quae te transportat ad vias vibrantas et culinas aromaticas Indiae.',
            'about.authenticRecipes': 'Receptae Indicae authenticae per generationes traditae',
            'about.freshSpices': 'Aromata fresca directe ex India importata',
            'about.serviceAvailability': 'Servitium 24/7 disponibile',
            'about.ourAchievements': 'Nostra Facta',
            'about.numbersTellStory': 'Numeri qui Historiam Nostram Narrant',
            'about.milestonesRepresent': 'Haec miliaria repraesentant nostram obligationem excellentiae et fidem quam clientes nostri in nobis ponunt.',
            'about.ourValues': 'Nostra Valores',
            'about.whatDrivesUs': 'Quid Nos Impellit Ante',
            'about.coreValuesShape': 'Nostri valores fundamentales formant omnem aspectum itineris culinarii nostri, ab ingredientibus quaerendis ad hospites servandos.',
            'about.authenticity': 'Authenticitas',
            'about.authenticityDescription': 'Honoramus methodos coquendi Indicas traditionales et receptas authenticas per generationes traditas.',
            'about.quality': 'Qualitas',
            'about.qualityDescription': 'Omne ingredientium diligenter eligitur et omne ferculum cum attentione meticulosa ad detailia praeparatur.',
            'about.hospitality': 'Hospitalitas',
            'about.hospitalityDescription': 'Credimus in tractando omnem hospitem sicut familiam, creando experientias cenae calidas et memorabiles.',
            'about.meetOurTeam': 'Conveni Nostram Turmam',
            'about.passionatePeople': 'Personae Passionatae Post Nostrum Successum',
            'about.meetTalentedIndividuals': 'Conveni individuos talentosos qui visionem culinariam nostram ad vitam ducunt cum experientia, passione, et dedicatione.',
            'about.chefRajeshKumar': 'Coquus Rajesh Kumar',
            'about.executiveChef': 'Coquus Exsecutivus',
            'about.chefRajeshDescription': 'Cum plus quam 15 annis experientiae, Coquus Rajesh affert sapores Indicos authenticos ad omne ferculum.',
            'about.internationalCuisine': 'Culina Internationalis et Bar',
            'about.chefPriyaSharma': 'Coqua Priya Sharma',
            'about.sousChef': 'Sub-Coquus',
            'about.chefPriyaDescription': 'Specializans in culina Indiae Meridionalis, Coqua Priya certat omnem curry perfecte aequilibratum esse.',
            'about.southIndianCurries': 'Curry Indiae Meridionalis',
            'about.amitPatel': 'Amit Patel',
            'about.restaurantManager': 'Procurator Restaurantis',
            'about.amitDescription': 'Amit certat omnem hospitem se sentire gratum et frui experientia cenae exceptionali.',
            'about.guestExperience': 'Experientia Hospitis',
            'about.loadingOurStory': 'Charging historiam nostram...',
            'join.stayConnected': 'Mane Coniunctus',
            'join.culinaryJourney': 'Iunge Nostrum Iter Culinarium',
            'join.getExclusiveOffers': 'Obtine offertas exclusivas, receptas, et esto primus scire de eventibus nostris specialibus',
            'join.enterYourEmail': 'Intra electronica epistula tua',
            'join.subscribe': 'Subscribere',
            'join.rating': '4.3★ Rating',
            'join.awardWinner': 'Praemium Victor',
            'join.happyCustomers': '500+ Clientes Felices',
            'join.diningEntertainmentHub': 'Hub Cenae et Ludi',
            'join.followUs': 'Sequere nos:',
            'gallery.loadingCategories': 'Charging galeriam...',
            'gallery.subtitle': 'Immerge te in itinere visuali per artem culinariam nostram, ambiantiam, et momenta quae oblivisci non possumus.',
            'gallery.photos': '50+ Imagines',
            'gallery.highQuality': 'Qualitas Alta',
            'gallery.curatedCollection': 'Collectio Curata',
            'gallery.browseCollection': 'Percurre Collectionem Nostram',
            'gallery.photo': 'imago',
            'gallery.photosAvailable': 'imagines disponibiles',
            'gallery.allPhotos': 'Omnes Imagines',
            'gallery.loadingItems': 'Charging imagines pulchras...',
            'gallery.viewFullSize': 'Vide Magnitudinem Plenam',
            'gallery.gallery': 'Galerium',
            'gallery.noPhotosFound': 'Nullae Imagines Inventae',
            'gallery.noPhotos': 'Nullae imagines disponibiles sunt in hac categoria hoc tempore.',
            'gallery.tryDifferentCategory': 'Tenta eligere categoriam diversam vel redi postea pro contento novo.',
            'gallery.viewAllPhotos': 'Vide Omnes Imagines',
            'gallery.fullResolution': 'Resolutio Plena',
            'booking.personalInformation': 'Informations Personales',
            'booking.fullName': 'Nomen Plenum',
            'booking.fullNameRequired': 'Nomen plenum requiritur',
            'booking.enterFullName': 'Intra nomen tuum plenum',
            'booking.emailAddress': 'Electronica Epistula',
            'booking.emailRequired': 'Electronica epistula requiritur',
            'booking.invalidEmail': 'Quaeso intra electronica epistula valida',
            'booking.emailPlaceholder': 'tua.epistula@exemplum.com',
            'booking.phoneNumber': 'Numerus Telephoni',
            'booking.phoneRequired': 'Numerus telephoni requiritur',
            'booking.phonePlaceholder': '+243 xxx xxx xxx',
            'booking.reservationDetails': 'Detailia Reservationis',
            'booking.preferredDate': 'Dies Praeferata',
            'booking.dateRequired': 'Dies requiritur',
            'booking.preferredTime': 'Tempus Praeferatum',
            'booking.timeRequired': 'Tempus requiritur',
            'booking.selectTimeSlot': 'Elige tempus',
            'booking.lunch': 'Prandium',
            'booking.dinner': 'Cena',
            'booking.additionalInformation': 'Informations Adicionales (Optionale)',
            'booking.specialOccasionPlaceholder': 'Narra nobis de occasione speciali, requisitis culinariis, praeferentiis sedendi, vel aliis petitionibus quae nos adiuvarent facere experientiam tuam perfectam...',
            'booking.examples': '💡 Exempla: Celebratio anniversarii, accessibilitas sella rotata, menu vegetariana, sedes prope fenestram, etc.',
            'booking.confirmWithin2Hours': 'Confirmabimus reservationem tuam intra 2 horas',
            'booking.submitting': 'Mittens...',
            'booking.reserveTable': 'Reserva Mensam',
            // Accessibility labels
            'aria.toggleTheme': 'Muta thema',
            'aria.toggleMenu': 'Muta menum',
            'aria.selectLanguage': 'Elige linguam',
            'aria.previousSlide': 'Slide prior',
            'aria.nextSlide': 'Slide sequens',
            'aria.goToSlide': 'I ad slide {number}',
            'aria.resumeVideo': 'Resume video',
            'aria.pauseVideo': 'Pausa video',
            'aria.facebook': 'Facebook',
            'aria.instagram': 'Instagram',
            'aria.twitter': 'Twitter',
            // Alt text
            'alt.logo': 'Logo restaurant',
            'alt.restaurantInterior': 'Interior Restaurantis',
        },
        ln: {
            'nav.home': 'Liboso',
            'nav.menu': 'Menu',
            'nav.about': 'Na biso',
            'nav.gallery': 'Bilili',
            'nav.contact': 'Bokutani',
            'nav.reservations': 'Bomba',
            'hero.slide1.tag': 'Boyei',
            'hero.slide1.title': 'Bolia ya Malamu na Biloko ya Motema',
            'hero.slide1.subtitle': 'Bola biloko ya malamu, bar ya kitoko, na biloko ya motema - nyonso na ndako moko',
            'hero.slide1.cta': 'Bomba Sik\'oyo',
            'hero.slide2.tag': 'Menu',
            'hero.slide2.title': 'Malamu ya Service 24/7',
            'hero.slide2.subtitle': 'Tofungolaka na ntango nyonso mpo na kosalela yo na moyi na na mpokwa',
            'hero.slide2.cta': 'Tala Menu',
            'hero.slide3.tag': 'Bolia',
            'hero.slide3.title': 'Bolia ya Mposa na Heure ya Kitoko',
            'hero.slide3.subtitle': 'Bolia ya mposa na biloko ya kitoko ya heure ya malamu',
            'hero.slide3.cta': 'Tala Bilili',
            'hero.bookNow': 'Bomba Sik\'oyo',
            'booking.title': 'Bomba Mesa na Yo',
            'booking.titlePerfect': 'Bomba Mesa na Yo ya Malamu',
            'booking.subtitle': 'Bomba esika na yo mpo na bolia ya kobosana te',
            'booking.quickReservation': 'Bomba ya Nsuka',
            'booking.reservationForm': 'Fomulere ya Bomba',
            'booking.description': 'Kokoma fomulere na nsé mpe tokokokisa bomba na yo na ngonga 2. Tokoki kozela te mpo na koyamba yo!',
            'booking.success': 'Bomba esilami malamu!',
            'booking.error': 'Bomba esilami te',
            'booking.form.name': 'Nkombo ya Mobimba',
            'booking.form.namePlaceholder': 'Kotya nkombo na yo ya mobimba',
            'booking.form.nameRequired': 'Nkombo esengeli',
            'booking.form.email': 'Email',
            'booking.form.emailPlaceholder': 'Kotya email na yo',
            'booking.form.emailRequired': 'Email esengeli',
            'booking.form.emailInvalid': 'Email ya mabe',
            'booking.form.phone': 'Numéro ya Téléphone',
            'booking.form.phonePlaceholder': 'Kotya numéro ya téléphone na yo',
            'booking.form.phoneRequired': 'Numéro ya téléphone esengeli',
            'booking.form.date': 'Mokolo',
            'booking.form.dateRequired': 'Mokolo esengeli',
            'booking.form.time': 'Ntango',
            'booking.form.timeSelect': 'Pona ntango',
            'booking.form.timeRequired': 'Ntango esengeli',
            'booking.form.guests': 'Motango ya Bato',
            'booking.form.guest': 'Moto',
            'booking.form.guestsSelect': 'Pona motango ya bato',
            'booking.form.guestsRequired': 'Motango ya bato esengeli',
            'booking.form.guestsMin': 'Moto 1 ya liboso',
            'booking.form.guestsMax': 'Bato 20 ya nsuka',
            'booking.form.specialRequests': 'Biloko ya Moto',
            'booking.form.specialRequestsPlaceholder': 'Biloko ya moto to biloko ya bolia...',
            'booking.form.submit': 'Bomba Mesa',
            'booking.form.submitting': 'Ezali kotinda...',
            'booking.form.orCall': 'To benga biso mpenza:',
            'booking.form.confirmationTime': 'Tokokokisa bomba na yo na ngonga 2',
            'highlights.title': 'Na',
            'highlights.subtitle': 'Biloko ya Kitoko',
            'highlights.description': 'Bona nini esali restaurant esika ya liboso mpo na bolia, biloko ya motema, na biloko ya kobosana te na Goma.',
            'highlights.tandoori.title': 'Bolia ya Mposa',
            'highlights.tandoori.description': 'Bola bolia ya solo na biloko ya mposa na biloko ya kitoko ya heure ya malamu.',
            'highlights.curries.title': 'Bokabwani "heure ya malamu" na restaurant',
            'highlights.curries.description': 'Yoka biloko ya biso ya kitoko na bokabwani ya kitoko na biloko ya biso ya kitoko na ntango ya biloko ya kitoko. Mokitani ya kitoko mpo na koyeba cuisine na biso na nkita ya moke.',
            'highlights.ambiance.title': 'Esika ya Biloko ya Motema',
            'highlights.ambiance.description': 'Liboso ya bolia moko - yoka bar na biso ya kitoko, biloko ya motema, na esika ya kitoko mpo na biloko nyonso.',
            'testimonials.title': 'Nini',
            'testimonials.subtitle': 'Bato na Biso Balobi',
            'testimonials.description': 'Kozwa te maloba na biso moko. Oyo nde bato na biso ya motema balobi na bolia na bango.',
            'testimonials.featured': 'Biloko ya Kitoko',
            'testimonials.overall.title': 'Motango ya Mobimba',
            'testimonials.overall.description': 'Na {count}+ maloba ya bato na biso',
            'footer.description': 'Bola bolia ya nsuka na biloko ya motema na restaurant na moto ya Goma. Na ngonga ya monene, tofandisi bolia ya malamu, service ya bar ya kitoko, na biloko ya motema nyonso na ndako moko.',
            'footer.quickLinks': 'Biloko ya Nsuka',
            'footer.contactInfo': 'Info ya Bokutani',
            'footer.openingHours': 'Ngonga ya Kofungola',
            'footer.days.monday': 'Mokolo ya Liboso',
            'footer.days.tuesday': 'Mokolo ya Mibale',
            'footer.days.wednesday': 'Mokolo ya Misato',
            'footer.days.thursday': 'Mokolo ya Minei',
            'footer.days.friday': 'Mokolo ya Mitano',
            'footer.days.saturday': 'Mposo',
            'footer.days.sunday': 'Eyenga',
            'footer.closed': 'Efungami',
            'footer.hours': '8:00 AM - 1:00 AM',
            'footer.hours.saturday': '10:30 AM - 1:00 AM',
            'footer.copyright': 'Mikano nyonso esimbami.',
            'home.welcome': 'Boyei na',
            'home.description': 'Bola bolia ya nsuka na biloko ya motema na restaurant na moto ya Goma. Na ngonga ya monene, tofandisi bolia ya malamu, service ya bar ya kitoko, biloko ya motema, na biloko ya mposa.',
            'home.rating.title': 'Motango ya 4.8 ya Minzoto',
            'home.rating.description': 'Esalemi malamu na Goma',
            'stats.starRating': 'Motango ya Minzoto',
            'stats.happyCustomers': 'Bato ya Mposa',
            'stats.signatureDishes': 'Bilia ya Kitoko',
            'stats.yearsExcellence': 'Mibu ya Malamu',
            'about.title': 'Na Biso',
            'about.subtitle': 'Bona lisolo na restaurant na bolingo na biso mpo na bolia ya malamu na biloko ya motema.',
            'about.story.title': 'Lisolo na Biso',
            'about.values.title': 'Biloko na Biso',
            'about.established': 'Esalemi',
            'about.values.subtitle': 'Biloko oyo esalaka biloko nyonso na restaurant.',
            'about.values.authenticity.title': 'Malamu',
            'about.values.authenticity.description': 'Tosali malamu na biloko nyonso - na bolia na service ya bar tii na biloko ya motema na bolingo ya bato.',
            'about.values.quality.title': 'Service 24/7',
            'about.values.quality.description': 'Tofungolaka na ntango nyonso mpo na yo, tofandisi malamu na service na ngonga nyonso.',
            'about.values.hospitality.title': 'Biloko ya Motema',
            'about.values.hospitality.description': 'Tosalaka biloko ya kobosana te na kosangisa bolia ya malamu, biloko ya kitoko, na biloko ya motema.',
            'about.team.title': 'Bonana na Équipe na Biso',
            'about.team.subtitle': 'Équipe na biso ya bolingo esaleli yo bolia ya malamu na biloko ya motema.',
            'about.team.chef.name': 'Chef Marie Uwimana',
            'about.team.chef.role': 'Chef ya Liboso',
            'about.team.chef.description': 'Na mibu 12 ya bolia ya mokili mobimba, Chef Marie asalaka bilia ya malamu mpo na menu na biso ya liboso.',
            'about.team.sousChef.name': 'James Kabera',
            'about.team.sousChef.role': 'Mokonzi ya Bar',
            'about.team.sousChef.description': 'James azali kotala bar na biso ya kitoko, asalaka cocktails ya kitoko na kotala biloko na biso ya kitoko.',
            'about.team.manager.name': 'Sarah Mugisha',
            'about.team.manager.role': 'Mokonzi ya Mobimba',
            'about.team.manager.description': 'Sarah azali kotala service 24/7 na kotya moto nyonso azali na service ya malamu na biloko nyonso na biso.',
            'about.story.content': 'restaurant ezali esika ya moko na moto ya Goma, efandisi bolia ya mobimba na biloko ya motema. Eyebani na bar ya liboso, restaurant, na biloko ya motema, restaurant ezali esika ya kokutana mpo na biloko nyonso.<br/><br/>Na ngonga 24/7, tofandisi service ya liboso mpo na kosalela yo na moyi na na mpokwa. Misala na biso ezali: kosala esika ya malamu wapi bolia ya kitoko, biloko ya kitoko, na biloko ya motema ekutani na ndako moko.<br/><br/>Tosalaka na biloko ya mposa na biloko ya kitoko ya heure ya malamu, tokokokisa moto nyonso azali na mposa na biso.<br/><br/>Tosepeli na misala na biso ya malamu, kofungola 24/7, na kosala biloko ya kobosana te. Équipe na biso esalaka na ngonga nyonso mpo na kotya moto nyonso azali na service ya malamu.',
            'menu.title': 'Menu na Biso',
            'menu.subtitle': 'Bona biloko na biso ya kitoko ya bilia ya mokili mobimba, esalemi na biloko ya malamu na biloko ya kosala bilia ya sik\'oyo.',
            'menu.allDishes': 'Bilia Nyonso',
            'menu.loading': 'Menu ezali kotia...',
            'menu.loadingCategories': 'Biloko ezali kotia...',
            'menu.loadingItems': 'Bilia ezali kotia...',
            'menu.noItems': 'Bilia te ezwami na biloko oyo.',
            'menu.viewDetails': 'Tala Biloko',
            'menu.addToOrder': 'Bakisa na Commande',
            'menu.close': 'Funga',
            'menu.vegetarian': 'Bilia ya Nzete',
            'menu.spicy': 'Na Pilipili',
            'menu.searchPlaceholder': 'Buka bilia...',
            'menu.filters': 'Biloko ya Kotia',
            'menu.dietary': 'Bilia',
            'menu.priceRange': 'Ntalo',
            'menu.allPrices': 'Ntalo Nyonso',
            'menu.under15': 'Na nsé ya $15',
            'menu.15to20': '$15 - $20',
            'menu.over20': 'Na likolo ya $20',
            'menu.reset': 'Zongisa',
            'menu.exploreCategories': 'Tala biloko na biso',
            'menu.dishes': 'Bilia',
            'menu.chefSelection': 'Bilia ya Chef',
            'menu.internationalCuisine': 'Bolia ya Mokili Mobimba',
            'menu.culinaryDelights': 'Bilia ya Kitoko',
            'menu.curriesDescription': 'Na curries ya kitoko tii na naan ya sika, bilia nyonso esalemi na biloko ya solo na biloko ya sik\'oyo.',
            'menu.noItemsDescription': 'Meza biloko ya kotia na yo to maloba ya kobuka mpo na kozwa oyo ozali koluka.',
            'menu.resetFilters': 'Zongisa Biloko ya Kotia',
            'gallery.title': 'Bilili',
            'gallery.culinaryArtistry': 'Misala ya Bolia',
            'gallery.visualGallery': 'Bilili ya Komona',
            'gallery.loading': 'Bilili ezali kotia...',
            'gallery.noItems': 'Bilili te ezwami.',
            'gallery.viewImage': 'Tala Bilili',
            'gallery.close': 'Funga',
            'gallery.previous': 'Liboso',
            'gallery.next': 'Nsima',
            'gallery.categories': 'Biloko',
            'gallery.allCategories': 'Biloko Nyonso',
            'contact.title': 'Bokutani',
            'contact.subtitle': 'Bonana na biso mpo na mituna, bomba, to biloko.',
            'contact.form.name': 'Nkombo',
            'contact.form.email': 'Email',
            'contact.form.subject': 'Lisolo',
            'contact.form.message': 'Maloba',
            'contact.form.submit': 'Tinda',
            'contact.form.submitting': 'Ezali kotinda...',
            'contact.info.title': 'Info ya Bokutani',
            'contact.info.address': 'Adresse',
            'contact.info.phone': 'Téléphone',
            'contact.info.email': 'Email',
            'contact.info.hours': 'Ngonga ya Kofungola',
            'contact.info.hours.weekdays': 'Mikolo ya Mosala: 8:30 AM - 1:00 AM',
            'contact.info.hours.weekends': 'Mposo: 10:30 AM - 1:00 AM',
            'contact.success': 'Maloba na yo esilami malamu!',
            'contact.error': 'Esilami te kotinda maloba.',
            'contact.required': 'Esika oyo esengeli',
            'contact.invalidEmail': 'Email ya mabe',
            'contact.fromCityCenter': 'Na moto ya mboka',
            'contact.parkingAvailable': 'Parking ezali',
            'contact.free': 'Liboso',
            'contact.minutes': 'minute',
            'contact.getInTouch': 'Kutana na Biso',
            'contact.contactOurTeam': 'Kutana na Équipe na Biso',
            'contact.readyToExperience': 'Ozali na mposa ya kozwa bolia ya nsuka na biloko ya motema? Tofungolaka mpo na kosalela yo na kosala bolia na yo ya malamu na restaurant.',
            'contact.contactDetails': 'Biloko ya Bokutani',
            'contact.howToReachUs': 'Ndeni na Kokutana na Biso',
            'contact.helpWithReservations': 'Tofungolaka mpo na kosalela yo na bomba, mituna, to biloko ya moto mpo na bolia na yo.',
            'contact.visitRestaurant': 'Tala Restaurant na Biso',
            'contact.callUsDirectly': 'Bengana na Biso Mpenza',
            'contact.sendUsEmail': 'Tinda na Biso Email',
            'contact.openingHours': 'Ngonga ya Kofungola',
            'contact.getDirections': 'Zwa Biloko ya Kokende',
            'contact.callNow': 'Benga Sik\'oyo',
            'contact.sendEmail': 'Tinda Email',
            'contact.bookTable': 'Bomba Mesa',
            'contact.availableDuringBusiness': 'Ezali na ngonga ya mosala',
            'contact.respondWithin24': 'Tokozongisa na ngonga 24',
            'contact.monday': 'Mokolo ya Liboso',
            'contact.tuesdayToSunday': 'Mokolo ya Mibale - Eyenga',
            'contact.closed': 'Efungami',
            'contact.findUs': 'Buka Biso',
            'contact.sendMessage': 'Tinda Maloba',
            'contact.getInTouchWithUs': 'Kutana na Biso',
            'contact.haveQuestion': 'Ozali na mituna, biloko ya moto, to ozali koluka kosala bomba? Tolingi koyoka na yo mpe kosalela yo mpo na kosala bolia na yo ya malamu.',
            'contact.personalInformation': 'Biloko ya Moto',
            'contact.contactInformation': 'Biloko ya Bokutani',
            'contact.messageDetails': 'Biloko ya Maloba',
            'contact.firstName': 'Nkombo ya Liboso',
            'contact.lastName': 'Nkombo ya Liboso',
            'contact.emailAddress': 'Adresse ya Email',
            'contact.phoneNumber': 'Numéro ya Téléphone',
            'contact.subject': 'Lisolo',
            'contact.yourMessage': 'Maloba na Yo',
            'contact.enterFirstName': 'Kotya nkombo na yo ya liboso',
            'contact.enterLastName': 'Kotya nkombo na yo ya liboso',
            'contact.emailPlaceholder': 'email.na.yo@mbanda.com',
            'contact.phonePlaceholder': '+243 xxx xxx xxx',
            'contact.selectPurpose': 'Pona lisolo ya maloba na yo',
            'contact.messagePlaceholder': 'Lobela biso na mituna na yo, biloko ya moto, mikolo ya moto, motango ya bato, to biloko ya mosusu oyo ekosalela biso mpo na kosalela yo malamu...',
            'contact.respondWithin24Hours': 'Tokozongisa na ngonga 24',
            'contact.readyToDine': 'Ozali na Mposa ya Kolia?',
            'contact.experienceAuthentic': 'Bola Bolia ya India ya Solo',
            'contact.readyToEmbark': 'Ozali na mposa ya kokende na bolia ya India ya kitoko? Bomba mesa na yo lelo mpe tika biso kosala bolia ya kobosana te mpo na yo.',
            'contact.callNumber': 'Benga +243 971 466 525',
            'contact.bookOnline': 'Bomba na Internet',
            'contact.quickResponse': 'Kozongisa ya Nsuka',
            'contact.confirmWithin2Hours': 'Tokokokisa bomba na yo na ngonga 2',
            'contact.premiumExperience': 'Bolia ya Kitoko',
            'contact.diningEntertainment': 'Bolia na biloko ya motema na ngonga nyonso',
            'contact.perfectLocation': 'Esika ya Malamu',
            'contact.easyToFind': 'Esengeli kozwa na parking ya liboso',
            'contact.reservationInquiry': '🍽️ Mituna ya Bomba',
            'contact.generalInquiry': '💬 Mituna ya Liboso',
            'contact.feedbackReviews': '⭐ Maloba na Biloko',
            'contact.privateEventBooking': '🎉 Bomba ya Biloko ya Moto',
            'contact.cateringServices': '🚚 Misala ya Bolia',
            'contact.dietaryRequirements': '🥗 Biloko ya Bolia',
            'contact.other': '📝 Mosusu',
            'reservation.bookYourTable': 'Bomba Mesa na Yo',
            'reservation.reserveDiningExperience': 'Bomba Bolia na Yo',
            'reservation.secureYourTable': 'Bomba mesa na yo na restaurant na biso ya kitoko mpe kokende na bolia ya kobosana te na biloko ya India ya solo.',
            'reservation.instantConfirmation': 'Kokokisa ya Nsuka',
            'reservation.awardWinning': 'Ya Kitoko',
            'reservation.madeWithLove': 'Esalemi na Bolingo',
            'reservation.personalInformation': 'Biloko ya Moto',
            'reservation.reservationDetails': 'Biloko ya Bomba',
            'reservation.specialRequests': 'Biloko ya Moto',
            'reservation.fullName': 'Nkombo ya Mobimba',
            'reservation.emailAddress': 'Adresse ya Email',
            'reservation.phoneNumber': 'Numéro ya Téléphone',
            'reservation.preferredDate': 'Mokolo ya Moto',
            'reservation.preferredTime': 'Ntango ya Moto',
            'reservation.numberOfGuests': 'Motango ya Bato',
            'reservation.additionalInformation': 'Biloko ya Mosusu (Moto)',
            'reservation.specialOccasionPlaceholder': 'Lobela biso na biloko ya moto, biloko ya bolia, biloko ya moto, to biloko ya mosusu oyo ekosalela biso mpo na kosala bolia na yo ya malamu...',
            'reservation.examples': '💡 Biloko: Bolia ya moto, biloko ya moto, menu ya nzete, esika ya moto, na biloko ya mosusu.',
            'reservation.confirmWithin2Hours': 'Tokokokisa bomba na yo na ngonga 2',
            'reservation.submitting': 'Ezali kotinda...',
            'reservation.reserveMyTable': 'Bomba Mesa na Ngai',
            'reservation.restaurantInfo': 'Biloko ya Restaurant',
            'reservation.everythingYouNeed': 'Biloko nyonso oyo ozali na mposa na koyeba',
            'reservation.openingHours': 'Ngonga ya Kofungola',
            'reservation.monday': 'Mokolo ya Liboso',
            'reservation.tuesdayToSunday': 'Mokolo ya Mibale - Eyenga',
            'reservation.closed': 'Efungami',
            'reservation.contactDetails': 'Biloko ya Bokutani',
            'reservation.callUsDirectly': 'Bengana na biso mpenza',
            'reservation.sendUsEmail': 'Tinda na biso email',
            'reservation.ourLocation': 'Esika na Biso',
            'reservation.freeParkingAvailable': 'Parking ya liboso ezali',
            'reservation.importantNotes': 'Biloko ya Mposa',
            'reservation.reservationsHeld': 'Bomba ezali na ngonga 15 na nsima ya ngonga ya bomba',
            'reservation.groupsAdvanceNotice': 'Bato 8+ esengeli koyeba liboso',
            'reservation.dietaryRequirements': 'Biloko ya bolia esengeli kozwa',
            'reservation.confirmationWithin2Hours': 'Kokokisa na ngonga 2 na email/téléphone',
            'reservation.specialOffers': 'Biloko ya Kitoko',
            'reservation.limitedTimePromotions': 'Biloko ya ngonga ya moke',
            'reservation.anniversarySpecial': '🎉 Biloko ya Moto',
            'reservation.complimentaryDessert': 'Bilia ya liboso mpo na biloko',
            'reservation.winePairing': '🍷 Bolia ya Moto',
            'reservation.expertSommelier': 'Biloko ya moto ya kitoko',
            'reservation.confirmed.title': 'Bomba Esilami!',
            'reservation.confirmed.details': 'Biloko ya Bomba',
            'reservation.confirmed.confirmationId': 'ID ya Kokokisa',
            'reservation.confirmed.responseTime': 'Ntango ya Kozongisa',
            'reservation.confirmed.status': 'Mobongo',
            'reservation.confirmed.within2Hours': 'Na ngonga 2',
            'reservation.confirmed.confirmed': 'Esilami',
            'reservation.confirmed.emailSent': 'Email ya kokokisa esilami kotinda na email na yo. Équipe na biso ekokutana na yo na ngonga 2 mpo na kokokisa biloko.',
            'reservation.confirmed.makeAnother': 'Kosala Bomba ya Mosusu',
            'reservation.confirmed.returnHome': 'Kozongisa na Liboso',
            'reservation.lunch': 'Bilia ya Midi',
            'reservation.dinner': 'Bilia ya Mpokwa',
            'reservation.selectTimeSlot': 'Pona ntango',
            'reservation.selectPartySize': 'Pona motango ya bato',
            'reservation.guest': 'Moto',
            'reservation.guests': 'Bato',
            'about.ourStory': 'Lisolo na Biso',
            'about.discoverOurJourney': 'Bola Bolia na Biso',
            'about.awardWinner': 'Moto ya Kitoko',
            'about.customers': 'Bato',
            'about.yearsExcellence': 'Mbula ya Kitoko',
            'about.ourBeginning': 'Liboso na Biso',
            'about.culinaryJourney': 'Bolia ya Kitoko ya Bolingo',
            'about.uniqueEstablishment': 'restaurant ezali esika ya kitoko na moto ya Goma, ezali kopesa bolia ya mobimba na biloko ya motema.',
            'about.storyBegins': 'Lisolo na biso ebandi na moto ya bolingo oyo akendaki na India, akoyekola biloko ya bolia ya liboso na bato ya kitoko na Mumbai, Delhi, na Kerala.',
            'about.todayWePride': 'Lelo, tofukami na kosalela te bolia kaka, kasi bolia oyo ekotisa yo na biloko ya kitoko na India.',
            'about.authenticRecipes': 'Bilia ya India ya solo ya liboso',
            'about.freshSpices': 'Biloko ya kitoko ya India',
            'about.serviceAvailability': 'Misala ya ngonga nyonso',
            'about.ourAchievements': 'Biloko na Biso',
            'about.numbersTellStory': 'Mitango oyo Ekoloba Lisolo na Biso',
            'about.milestonesRepresent': 'Biloko oyo ezali koloba bolingo na biso na bolia ya kitoko na bolingo ya bato na biso.',
            'about.ourValues': 'Biloko na Biso',
            'about.whatDrivesUs': 'Nini Ekosalela Biso',
            'about.coreValuesShape': 'Biloko na biso ya liboso ezali kosala biloko nyonso ya bolia na biso, na kozwa biloko na kosalela bato na biso.',
            'about.authenticity': 'Bolia ya Solo',
            'about.authenticityDescription': 'Tolingi biloko ya bolia ya India ya liboso na biloko ya solo ya liboso.',
            'about.quality': 'Kitoko',
            'about.qualityDescription': 'Biloko nyonso ezali kozwa malamu na biloko nyonso ezali kosalema na bolingo.',
            'about.hospitality': 'Bolingo ya Bato',
            'about.hospitalityDescription': 'Tolingi kosalela bato nyonso lokola libota, kosala bolia ya kitoko na ya kobosana te.',
            'about.meetOurTeam': 'Kutana na Équipe na Biso',
            'about.passionatePeople': 'Bato ya Bolingo na Nsima ya Kitoko na Biso',
            'about.meetTalentedIndividuals': 'Kutana na bato ya kitoko oyo ekosalela bolia na biso na kitoko na bolingo na bolingo.',
            'about.chefRajeshKumar': 'Moto ya Bolia Rajesh Kumar',
            'about.executiveChef': 'Moto ya Bolia ya Liboso',
            'about.chefRajeshDescription': 'Na ngonga 15+, Moto ya Bolia Rajesh ekosalela biloko ya India ya solo na biloko nyonso.',
            'about.internationalCuisine': 'Bolia ya Liboso na Bar',
            'about.chefPriyaSharma': 'Moto ya Bolia Priya Sharma',
            'about.sousChef': 'Moto ya Bolia ya Liboso',
            'about.chefPriyaDescription': 'Moto ya Bolia Priya akosalela bolia ya India ya Sudi, akosalela curry nyonso ya malamu.',
            'about.southIndianCurries': 'Curry ya India ya Sudi',
            'about.amitPatel': 'Amit Patel',
            'about.restaurantManager': 'Moto ya Restaurant',
            'about.amitDescription': 'Amit akosalela bato nyonso kozwa bolia ya kitoko.',
            'about.guestExperience': 'Bolia ya Moto',
            'about.loadingOurStory': 'Ezali kotinda lisolo na biso...',
            'join.stayConnected': 'Kozala na Bokutani',
            'join.culinaryJourney': 'Kotana na Bolia na Biso',
            'join.getExclusiveOffers': 'Zwa biloko ya kitoko, biloko ya bolia, mpe ozala ya liboso na koyeba biloko ya moto na biso',
            'join.enterYourEmail': 'Kotya email na yo',
            'join.subscribe': 'Kotya nkombo',
            'join.rating': '4.3★ Bolingo',
            'join.awardWinner': 'Moto ya Kitoko',
            'join.happyCustomers': '500+ Bato ya Kitoko',
            'join.diningEntertainmentHub': 'Esika ya Bolia na Biloko',
            'join.followUs': 'Landela biso:',
            'gallery.loadingCategories': 'Ezali kotinda biloko ya moto...',
            'gallery.subtitle': 'Kotisa yo na bolia ya motema na bolia na biso, biloko ya kitoko, na biloko ya kobosana te.',
            'gallery.photos': '50+ Biloko',
            'gallery.highQuality': 'Kitoko ya Liboso',
            'gallery.curatedCollection': 'Biloko ya Kitoko',
            'gallery.browseCollection': 'Bola Biloko na Biso',
            'gallery.photo': 'biloko',
            'gallery.photosAvailable': 'biloko ya kitoko',
            'gallery.allPhotos': 'Biloko Nyonso',
            'gallery.loadingItems': 'Ezali kotinda biloko ya kitoko...',
            'gallery.viewFullSize': 'Kotala na Kitoko',
            'gallery.gallery': 'Biloko',
            'gallery.noPhotosFound': 'Biloko Te Ezui',
            'gallery.noPhotos': 'Biloko te ezali na moto ya biloko oyo na ntango oyo.',
            'gallery.tryDifferentCategory': 'Meleka kozwa moto mosusu to zongela na nsima na biloko ya sika.',
            'gallery.viewAllPhotos': 'Kotala Biloko Nyonso',
            'gallery.fullResolution': 'Kitoko ya Mobimba',
            'booking.personalInformation': 'Biloko ya Moto',
            'booking.fullName': 'Liboso Mobimba',
            'booking.fullNameRequired': 'Liboso mobimba ezali na moto',
            'booking.enterFullName': 'Kotya liboso na yo mobimba',
            'booking.emailAddress': 'Adresse ya Email',
            'booking.emailRequired': 'Adresse ya email ezali na moto',
            'booking.invalidEmail': 'Tuna kotya adresse ya email ya malamu',
            'booking.emailPlaceholder': 'email.na.yo@exemple.com',
            'booking.phoneNumber': 'Numéro ya Téléphone',
            'booking.phoneRequired': 'Numéro ya téléphone ezali na moto',
            'booking.phonePlaceholder': '+243 xxx xxx xxx',
            'booking.reservationDetails': 'Biloko ya Bolia',
            'booking.preferredDate': 'Mokolo ya Bolingo',
            'booking.dateRequired': 'Mokolo ezali na moto',
            'booking.preferredTime': 'Ntango ya Bolingo',
            'booking.timeRequired': 'Ntango ezali na moto',
            'booking.selectTimeSlot': 'Pona ntango',
            'booking.lunch': 'Bilia ya Midi',
            'booking.dinner': 'Bilia ya Mpokwa',
            'booking.additionalInformation': 'Biloko ya Liboso (Na Bolingo)',
            'booking.specialOccasionPlaceholder': 'Koloba na biso biloko ya moto, biloko ya bolia, biloko ya kofanda, to biloko mosusu oyo ekosalela biso kosala bolia na yo ya kitoko...',
            'booking.examples': '💡 Biloko: Bolia ya moto, biloko ya kitoko, biloko ya bolia, kofanda na liboso ya fenêtre, etc.',
            'booking.confirmWithin2Hours': 'Tokosala bolia na yo na ngonga 2',
            'booking.submitting': 'Ezali kotinda...',
            'booking.reserveTable': 'Kobomba Mesa',
            // Accessibility labels
            'aria.toggleTheme': 'Bongola moto',
            'aria.toggleMenu': 'Bongola menu',
            'aria.selectLanguage': 'Pona lokota',
            'aria.previousSlide': 'Slide ya liboso',
            'aria.nextSlide': 'Slide ya nsima',
            'aria.goToSlide': 'Kende na slide {number}',
            'aria.resumeVideo': 'Zongisa video',
            'aria.pauseVideo': 'Kanga video',
            'aria.facebook': 'Facebook',
            'aria.instagram': 'Instagram',
            'aria.twitter': 'Twitter',
            // Alt text
            'alt.logo': 'Logo ya restaurant',
            'alt.restaurantInterior': 'Na kati ya restaurant',
        },
    };

    const t = useCallback((key: string, ...args: any[]): string => {
        let translatedText = translations[currentLanguage]?.[key] || translations.en[key] || key;

        // Replace placeholders like {count}
        args.forEach((arg, index) => {
            translatedText = translatedText.replace(`{${index}}`, arg);
        });

        return translatedText;
    }, [currentLanguage, translations]);

    const setLanguage = (code: string) => {
        setCurrentLanguage(code);
        // Save language preference to localStorage
        localStorage.setItem('restaurant-language', code);
    };

    useEffect(() => {
        const fetchLanguages = async () => {
            try {
                // Use the configured apiClient instead of direct axios
                const response = await apiClient.get('/languages');
                setLanguages(response.data);
            } catch (error) {
                console.error('Failed to fetch languages:', error);
                // Fallback languages
                setLanguages([
                    { id: '1', code: 'en', name: 'English', native_name: 'English', is_active: true, is_default: true, sort_order: 0 },
                    { id: '2', code: 'fr', name: 'French', native_name: 'Français', is_active: true, is_default: false, sort_order: 1 },
                    { id: '3', code: 'es', name: 'Spanish', native_name: 'Español', is_active: true, is_default: false, sort_order: 2 },
                    { id: '4', code: 'ar', name: 'Arabic', native_name: 'العربية', is_active: true, is_default: false, sort_order: 3 },
                ]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchLanguages();
    }, []);

    // Configure axios to include language parameter in requests
    useEffect(() => {
        // Add request interceptor to include language parameter
        const requestInterceptor = apiClient.interceptors.request.use(
            (config) => {
                // Add language parameter to API requests
                if (config.url && config.url.startsWith('/')) {
                    // Check if lang parameter already exists
                    if (!config.url.includes('lang=')) {
                        const separator = config.url.includes('?') ? '&' : '?';
                        config.url = `${config.url}${separator}lang=${currentLanguage}`;
                    }
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Cleanup interceptor on unmount
        return () => {
            apiClient.interceptors.request.eject(requestInterceptor);
        };
    }, [currentLanguage]);

    const value: LanguageContextType = {
        currentLanguage,
        setLanguage,
        languages,
        isLoading,
        t,
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};
